import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useTheme } from "../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  ExternalLink,
  Loader2,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Info,
  History,
  FileText,
  X,
  PanelLeft,
  PanelLeftClose,
  Moon,
  Sun,
  MessageSquare,
  Search,
  FolderPlus,
  Settings,
  Mic,
  CheckCircle,
  Users,
  ArrowRight,
  AlertTriangle,
  Hourglass,
  Check,
  Scale,
  Download,
  LayoutDashboard,
  Lock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import axios from "axios";
import ReactQuill from "react-quill";
import html2pdf from "html2pdf.js";
import {
  pdf,
  Document as PDFDocument,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import "./dashboard/Dashboard.css";
import "./DebateMode.css";
import "./DebateProgress.css";
import "../styles/research-redesign.css";
import "../styles/debate-redesign.css";
import "../styles/debate-workspace.css";
import LazySection from "./LazySection";
import Footer from "./Footer";
import DebateSidebar from "./DebateSidebar";
import DebateContextRail from "./DebateContextRail";
import Header from "./Header";
import SEO from "./SEO";
import VideoWidget from "./VideoWidget";
import ConfirmDialog from "./ConfirmDialog";
import DebateSummary from "./DebateSummary";
import {
  ensureDisclaimerAtEnd,
  convertTextToHtml,
  removeCitationMarkers,
  removeSeparatorLines,
} from "./DebateUtils";
import "react-quill/dist/quill.snow.css";
import DebatModePanelComposition from "./DebatModePanelComposition";
import compositionOptions from "../utils/compositionModels";
import { needsProfileCompletion } from "../utils/profileCompletion";
import {
  shouldBlockSubscriptionAccess,
  isSubscriptionPaymentWarning,
  getSubscriptionPaymentMessage,
  hasPremiumFeatureAccess,
  getMaxFilesPerQuery,
} from "../utils/subscriptionAccess";
import { useChatFiles } from "../hooks/useChatFiles";
import { useFileUpload } from "../hooks/useFileUpload";
import FileUploader from "./dashboard/FileUploader";
import { deleteFile } from "../services/fileService";
import {
  handleDownloadPDF as exportEditorHtmlToPdf,
  handleDownloadDoc as exportEditorHtmlToDocx,
  saveBlobWithPlatformSupport,
} from "../utils/exportUtils";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const hasUsableDocumentText = (text) => {
  const normalized = typeof text === "string" ? text.trim() : "";
  if (!normalized) return false;
  if (/[\p{L}\p{N}]+/u.test(normalized)) return true;
  // Fallback for extraction outputs that are not strictly word-tokenized but still contain readable content.
  return normalized.replace(/\s+/g, "").length >= 20;
};

/** Map saved chat payload after a dropped debate stream (e.g. device sleep killing the HTTP/SSE connection). */
function pickRecoverySnapshotForStreamResume(chat, didSendRerun) {
  if (!chat) return null;
  const rerun = chat.debateRerun;
  const topRounds = Array.isArray(chat.debateRounds) ? chat.debateRounds : [];
  const rerunRounds =
    rerun && Array.isArray(rerun.debateRounds) ? rerun.debateRounds : [];
  const preferRerun =
    didSendRerun &&
    rerun &&
    (rerunRounds.length > 0 ||
      rerun.stopped ||
      rerun.status === "failed" ||
      rerun.debateError ||
      rerun.error);

  if (preferRerun) {
    return {
      debateRounds: rerunRounds,
      finalSummary: rerun.finalSummary || chat.finalSummary || "",
      debateError: rerun.debateError || null,
      stopped: !!(rerun.stopped || rerun.status === "failed"),
      stoppedAtRound: rerun.stoppedAtRound,
      maxRounds: rerun.maxRounds || chat.maxRounds || 5,
    };
  }

  return {
    debateRounds: topRounds,
    finalSummary: chat.finalSummary || "",
    debateError: chat.debateError || null,
    stopped: !!chat.stopped,
    stoppedAtRound: chat.stoppedAtRound,
    maxRounds: chat.maxRounds || 5,
  };
}

/** True when persisted rounds include the consensus phase (logged-in debates saved incrementally during sleep/offline SSE). */
function isServerDebateSnapshotComplete(snap, fallbackMaxRounds) {
  if (!snap || !Array.isArray(snap.debateRounds)) return false;
  const maxR =
    Number(snap.maxRounds) || Number(fallbackMaxRounds) || 5;
  if (!maxR) return false;
  const roundsArr = snap.debateRounds;
  if (roundsArr.length < maxR + 1) return false;
  return roundsArr.some(
    (r) =>
      r?.arguments?.consensus &&
      (!r?.arguments?.advocate || Number(r.round) > maxR),
  );
}

function formatClassifiedDebateFailure(classified) {
  if (!classified) return "";
  if (classified.userTitle && classified.userDetail) {
    return `${classified.userTitle}. ${classified.userDetail}`;
  }
  return classified.userDetail || classified.userTitle || "";
}

/** Turn low-level Fetch / WebKit messages into actionable copy for debate streaming. */
function classifyDebateStreamFailure(err) {
  const name = err?.name || "";
  const msg = `${err?.message || ""} ${err?.cause?.message || ""}`.trim();
  const combined = `${name} ${msg}`.toLowerCase();

  if (name === "AbortError" || combined.includes("aborted")) {
    return {
      category: "aborted",
      userTitle: "",
      userDetail: "",
    };
  }

  if (
    combined.includes("load failed") ||
    combined.includes("failed to fetch") ||
    combined.includes("networkerror") ||
    combined.includes("network error") ||
    combined.includes("internet connection appears to be offline") ||
    combined.includes("connection was lost") ||
    combined.includes("err_connection_reset") ||
    combined.includes("econnreset")
  ) {
    return {
      category: "connection",
      userTitle: "Connection paused or lost",
      userDetail:
        "Mobile browsers often drop long connections when the screen locks or you leave the tab for too long—this is separate from tapping Stop debate. Reloading this chat shows anything the server already saved.",
    };
  }

  if (combined.includes("timeout") || combined.includes("timed out")) {
    return {
      category: "timeout",
      userTitle: "Request timed out",
      userDetail:
        msg.trim() ||
        "The debate ran longer than your network tolerated. Try fewer rounds or a more stable connection.",
    };
  }

  return {
    category: "unknown",
    userTitle: "Debate streaming stopped unexpectedly",
    userDetail:
      msg.trim() ||
      "Something went wrong while receiving the debate. If you were not returned to partial results automatically, reopen this chat from history.",
  };
}

// Default prompt for debate mode
const DEFAULT_DEBATE_PROMPT =
  "What are the strongest and weakest arguments in my case? How can the weakest arguments be improved?";

const promptSections = [
  {
    key: "strategy",
    label: "TRIAL & CASE STRATEGY",
    iconSrc: "/1.svg",
    iconAlt: "trial strategy icon",
    prompts: [
      "Debate the strongest defense strategy for a personal injury case involving conflicting eyewitness testimony.",
      "Debate the most persuasive arguments for challenging the admissibility of digital evidence in a criminal trial.",
      "Debate how to build a defense against a breach of fiduciary duty claim under Delaware law.",
    ],
  },
  {
    key: "evidence",
    label: "MOTION & BRIEF PREPARATION",
    iconSrc: "/2.svg",
    iconAlt: "motion brief icon",
    prompts: [
      "Debate the most effective argument for and against granting summary judgment in a negligence case.",
      "Debate the legal standard for intentional infliction of emotional distress and how best to argue it in court.",
      "Debate how to strengthen a motion to dismiss based on lack of jurisdiction.",
    ],
  },
  {
    key: "reasoning",
    label: "CROSS-EXAMINATION & WITNESS PREP",
    iconSrc: "/3.svg",
    iconAlt: "cross examination icon",
    prompts: [
      "Debate the most strategic cross-examination questions for a medical expert testifying on causation.",
      "Debate how to undermine an opposing expert’s credibility without appearing aggressive.",
    ],
  },
  {
    key: "appellate",
    label: "APPELLATE & CONSTITUTIONAL ANALYSIS",
    iconSrc: "/4.svg",
    iconAlt: "appellate icon",
    prompts: [
      "Debate both sides of the argument regarding whether AI-generated content qualifies as protected speech.",
      "Debate whether an appellate court should overturn a lower court’s ruling in a case of procedural error.",
    ],
  },
  {
    key: "compliance",
    label: "MULTI-JURISDICTION & COMPLIANCE",
    iconSrc: "/5.svg",
    iconAlt: "compliance icon",
    prompts: [
      "Debate how California, Texas, and New York courts interpret non-compete clauses post-FTC ruling.",
      "Debate potential liabilities when terminating a contractor under a vague breach clause.",
      "Debate whether GDPR principles apply to U.S.-based law firms handling EU client data.",
    ],
  },
  {
    key: "training",
    label: "LEGAL EDUCATION & TRAINING",
    iconSrc: "/6.svg",
    iconAlt: "legal education icon",
    prompts: [
      "Debate both sides of Roe v. Wade to illustrate constitutional reasoning.",
      "Debate whether Miranda rights should extend to digital interrogations (chat-based questioning).",
      "Debate how AI tools could influence the evolution of attorney-client privilege.",
    ],
  },
];

const DebateProgressUI = ({ maxRounds, currentRound, darkMode, modelNames }) => {
  const stepperRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = stepperRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    // Add a small epsilon (1px) for floating point precision issues
    const maxScrollLeft = scrollWidth - clientWidth;
    setCanScrollLeft(Math.ceil(scrollLeft) > 0);
    setCanScrollRight(Math.ceil(scrollLeft) < Math.floor(maxScrollLeft) - 1 && scrollWidth > clientWidth);
  }, []);

  useEffect(() => {
    const el = stepperRef.current;
    if (!el) return;

    // Runtime safeguard: on small screens, global CSS resets in
    // `.debate-progress-wrapper *` ({ min-width:0 !important; max-width:100% !important })
    // can collapse the flex track so the scroll container has no overflow,
    // which makes the arrow buttons appear "stuck". Force inline !important
    // widths on each step and a min-width on the track itself.
    const enforceMobileTrackWidth = () => {
      const isMobile = window.innerWidth <= 500;
      const stepEls = Array.from(el.querySelectorAll(".debate-step"));
      if (isMobile) {
        const stepPx = 80;
        stepEls.forEach((node) => {
          node.style.setProperty("flex", `0 0 ${stepPx}px`, "important");
          node.style.setProperty("width", `${stepPx}px`, "important");
          node.style.setProperty("min-width", `${stepPx}px`, "important");
          node.style.setProperty("max-width", `${stepPx}px`, "important");
        });
        // Important: do NOT set min-width on the stepper itself. That would
        // make the scroll container as wide as its own content, leaving no
        // overflow to scroll. The stepper must stay at 100% of its wrapper
        // (visible viewport width) so the steps overflow it horizontally.
        el.style.removeProperty("min-width");
        el.style.setProperty("display", "flex", "important");
        el.style.setProperty("flex-wrap", "nowrap", "important");
        el.style.setProperty("overflow-x", "auto", "important");
        el.style.setProperty("overflow-y", "visible", "important");
        el.style.setProperty("width", "100%", "important");
        el.style.setProperty("max-width", "100%", "important");
      } else {
        stepEls.forEach((node) => {
          node.style.removeProperty("flex");
          node.style.removeProperty("width");
          node.style.removeProperty("min-width");
          node.style.removeProperty("max-width");
        });
        el.style.removeProperty("min-width");
        el.style.removeProperty("display");
        el.style.removeProperty("flex-wrap");
        el.style.removeProperty("overflow-x");
        el.style.removeProperty("overflow-y");
        el.style.removeProperty("width");
        el.style.removeProperty("max-width");
      }
    };

    enforceMobileTrackWidth();
    updateScrollArrows();
    const tm = setTimeout(() => {
      enforceMobileTrackWidth();
      updateScrollArrows();
    }, 100);

    const onResize = () => {
      enforceMobileTrackWidth();
      updateScrollArrows();
    };

    el.addEventListener("scroll", updateScrollArrows, { passive: true });
    window.addEventListener("resize", onResize);

    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        enforceMobileTrackWidth();
        updateScrollArrows();
      });
      resizeObserver.observe(el);
    }

    return () => {
      clearTimeout(tm);
      el.removeEventListener("scroll", updateScrollArrows);
      window.removeEventListener("resize", onResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateScrollArrows, maxRounds, currentRound]);

  const scrollByAmount = (direction) => {
    const el = stepperRef.current;
    if (!el) return;
    const domSteps = Array.from(el.querySelectorAll(".debate-step"));
    if (domSteps.length > 0) {
      const first = domSteps[0];
      const second = domSteps[1];
      const inferredStepSpan =
        first && second
          ? Math.max(1, second.offsetLeft - first.offsetLeft)
          : Math.max(1, first?.getBoundingClientRect().width || 80);
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const stepDelta = direction * inferredStepSpan;
      const nextLeft = Math.max(0, Math.min(el.scrollLeft + stepDelta, maxScrollLeft));
      el.scrollLeft = nextLeft;
      try {
        el.scrollTo({ left: nextLeft, behavior: "smooth" });
      } catch (_) { }
    } else {
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const fallback = el.scrollLeft + direction * (el.clientWidth * 0.7 || 200);
      const clamped = Math.max(0, Math.min(fallback, maxScrollLeft));
      el.scrollLeft = clamped;
      try {
        el.scrollTo({ left: clamped, behavior: "smooth" });
      } catch (_) { }
    }
    setTimeout(updateScrollArrows, 140);
    return;

  };

  const handleArrow = (direction, e) => {
    if (e?.preventDefault) e.preventDefault();
    scrollByAmount(direction);
  };
  const steps3 = [
    { round: 1, label: "Round 1", sublabel: "Initial Arguments" },
    { round: 2, label: "Round 2", sublabel: "Challenges and Counterpoints" },
    { round: 3, label: "Round 3", sublabel: "Closing Arguments" },
    {
      round: 4,
      label: "Final Consensus & Summary",
      sublabel: "Synthesis (Claude)",
    },
  ];

  const steps1 = [
    { round: 1, label: "Round 1", sublabel: "Initial Arguments" },
  ];

  const steps5 = [
    { round: 1, label: "Round 1", sublabel: "Initial Arguments" },
    { round: 2, label: "Round 2", sublabel: "Challenges and Counterpoints" },
    { round: 3, label: "Round 3", sublabel: "Deep Analysis" },
    { round: 4, label: "Round 4", sublabel: "Final Refinements" },
    { round: 5, label: "Round 5", sublabel: "Procedural Considerations" },
    {
      round: 6,
      label: "Final Consensus & Summary",
      sublabel: "Synthesis (Claude)",
    },
  ];

  const steps8 = [
    { round: 1, label: "Round 1", sublabel: "Initial Arguments" },
    { round: 2, label: "Round 2", sublabel: "Challenges and Counterpoints" },
    { round: 3, label: "Round 3", sublabel: "Deep Analysis" },
    { round: 4, label: "Round 4", sublabel: "Strategic Responses" },
    { round: 5, label: "Round 5", sublabel: "Counter-Strategy & Rebuttals" },
    { round: 6, label: "Round 6", sublabel: "Evidence Analysis" },
    { round: 7, label: "Round 7", sublabel: "Procedural Considerations" },
    { round: 8, label: "Round 8", sublabel: "Consolidation" },
    {
      round: 9,
      label: "Final Consensus & Summary",
      sublabel: "Synthesis (Claude)",
    },
  ];

  let steps = steps5;
  let timeEstimate = "5 to 8 Minutes"; // Default for 5 rounds
  let stepWidth = "120px";
  let stepGap = "0px";

  if (maxRounds === 1) {
    steps = steps1;
    timeEstimate = "1 - 2 Minutes";
    stepWidth = "120px";
    stepGap = "0px";
  } else if (maxRounds === 3) {
    steps = steps3;
    timeEstimate = "3 - 5 Minutes";
    stepWidth = "120px";
    stepGap = "0px";
  } else if (maxRounds === 8) {
    steps = steps8;
    timeEstimate = "8 to 14 Minutes";
    stepWidth = "90px";
    stepGap = "8px";
  }

  useEffect(() => {
    const el = stepperRef.current;
    if (!el) return;
    const domSteps = Array.from(el.querySelectorAll(".debate-step"));
    if (!domSteps.length) return;

    const currentIdx = Math.max(0, Math.min((currentRound || 1) - 1, domSteps.length - 1));
    const targetStep = domSteps[currentIdx];
    if (!targetStep) return;
    if (targetStep?.scrollIntoView) {
      targetStep.scrollIntoView({
        behavior: "auto",
        inline: "nearest",
        block: "nearest",
      });
      setTimeout(updateScrollArrows, 50);
    }
  }, [currentRound, updateScrollArrows]);

  return (
    <div className="debate-progress-container">
      <div style={{ transform: "rotate(0deg)" }}>
        <AlertTriangle
          size={48}
          className="debate-progress-icon"
          fill="#f59e0b"
          color="white"
        />
      </div>

      <h2 className="debate-progress-title">
        AI Debate in Progress - Please Do Not Close This Window
      </h2>
      <p className="debate-progress-subtitle">
        Estimated Time for {maxRounds} Rounds:{" "}
        <span className="debate-progress-time">{timeEstimate}</span>
      </p>

      <p className="debate-progress-description">
        The system is coordinating arguments between{" "}
        {modelNames || "Claude, ChatGPT, and Gemini"}.
      </p>

      <div className="debate-progress-list-title">Each Round Involves:</div>
      <ul className="debate-progress-list">
        <li>Independent Legal Reasoning</li>
        <li>Counter-Arguments</li>
        <li>Cross-Model Refinement</li>
        <li>Structured IRAC Responses</li>
      </ul>

      <div className="debate-stepper-wrapper">
        {steps.length > 1 && (
          <button
            type="button"
            className={`debate-stepper-arrow debate-stepper-arrow-left${canScrollLeft ? "" : " is-dimmed"}`}
            onPointerDown={(e) => {
              // Mobile Safari/Chrome can drop "click" on elements over momentum scrollers.
              // Pointer handler only: prevents duplicate touch+click firings.
              handleArrow(-1, e);
            }}
            aria-label="Scroll rounds left"
            aria-disabled={!canScrollLeft}
          >
            <ChevronLeft size={18} />
          </button>
        )}
        <div
          ref={stepperRef}
          className={`debate-stepper ${steps.length === 1 ? "single-step" : ""}`}
          style={{
            "--step-count": steps.length,
            "--step-width": stepWidth,
            "--step-gap": stepGap,
          }}
        >
          {steps.map((step) => {
            const isActive = step.round === currentRound;
            const isCompleted = step.round < currentRound;

            let className = "debate-step";
            if (isActive) className += " active";
            if (isCompleted) className += " completed";

            return (
              <div key={step.round} className={className}>
                <div className="debate-step-circle">
                  {isCompleted ? <Check size={16} strokeWidth={3} /> : null}
                  {isActive && <span className="debate-step-dot" />}
                </div>
                <div
                  className="debate-step-label"
                  style={{
                    fontWeight: "bold",
                    color: darkMode ? "white" : "#193CB9",
                  }}
                >
                  {step.label}
                </div>
                <div
                  className="debate-step-sublabel"
                  style={{
                    fontWeight: "bold",
                    color: darkMode ? "white" : "#193CB9",
                  }}
                >
                  {step.sublabel}
                </div>
              </div>
            );
          })}
        </div>
        {steps.length > 1 && (
          <button
            type="button"
            className={`debate-stepper-arrow debate-stepper-arrow-right${canScrollRight ? "" : " is-dimmed"}`}
            onPointerDown={(e) => {
              handleArrow(1, e);
            }}
            aria-label="Scroll rounds right"
            aria-disabled={!canScrollRight}
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      <div className="debate-status-bar">
        <Hourglass
          className="debate-status-icon debate-hourglass-queued"
          size={20}
        />
        <span className="debate-status-text" style={{ fontWeight: "bold" }}>
          {currentRound > maxRounds
            ? "Generating Final Consensus Summary..."
            : `Generating Round ${currentRound || 1} of Debate...`}
        </span>
      </div>
    </div>
  );
};

// Component for selecting a chat to compare with
const ComparisonChatSelector = ({
  currentChatId,
  selectedChatId,
  onSelect,
  darkMode,
}) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/ai/chats`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Fetched chats response:", response.data);

        // Backend returns { chats: [...] }
        const allChats = response.data.chats || response.data || [];

        // Filter to only show debate chats (those with debateRounds) and exclude current chat
        const debateChats = allChats.filter((chat) => {
          const chatIdentifier = chat.chatId || chat._id;
          return (
            chat.debateRounds &&
            chat.debateRounds.length > 0 &&
            chatIdentifier !== currentChatId
          );
        });

        console.log("Filtered debate chats:", debateChats.length, debateChats);
        setChats(debateChats);
      } catch (error) {
        console.error("Failed to fetch chats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [currentChatId]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "1rem" }}>
        <Loader2
          size={24}
          className="spinner"
          style={{ animation: "spin 1s linear infinite" }}
        />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <p
        style={{
          color: darkMode ? "#94a3b8" : "#64748b",
          fontSize: "0.9rem",
          textAlign: "center",
          padding: "1rem",
        }}
      >
        No other debate chats available for comparison.
      </p>
    );
  }

  return (
    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
      {chats.map((chat) => {
        const chatIdentifier = chat.chatId || chat._id;
        const isSelected = chatIdentifier === selectedChatId;
        return (
          <div
            key={chatIdentifier}
            onClick={() => onSelect(chatIdentifier)}
            style={{
              padding: "0.75rem",
              marginBottom: "0.5rem",
              borderRadius: "6px",
              border: `2px solid ${isSelected ? "#6366f1" : darkMode ? "#334155" : "#e2e8f0"}`,
              backgroundColor: isSelected
                ? darkMode
                  ? "#1e3a8a"
                  : "#dbeafe"
                : darkMode
                  ? "#0f172a"
                  : "#ffffff",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!isSelected) {
                e.currentTarget.style.borderColor = darkMode
                  ? "#475569"
                  : "#cbd5e1";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                e.currentTarget.style.borderColor = darkMode
                  ? "#334155"
                  : "#e2e8f0";
              }
            }}
          >
            <div
              style={{
                fontSize: "0.9rem",
                fontWeight: "500",
                marginBottom: "0.25rem",
                color: darkMode ? "#f1f5f9" : "#0f172a",
              }}
            >
              {chat.title || chat.query || "Untitled Debate"}
            </div>
            <div
              style={{
                fontSize: "0.8rem",
                color: darkMode ? "#94a3b8" : "#64748b",
              }}
            >
              {chat.debateRounds?.length || 0} rounds •{" "}
              {new Date(chat.createdAt || chat.updatedAt).toLocaleDateString()}
            </div>
            {isSelected && (
              <div
                style={{
                  fontSize: "0.75rem",
                  marginTop: "0.25rem",
                  color: darkMode ? "#60a5fa" : "#2563eb",
                  fontWeight: "500",
                }}
              >
                ✓ Selected for comparison
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function getDebateRoundOptions(isLoggedIn) {
  return [
    {
      value: isLoggedIn ? 3 : 1,
      label: isLoggedIn ? "Short (3 rounds)" : "Short (1 round)",
    },
    ...(!isLoggedIn
      ? [{ value: 3, label: "Short (3 rounds)", lockedFor: "anonymous" }]
      : []),
    { value: 5, label: "Standard (5 rounds)", lockedFor: "free" },
    { value: 8, label: "Extended (8 rounds)", lockedFor: "trial" },
  ];
}

function getDebateRoundLabel(roundCount, isLoggedIn) {
  const match = getDebateRoundOptions(isLoggedIn).find((opt) => opt.value === roundCount);
  if (match) return match.label;
  if (roundCount === 1) return "Short (1 round)";
  if (roundCount === 3) return "Short (3 rounds)";
  if (roundCount === 5) return "Standard (5 rounds)";
  if (roundCount === 8) return "Extended (8 rounds)";
  return `${roundCount} rounds`;
}

function DebateSelectedRoundDisplay({
  roundCount,
  isLoggedIn,
  showLabel = true,
  className = "",
  compact = false,
}) {
  const label = getDebateRoundLabel(roundCount, isLoggedIn);
  return (
    <div
      className={`debate-controls-top-inner debate-selected-round-display${compact ? " debate-selected-round-display--compact" : ""}${className ? ` ${className}` : ""}`}
    >
      {showLabel && (
        <label htmlFor="roundSelect">Max Debate Rounds</label>
      )}
      <div className="debate-rounds-selector debate-rounds-selector--readonly">
        <span
          className="debate-round-option debate-round-option-active debate-round-option-readonly"
          aria-current="true"
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function DebateInfoPanels({ className = "debate-info-panels debate-info-panels--below" }) {
  const panels = [
    {
      icon: "/what debate mode does.svg",
      title: "What Debate Mode Does",
      body: (
        <>
          Automatically stress-tests your legal arguments using a panel of
          advanced AIs that you select.
          <br />
          <br />
          They collaborate and debate your legal issue over multiple rounds —
          each presenting arguments, challenging each other's reasoning, and
          refining their positions together.
        </>
      ),
    },
    {
      icon: "/the results.svg",
      title: "The result",
      body: (
        <>
          Each AI uses <strong>IRAC-style reasoning</strong> to keep the debate
          structured and rigorous — focusing on stronger, more strategic, and
          court-ready arguments.
          <br />
          <br />
          A final consensus summary from <strong>Claude</strong> showing the
          strongest arguments, potential weaknesses, and areas where all AIs
          agree or disagree.
        </>
      ),
    },
    {
      icon: "/why it matters.svg",
      title: "Why it matters",
      body: (
        <>
          Solo and small firm attorneys get multiple expert perspectives
          debating your case — like having a{" "}
          <strong>team of senior associates</strong> stress-testing every
          argument.
          <br />
          <br />
          <strong>
            PLUS: Paid users can re-run revised documents to instantly see what
            improved, what's still weak, and where all AIs agree — making
            iterative drafting effortless.
          </strong>
        </>
      ),
    },
  ];

  return (
    <div className={className}>
      {panels.map((panel, i) => (
        <motion.div
          key={panel.title}
          className="debate-info-panel"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.06 * (i + 1) }}
        >
          <div className="debate-info-head">
            <span className="debate-info-icon">
              <img src={panel.icon} alt="" aria-hidden="true" />
            </span>
            <h3 className="debate-info-title">{panel.title}</h3>
          </div>
          <p className="debate-info-text">{panel.body}</p>
        </motion.div>
      ))}
    </div>
  );
}

const DebateMode = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const anonDebateRoundLimit = 1;
  const defaultDebateRounds = isLoggedIn ? 3 : anonDebateRoundLimit;
  const isTrial = user?.subscriptionStatus === "trialing" || (!isLoggedIn); // Trial users + anonymous users get restrictions

  const [anonUsesLeft, setAnonUsesLeft] = useState(() => {
    if (isLoggedIn) return null;
    const used = parseInt(localStorage.getItem("anon_uses") || "0", 10);
    return Math.max(0, 3 - used);
  });
  const [showAnonSignupModal, setShowAnonSignupModal] = useState(false);
  const [subscriptionPaymentBlocked, setSubscriptionPaymentBlocked] =
    useState(false);
  const [subscriptionPaymentMessage, setSubscriptionPaymentMessage] =
    useState("");

  const { showError, showSuccess, showInfo, showWarning } = useNotification();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionNoticeShownRef = useRef(false);
  const paymentFailedToastShownRef = useRef(false);
  const paymentGraceToastShownRef = useRef(false);
  useEffect(() => {
    const hasPaymentIssue = shouldBlockSubscriptionAccess(user);
    const hasActiveAccess =
      !hasPaymentIssue &&
      (user?.is_tester ||
        user?.subscriptionStatus === "active" ||
        user?.subscriptionStatus === "trialing" ||
        (user?.aiMonthlyLimit || 0) > 0 ||
        (user?.adminAllocatedActionsTotal || 0) > 0);

    // Enforce flow order: pricing/access gate first, profile completion second.
    if (isAuthenticated && hasActiveAccess && needsProfileCompletion(user)) {
      navigate("/complete-profile", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const runSelectionKey = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const run = (sp.get("run") || "").trim().toLowerCase();
      const rerunRaw = sp.get("rerun");
      const rerun = rerunRaw ? parseInt(rerunRaw, 10) : NaN;
      if (run === "initial") return "initial";
      if (Number.isFinite(rerun) && rerun > 0) return `rerun:${rerun}`;
      return "latest";
    } catch (e) {
      return "latest";
    }
  }, [location.search]);
  const lastAppliedRunSelectionRef = useRef(null);

  // Sidebar state
  // Below 900px the sidebar is an off-canvas drawer (debate-workspace.css).
  // It starts closed there and needs a backdrop when open. matchMedia rather
  // than innerWidth: on iOS Safari innerWidth follows the zoomed layout
  // viewport, so a phone could report a "desktop" width, open the drawer by
  // default and draw it over the whole page with no backdrop — which is the
  // broken mobile layout QA saw.
  const sidebarOverlayQuery = "(max-width: 900px)";
  const [isSidebarOverlay, setIsSidebarOverlay] = useState(
    () => window.matchMedia(sidebarOverlayQuery).matches,
  );
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !window.matchMedia(sidebarOverlayQuery).matches,
  );
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth <= 500);
  const [isDebateIntroMobile, setIsDebateIntroMobile] = useState(window.innerWidth <= 940);
  const [headerHeightPx, setHeaderHeightPx] = useState(
    69,
  );

  // Existing state variables
  const [prompt, setPrompt] = useState(DEFAULT_DEBATE_PROMPT);
  const [debating, setDebating] = useState(false);
  const [round, setRound] = useState(0);
  const [maxRounds, setMaxRounds] = useState(defaultDebateRounds);
  const [debateSessionsCompleted, setDebateSessionsCompleted] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const mobileTooltipTimeoutRef = useRef(null);
  const [debateData, setDebateData] = useState(null);
  // Downloads collapse into a single menu button (UI state only).
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDebateRounds, setExpandedDebateRounds] = useState({});
  const [streamingRounds, setStreamingRounds] = useState({});
  const [streamingConsensus, setStreamingConsensus] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [composition, setComposition] = useState(compositionOptions[0]);

  const initialOpenSections = useMemo(
    () =>
      promptSections.reduce(
        (acc, section) => ({ ...acc, [section.key]: false }),
        {},
      ),
    [],
  );

  const [openSection, setOpenSection] = useState(initialOpenSections);
  // Removed old debateFile state in favor of useChatFiles
  // const [debateFile, setDebateFile] = useState(null);
  // const [debateFileUploading, setDebateFileUploading] = useState(false);

  const [loadingChat, setLoadingChat] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  // Lock query after user stops a debate; must click "New Debate" to run again
  const [queryLockedAfterStop, setQueryLockedAfterStop] = useState(false);
  // Comparison re-run state: track original debate query + files and when a revised document is uploaded.
  // originalDebateChatId is the first run (Run 1) only — never updated to a re-run. Comparison Dashboard
  // always compares "original vs. most recent re-run" (no rolling baseline).
  const [originalDebatePrompt, setOriginalDebatePrompt] = useState(null);
  const [originalDebateFilesSnapshot, setOriginalDebateFilesSnapshot] =
    useState(null);
  const [originalDebateChatId, setOriginalDebateChatId] = useState(null);
  const [latestRerunChatId, setLatestRerunChatId] = useState(null);
  const [isComparisonReRun, setIsComparisonReRun] = useState(false);
  const [queryLockedForComparison, setQueryLockedForComparison] =
    useState(false);
  const [comparisonDashboard, setComparisonDashboard] = useState(null);
  const [manualCompareMode, setManualCompareMode] = useState(false);
  const [selectedChatForComparison, setSelectedChatForComparison] =
    useState(null);
  const [comparisonNetAssessment, setComparisonNetAssessment] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [comparisonSections, setComparisonSections] = useState(null);
  // Controls whether the user has explicitly chosen to view the Comparison Dashboard
  const [showComparisonDashboard, setShowComparisonDashboard] = useState(false);
  // Phase 1: streaming 3-bullet content per card (all cards open from start, one card at a time)
  const [comparisonStreamingBullets, setComparisonStreamingBullets] = useState(
    {},
  );
  const [comparisonStreamingNetLabel, setComparisonStreamingNetLabel] =
    useState(null);
  // Which card is currently receiving stream (card id or null); used to show loading on active card
  const [comparisonStreamingCard, setComparisonStreamingCard] = useState(null);
  // Phase 2: full doc generating — show "Generating details..." on download button with loading
  const [comparisonGeneratingDetails, setComparisonGeneratingDetails] =
    useState(false);
  // Chat id used for detailed Comparison Dashboard document generation/status polling
  const [comparisonDetailChatId, setComparisonDetailChatId] = useState(null);
  // True after a re-run on the same chat (Run 1 + re-run stored under one chatId) so we show "Generate Comparison Dashboard"
  const [hasRerunOnSameChat, setHasRerunOnSameChat] = useState(false);
  // Count of reruns for this debate chat (0 = original run only)
  const [debateRerunCount, setDebateRerunCount] = useState(0);
  const isViewingRerunSnapshot = useMemo(() => {
    if (runSelectionKey === "initial") return false;
    if (runSelectionKey && runSelectionKey.startsWith("rerun:")) return true;
    // "latest" means rerun if the chat has reruns, otherwise original run
    if (runSelectionKey === "latest") return (debateRerunCount || 0) > 0;
    return false;
  }, [runSelectionKey, debateRerunCount]);

  // Initialize hooks for file management
  const {
    chatFiles,
    setChatFiles,
    addChatFile,
    removeChatFile,
    setChatFilesList,
    getCurrentChatFiles,
  } = useChatFiles(currentChatId || "new");

  // Derived flags used across uploader + results controls
  const originalDebateHadDocument = !!(
    originalDebateFilesSnapshot && originalDebateFilesSnapshot.length > 0
  );
  const onResultsScreen = !!(debateData?.rounds?.length > 0);
  // Once a debate is finished the setup form is only still actionable for the
  // document re-run flow. Without a document it degrades to a dead form whose
  // own helper text points at "New Debate" in the sidebar, so it is hidden and
  // the transcript gets the whole stage.
  const setupFormIsDead = onResultsScreen && !originalDebateHadDocument;
  // With a document the form still works, but ~540px of always-open controls
  // under the transcript is more weight than a secondary action deserves. It
  // collapses to a single row instead, the same way the Comparison Dashboard
  // does, and opens on demand.
  const setupFormIsCollapsible = onResultsScreen && originalDebateHadDocument;
  const [setupFormOpen, setSetupFormOpen] = useState(false);
  const displayedDebateSessionsCompleted =
    debateSessionsCompleted === null
      ? null
      : debateSessionsCompleted + 800;

  const maxAllowedFiles = getMaxFilesPerQuery(user);
  // For re-runs, only count NEW files (not the original run's files) against the limit
  const allDebateFiles = getCurrentChatFiles().debateFiles || [];
  const originalFileKeys = new Set(
    (originalDebateFilesSnapshot || []).map((f) => f.s3Key || f.documentId).filter(Boolean)
  );
  const hasOriginalFiles = originalDebateFilesSnapshot && originalDebateFilesSnapshot.length > 0;
  const newFilesCount = hasOriginalFiles
    ? allDebateFiles.filter((f) => !originalFileKeys.has(f.s3Key) && !originalFileKeys.has(f.documentId)).length
    : allDebateFiles.length;
  const isFileUploaderDisabled = newFilesCount >= maxAllowedFiles;
  const isClipperLockedAfterRerun =
    !!(debateData?.rounds?.length > 0) &&
    originalDebateHadDocument &&
    (hasRerunOnSameChat || debateRerunCount > 0);

  const fileUploadTooltip = isClipperLockedAfterRerun
    ? "Upload is disabled after a revised-document re-run is completed."
    : isFileUploaderDisabled
      ? maxAllowedFiles === 1
        ? "Only 1 document per query is allowed on free plans. Upgrade to upload multiple documents."
        : `Maximum of ${maxAllowedFiles} document${maxAllowedFiles > 1 ? "s" : ""} allowed for your plan.`
      : undefined;

  useEffect(() => {
    let isActive = true;
    let statsStream = null;

    const fetchDebateStats = () => {
      fetch(`${API_URL}/stats/debates-completed`, {
        credentials: "omit",
      })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => {
          if (!isActive) return;
          if (typeof data?.count === "number" && data.count >= 0) {
            setDebateSessionsCompleted(data.count);
          } else {
            setDebateSessionsCompleted(null);
          }
        })
        .catch(() => {
          if (!isActive) return;
          setDebateSessionsCompleted(null);
        });
    };

    fetchDebateStats();
    statsStream = new EventSource(`${API_URL}/stats/live`);
    statsStream.addEventListener("stats", (event) => {
      if (!isActive) return;
      try {
        const data = JSON.parse(event.data);
        if (
          typeof data?.debatesCompleted === "number" &&
          data.debatesCompleted >= 0
        ) {
          setDebateSessionsCompleted(data.debatesCompleted);
        }
      } catch (_) {
        // Keep last known value if stream payload is malformed.
      }
    });
    statsStream.onerror = () => {
      if (!isActive) return;
      fetchDebateStats();
    };

    return () => {
      isActive = false;
      if (statsStream) {
        statsStream.close();
      }
    };
  }, []);

  // Helper to determine if current debate files differ from the original snapshot.
  // Pass currentFilesOverride when the list has just been updated (e.g. after add/remove) so we don't rely on state having committed yet — allows re-run when user adds doc B without removing doc A.
  const updateComparisonReRunState = useCallback(
    (currentFilesOverride) => {
      try {
        const snapshot = originalDebateFilesSnapshot || [];
        const currentFiles = Array.isArray(currentFilesOverride)
          ? currentFilesOverride
          : getCurrentChatFiles().debateFiles || [];

        const hasOriginalDocs = Array.isArray(snapshot) && snapshot.length > 0;
        if (!hasOriginalDocs) {
          // No original document-backed debate recorded, so no re-run comparison mode
          setIsComparisonReRun(false);
          return;
        }

        const normalizeFiles = (files) =>
          files.map((f) => ({
            id: f.s3Key || f.documentId || f.fileName || "",
          }));

        const orig = normalizeFiles(snapshot);
        const curr = normalizeFiles(currentFiles);

        const origIds = orig.map((f) => f.id).sort();
        const currIds = curr.map((f) => f.id).sort();
        const changed =
          origIds.length !== currIds.length ||
          origIds.some((id, idx) => id !== currIds[idx]);

        setIsComparisonReRun(changed);
        if (changed && originalDebatePrompt) {
          setPrompt(originalDebatePrompt);
        }
      } catch (e) {
        console.warn("Failed to update comparison re-run state:", e);
      }
    },
    [
      getCurrentChatFiles,
      originalDebateFilesSnapshot,
      originalDebatePrompt,
      setPrompt,
    ],
  );

  const { fileUploading, handleFileUpload, uploadError } = useFileUpload();

  // Helper to parse the raw comparison dashboard text into structured sections
  // netAssessmentOverride: when loading from DB, pass saved netAssessment so parsing can use it
  const parseComparisonSections = useCallback(
    (text, netAssessmentOverride) => {
      if (!text || typeof text !== "string") return null;

      // Normalise newlines
      const normalized = text.replace(/\r\n/g, "\n");

      const getSection = (startPattern, nextPatterns) => {
        const startRegex = new RegExp(startPattern, "im");
        const startMatch = normalized.match(startRegex);
        if (!startMatch) return null;

        const startIndex = startMatch.index ?? 0;

        // Find the earliest next heading after this section
        let endIndex = normalized.length;
        nextPatterns.forEach((pat) => {
          const r = new RegExp(pat, "im");
          const m = normalized.slice(startIndex + 1).match(r);
          if (m) {
            const absIndex = startIndex + 1 + (m.index ?? 0);
            if (absIndex < endIndex) endIndex = absIndex;
          }
        });

        const sectionText = normalized.slice(startIndex, endIndex).trim();
        return sectionText || null;
      };

      // Match numeric headings (1. What Improved) or legacy Roman (I. WHAT IMPROVED)
      const whatImproved = getSection(
        "^\\s*(?:1\\.\\s+What Improved|I\\.\\s+WHAT IMPROVED)",
        [
          "^\\s*(?:2\\.\\s+What Remains Weak|II\\.\\s+WHAT REMAINS WEAK)",
          "^\\s*(?:3\\.\\s+Net Assessment|III\\.\\s+NET ASSESSMENT)",
        ],
      );

      const whatRemainsWeak = getSection(
        "^\\s*(?:2\\.\\s+What Remains Weak|II\\.\\s+WHAT REMAINS WEAK)",
        [
          "^\\s*(?:3\\.\\s+Net Assessment|III\\.\\s+NET ASSESSMENT)",
          "^\\s*(?:4\\.\\s+Areas of AI Consensus on Improvement|IV\\.\\s+AREAS OF AI CONSENSUS)",
        ],
      );

      const netAssessmentSection = getSection(
        "^\\s*(?:3\\.\\s+Net Assessment|III\\.\\s+NET ASSESSMENT)",
        [
          "^\\s*(?:4\\.\\s+Areas of AI Consensus on Improvement|IV\\.\\s+AREAS OF AI CONSENSUS)",
          "^\\s*(?:5\\.\\s+Remaining Litigation Risks|V\\.\\s+REMAINING LITIGATION RISKS)",
        ],
      );

      const consensusSection = getSection(
        "^\\s*(?:4\\.\\s+Areas of AI Consensus on Improvement|IV\\.\\s+AREAS OF AI CONSENSUS ON IMPROVEMENT)",
        [
          "^\\s*(?:5\\.\\s+Remaining Litigation Risks|V\\.\\s+REMAINING LITIGATION RISKS)",
        ],
      );

      const remainingRisksSection = getSection(
        "^\\s*(?:5\\.\\s+Remaining Litigation Risks|V\\.\\s+REMAINING LITIGATION RISKS)",
        [],
      );

      const extractBullets = (section) => {
        if (!section) return [];
        const lines = section.split("\n");
        const bullets = [];
        lines.forEach((line) => {
          const trimmed = line.trim();
          if (/^[-•\u2022]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
            bullets.push(trimmed.replace(/^[-•\u2022]\s+/, "").trim());
          }
        });
        return bullets;
      };

      let netAssessmentLabel =
        netAssessmentOverride ?? comparisonNetAssessment ?? null;
      let netAssessmentReason = null;
      if (netAssessmentSection) {
        const lines = netAssessmentSection
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        // Skip the first heading line; rest form the explanation
        if (lines.length > 1) {
          // If we don't already have a label from the backend, search the ENTIRE section for one of the three classifications
          // (AI may put the label in any bullet or in the prose, not just the first two lines)
          if (!netAssessmentLabel) {
            const fullSectionText = lines.slice(1).join(" ");
            const labelMatch = fullSectionText.match(
              /\b(STRONGER|MARGINALLY STRONGER|MARGINALLY WEAKER|NO MEANINGFUL IMPROVEMENT|NO MATERIAL CHANGE|MATERIALLY STRONGER|MATERIALLY WEAKER)\b/i,
            );
            if (labelMatch) {
              netAssessmentLabel = labelMatch[1].toUpperCase();
            }
          }
          netAssessmentReason = lines.slice(1).join(" ");
        }
      }

      const result = {
        whatImproved: extractBullets(whatImproved),
        whatRemainsWeak: extractBullets(whatRemainsWeak),
        netAssessmentLabel: netAssessmentLabel,
        netAssessmentReason: netAssessmentReason,
        aiConsensusOnImprovement: extractBullets(consensusSection),
        remainingLitigationRisks: extractBullets(remainingRisksSection),
      };

      // If everything is empty, return null so we can fall back to raw text
      const hasContent =
        (result.whatImproved && result.whatImproved.length > 0) ||
        (result.whatRemainsWeak && result.whatRemainsWeak.length > 0) ||
        !!result.netAssessmentReason ||
        (result.aiConsensusOnImprovement &&
          result.aiConsensusOnImprovement.length > 0) ||
        (result.remainingLitigationRisks &&
          result.remainingLitigationRisks.length > 0);

      return hasContent ? result : null;
    },
    [comparisonNetAssessment],
  );

  // Extract exactly the first 3 bullet headlines for UI only; PDF download shows full content (heading + 3 bullets + discussion).
  const extractSectionHeadlines = useCallback((sectionContent) => {
    if (sectionContent == null) return [];
    // If a section is already parsed into bullets (array of strings), treat each entry as its own bullet.
    // `parseComparisonSections()` strips bullet markers, so we can't rely on leading "•"/"-" here.
    if (Array.isArray(sectionContent)) {
      return sectionContent
        .map((v) => String(v || "").trim())
        .map((v) =>
          v
            .replace(/^[-*•\u2022\u2013\u2014]\s+/, "")
            .replace(/^\d+[\.\)]\s+/, "")
            .trim(),
        )
        .filter(Boolean)
        .slice(0, 3);
    }

    const text =
      typeof sectionContent === "string"
        ? sectionContent
        : String(sectionContent);
    let trimmed = text.trim();
    if (!trimmed) return [];

    const sectionHeadingPattern =
      /^\s*(?:[1-5]\.\s+(?:What Improved|What Remains Weak|Net Assessment|Areas of AI Consensus on Improvement|Remaining Litigation Risks)|I\.\s+WHAT IMPROVED|II\.\s+WHAT REMAINS WEAK|III\.\s+NET ASSESSMENT|IV\.\s+AREAS OF AI CONSENSUS ON IMPROVEMENT|V\.\s+REMAINING LITIGATION RISKS)\s*$/im;
    const firstLineEnd = trimmed.indexOf("\n");
    const firstLine =
      firstLineEnd >= 0 ? trimmed.slice(0, firstLineEnd).trim() : trimmed;
    if (sectionHeadingPattern.test(firstLine)) {
      trimmed = (
        firstLineEnd >= 0 ? trimmed.slice(firstLineEnd + 1) : ""
      ).trim();
    }

    const lines = trimmed.split("\n");
    const bulletHeadlines = [];
    let current = [];

    const flushBullet = () => {
      if (current.length === 0) return;
      const firstLineOfBlock = (current[0] || "")
        .replace(/^[-*•\u2022\u2013\u2014]\s+/, "")
        .replace(/^\d+[\.\)]\s+/, "")
        .trim();
      if (firstLineOfBlock) bulletHeadlines.push(firstLineOfBlock);
      current = [];
    };

    for (const line of lines) {
      const trimmedLine = line.trim();
      const isBulletStart =
        /^[-*•\u2022\u2013\u2014]\s+/.test(trimmedLine) ||
        /^\d+[\.\)]\s+/.test(trimmedLine);
      if (isBulletStart && current.length > 0) {
        flushBullet();
      }
      if (isBulletStart) {
        current.push(line);
      } else if (current.length > 0) {
        current.push(line);
      }
    }
    if (current.length > 0) flushBullet();

    return bulletHeadlines.slice(0, 3);
  }, []);

  // Map raw Net Assessment labels to exactly one of three classifications.
  // Stronger (Green) | Marginally Stronger (Yellow) | No Meaningful Improvement (Red)
  const mapNetAssessmentLabel = (rawLabel) => {
    if (!rawLabel) {
      return { cssSuffix: "", display: "" };
    }
    const norm = String(rawLabel).toUpperCase().trim();

    // Exact match first — the AI prompt guarantees one of these three phrases
    if (norm === "NO MEANINGFUL IMPROVEMENT") {
      return { cssSuffix: "no-meaningful-improvement", display: "No Meaningful Improvement" };
    }
    if (norm === "MARGINALLY STRONGER") {
      return { cssSuffix: "marginally-stronger", display: "Marginally Stronger" };
    }
    if (norm === "STRONGER") {
      return { cssSuffix: "stronger", display: "Stronger" };
    }

    // Fuzzy fallback — check order matters: red first, then yellow, then green
    // Red: No Meaningful Improvement
    if (
      norm.includes("NO MEANINGFUL") ||
      norm.includes("NO MATERIAL") ||
      norm.includes("NO CHANGE") ||
      norm.includes("UNCHANGED") ||
      norm.includes("WEAKER") ||
      norm.includes("WORSE") ||
      norm.includes("WORSENED") ||
      norm.includes("NOT IMPROVED")
    ) {
      return { cssSuffix: "no-meaningful-improvement", display: "No Meaningful Improvement" };
    }

    // Yellow: Marginally Stronger — must check before "STRONGER" alone
    if (
      norm.includes("MARGINALLY STRONGER") ||
      norm.includes("SLIGHTLY STRONGER") ||
      norm.includes("SOMEWHAT STRONGER") ||
      norm.includes("PARTIALLY") ||
      norm.includes("INCREMENTALLY")
    ) {
      return { cssSuffix: "marginally-stronger", display: "Marginally Stronger" };
    }

    // Green: Stronger (only if not preceded by a qualifier)
    if (
      norm.includes("STRONGER") ||
      norm.includes("SIGNIFICANTLY") ||
      norm.includes("SUBSTANTIALLY")
    ) {
      return { cssSuffix: "stronger", display: "Stronger" };
    }

    // Unknown label — no badge
    return { cssSuffix: "", display: "" };
  };

  // Helper to handle multiple file uploads (with duplicate detection: existing + same batch)
  const onFileUpload = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    if (fileList.length === 0) return;

    // Get existing files to check for duplicates
    let currentList = getCurrentChatFiles().debateFiles || [];
    const existingFileNames = [...(currentList.map((f) => f.fileName) || [])];
    const seenInBatch = new Set();

    const maxAllowedFiles = getMaxFilesPerQuery(user);
    // For re-runs, only count NEW files (not original run's) against the limit
    const origKeys = new Set(
      (originalDebateFilesSnapshot || []).map((f) => f.s3Key || f.documentId).filter(Boolean)
    );
    const hasOrigFiles = originalDebateFilesSnapshot && originalDebateFilesSnapshot.length > 0;
    const newFilesInList = hasOrigFiles
      ? currentList.filter((f) => !origKeys.has(f.s3Key) && !origKeys.has(f.documentId)).length
      : currentList.length;
    const remainingSlots = maxAllowedFiles - newFilesInList;

    if (remainingSlots <= 0) {
      showError(
        `Maximum of ${maxAllowedFiles} document${maxAllowedFiles > 1 ? "s" : ""} allowed per run.`,
      );
      return;
    }

    const filesToProcess =
      fileList.length > remainingSlots
        ? fileList.slice(0, remainingSlots)
        : fileList;

    if (fileList.length > remainingSlots) {
      showWarning(
        `Your plan allows a maximum of ${maxAllowedFiles} document${maxAllowedFiles > 1 ? "s" : ""} per query.`,
        "Limit Reached",
      );
    }

    for (const file of filesToProcess) {
      // Skip if already uploaded in this chat
      if (existingFileNames.includes(file.name)) {
        showError(
          `The brief "${file.name}" has already been uploaded and is a duplicate.`,
        );
        continue;
      }
      // Skip if same name already selected in this batch
      if (seenInBatch.has(file.name)) {
        showError(
          `The brief "${file.name}" has already been uploaded and is a duplicate.`,
        );
        continue;
      }

      const result = await handleFileUpload(file, "debateFiles");
      if (result && result.success) {
        addChatFile("debateFiles", result.fileData);
        currentList = [...currentList, result.fileData];
        existingFileNames.push(file.name);
        seenInBatch.add(file.name);
        // Pass pending list so re-run is enabled even before state commits (e.g. add doc B without removing doc A)
        updateComparisonReRunState(currentList);
      } else {
        console.error("Upload failed for file:", file.name);
      }
    }

  };

  const onFileRemove = async (file) => {
    // 1. Remove from local state immediately
    const currentList = getCurrentChatFiles().debateFiles || [];
    const nextList = currentList.filter(
      (f) => f.s3Key !== file.s3Key && f.fileName !== file.fileName,
    );
    removeChatFile("debateFiles", file.fileId || file.s3Key || file.fileName);
    // Pass pending list so state is correct before next render
    updateComparisonReRunState(nextList);

    // 2. Identify if this file belongs to a committed/persisted chat
    const isNewChat = !currentChatId || currentChatId === "new";

    // 3. If persisted chat, and we have a valid fileId, fire DELETE API
    if (!isNewChat) {
      const targetFileId = file?.fileId;
      if (targetFileId) {
        try {
          console.log(`[DebateMode] Deleting committed file: ${targetFileId}`);
          await deleteFile(targetFileId);
        } catch (error) {
          console.error("Error deleting file via API:", error);
          showWarning(
            "File removed from view, but failed to delete from backend.",
            "Deletion Warning",
          );
        }
      }
    }
  };

  // Helper to request a Comparison Dashboard from the backend (streaming: phase 1 = 3 bullets per card, phase 2 = full doc; download shows "Generating details..." during phase 2).
  // Single chatId: backend uses debateRun1 vs debateRerun on same document. Legacy: originalChatId + rerunChatId (two documents).
  const runDebateComparison = useCallback(
    async (originalId, rerunId) => {
      // Hard guard: never generate comparison dashboard for anonymous users
      // Check both auth context AND localStorage token to avoid false "login required" after rerun
      const hasToken = !!localStorage.getItem("token");
      if (!isLoggedIn && !hasToken) {
        showInfo(
          "Please login to access this feature.",
          "Login required for comparison dashboard",
        );
        return;
      }

      const useSingleChat = originalId && (originalId === rerunId || !rerunId);
      if (!originalId && !rerunId) return;
      if (!useSingleChat && !rerunId) return;

      try {
        setComparisonLoading(true);
        setComparisonError(null);
        setComparisonSections(null);
        setComparisonDashboard(null);
        setComparisonNetAssessment(null);
        setComparisonDetailChatId(useSingleChat ? originalId : rerunId);
        setComparisonStreamingBullets({
          whatImproved: "",
          whatRemainsWeak: "",
          netAssessment: "",
          aiConsensusOnImprovement: "",
          remainingLitigationRisks: "",
        });
        setComparisonStreamingNetLabel(null);
        setComparisonStreamingCard(null);
        setComparisonGeneratingDetails(false);
        setShowComparisonDashboard(true);

        let authToken = localStorage.getItem("token");
        if (!authToken) {
          try {
            const tokenResponse = await axios.get(`${API_URL}/auth/token`);
            authToken = tokenResponse.data.token;
            if (authToken) {
              localStorage.setItem("token", authToken);
            }
          } catch (tokenError) {
            console.warn("Could not get token from backend:", tokenError);
          }
        }

        const headers = { "Content-Type": "application/json" };
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const response = await fetch(`${API_URL}/ai/debate-comparison-stream`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify(
            useSingleChat
              ? { chatId: originalId }
              : { originalChatId: originalId, rerunChatId: rerunId },
          ),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message ||
            `Failed to generate comparison dashboard (${response.status})`,
          );
        }

        if (!response.body || typeof response.body.getReader !== "function") {
          throw new Error(
            "Connection was interrupted while the app was in the background. Please try again.",
          );
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const cardIds = [
          "whatImproved",
          "whatRemainsWeak",
          "netAssessment",
          "aiConsensusOnImprovement",
          "remainingLitigationRisks",
          "netAssessmentLabel",
        ];

        const processEvent = (event, data) => {
          try {
            if (event === "cardStart") {
              const { card } = data;
              if (card) setComparisonStreamingCard(card);
            } else if (event === "cardStream") {
              const { card, delta } = data;
              if (card === "netAssessmentLabel") {
                setComparisonStreamingNetLabel(
                  (prev) => (prev || "") + (delta || ""),
                );
              } else if (cardIds.includes(card)) {
                setComparisonStreamingBullets((prev) => {
                  const base = prev && typeof prev === "object" ? prev : {};
                  return {
                    ...base,
                    [card]: (base[card] || "") + (delta || ""),
                  };
                });
              }
            } else if (event === "cardDone") {
              const { card, content } = data;
              setComparisonStreamingCard(null);
              if (card === "netAssessmentLabel") {
                const label = (content || "").trim().toUpperCase();
                const m = label.match(
                  /\b(STRONGER|MARGINALLY STRONGER|NO MEANINGFUL IMPROVEMENT)\b/,
                );
                setComparisonStreamingNetLabel(m ? m[1] : label || null);
              } else if (cardIds.includes(card)) {
                setComparisonStreamingBullets((prev) => {
                  const base = prev && typeof prev === "object" ? prev : {};
                  return {
                    ...base,
                    [card]: (content || "").trim(),
                  };
                });
              }
            } else if (event === "error") {
              throw new Error(data.message || "Comparison failed");
            }
          } catch (e) {
            if (event === "error") throw e;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const block of parts) {
            let eventName = "";
            const dataLine = block
              .split("\n")
              .find((l) => l.startsWith("data: "));
            const eventLine = block
              .split("\n")
              .find((l) => l.startsWith("event: "));
            if (eventLine) eventName = eventLine.slice(7).trim();
            if (dataLine) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                processEvent(eventName, data);
              } catch (parseErr) {
                // skip
              }
            }
          }
        }
        if (buffer.trim()) {
          const dataLine = buffer
            .split("\n")
            .find((l) => l.startsWith("data: "));
          const eventLine = buffer
            .split("\n")
            .find((l) => l.startsWith("event: "));
          const eventName = eventLine ? eventLine.slice(7).trim() : "";
          if (dataLine) {
            try {
              const data = JSON.parse(dataLine.slice(6));
              processEvent(eventName, data);
            } catch (parseErr) { }
          }
        }

        // Phase 2 (detailed document) is intentionally NOT generated by default.
        // Users must click "Generate Detailed Document Comparison Dashboard" to start it.
      } catch (error) {
        console.error("Error generating debate comparison:", error);
        setComparisonError(
          error.message || "Failed to generate comparison dashboard.",
        );
        setComparisonGeneratingDetails(false);
        setComparisonStreamingBullets(null);
        setComparisonStreamingNetLabel(null);
        setComparisonStreamingCard(null);
      } finally {
        setComparisonLoading(false);
      }
    },
    [parseComparisonSections],
  );

  const comparisonCollapsedSyncing = false; // kept for JSX references
  const comparisonCardsGenerating =
    comparisonLoading &&
    !comparisonSections &&
    !!comparisonStreamingBullets;

  const handleToggleComparisonDashboard = useCallback(() => {
    setShowComparisonDashboard((prev) => !prev);
  }, []);

  const handleGenerateDetailedComparisonDashboard = useCallback(async () => {
    if (!comparisonDetailChatId) return;
    if (comparisonCardsGenerating) return;
    if (comparisonGeneratingDetails) return;

    try {
      setComparisonGeneratingDetails(true);

      let authToken = localStorage.getItem("token");
      if (!authToken) {
        try {
          const tokenResponse = await axios.get(`${API_URL}/auth/token`);
          authToken = tokenResponse.data.token;
          if (authToken) localStorage.setItem("token", authToken);
        } catch (tokenError) {
          console.warn("Could not get token from backend:", tokenError);
        }
      }

      const headers = { "Content-Type": "application/json" };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const startRes = await fetch(
        `${API_URL}/ai/debate-comparison-generate-full`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ chatId: comparisonDetailChatId }),
        },
      );
      if (!startRes.ok) {
        const errorData = await startRes.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `Failed to start detailed comparison generation (${startRes.status})`,
        );
      }

      const pollHeaders = {};
      if (authToken) pollHeaders["Authorization"] = `Bearer ${authToken}`;

      const maxAttempts = 60;
      const delayMs = 3000;
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const statusRes = await fetch(
          `${API_URL}/ai/debate-comparison-status/${comparisonDetailChatId}`,
          {
            method: "GET",
            headers: pollHeaders,
            credentials: "include",
          },
        );
        if (statusRes.ok) {
          const status = await statusRes.json().catch(() => ({}));
          if (status && status.ready) {
            setComparisonDashboard(status.comparison || "");
            setComparisonNetAssessment(status.netAssessment || null);
            // Keep sections in sync if backend provides them (Phase 1 bullets)
            if (status.sections && typeof status.sections === "object") {
              const s = status.sections;
              const toArray = (value) => {
                if (!value) return [];
                if (Array.isArray(value)) return value;
                return String(value)
                  .split("\n")
                  .map((l) => l.trim())
                  .filter(Boolean);
              };
              setComparisonSections({
                whatImproved: toArray(s.whatImproved),
                whatRemainsWeak: toArray(s.whatRemainsWeak),
                netAssessmentSection: s.netAssessmentSection || "",
                aiConsensusOnImprovement: toArray(s.aiConsensusOnImprovement),
                remainingLitigationRisks: toArray(s.remainingLitigationRisks),
                netAssessmentLabel: s.netAssessmentLabel || status.netAssessment || null,
              });
            }
            setComparisonGeneratingDetails(false);
            return;
          }
        }
        await sleep(delayMs);
      }

      setComparisonGeneratingDetails(false);
      showWarning(
        "Detailed comparison is taking longer than expected. Please try again shortly.",
        "Still generating",
      );
    } catch (err) {
      console.warn("Detailed comparison generation failed:", err);
      setComparisonGeneratingDetails(false);
      showError(err.message || "Failed to generate detailed comparison dashboard.");
    }
  }, [
    comparisonDetailChatId,
    comparisonCardsGenerating,
    comparisonGeneratingDetails,
    showWarning,
    showError,
  ]);

  const handleDownloadComparisonDashboard = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (comparisonCardsGenerating) {
      return;
    }

    const hasStreamingContent =
      comparisonStreamingBullets &&
      typeof comparisonStreamingBullets === "object" &&
      Object.values(comparisonStreamingBullets).some(
        (v) => (v || "").toString().trim().length > 0,
      );

    if (!comparisonDashboard && !comparisonSections && !hasStreamingContent) {
      return;
    }

    const toText = (val) => {
      if (val == null) return "";
      return Array.isArray(val) ? val.join("\n") : String(val);
    };
    const hasContent = (val) => toText(val).trim().length > 0;

    // For the full document, prefer the backend's DEBATE_COMPARISON_DOCUMENT_SINGLE output (`comparisonDashboard`)
    // so the PDF matches the complete narrative document. Fall back to reconstructed sections,
    // and as a last resort use the in-memory streaming bullets.
    let comparisonText = "";
    if (comparisonDashboard) {
      comparisonText = removeCitationMarkers(
        removeSeparatorLines(comparisonDashboard),
      );
    } else if (comparisonSections) {
      const sectionHeadings = [
        "I. WHAT IMPROVED",
        "II. WHAT REMAINS WEAK",
        "III. NET ASSESSMENT",
        "IV. AREAS OF AI CONSENSUS ON IMPROVEMENT",
        "V. REMAINING LITIGATION RISKS",
      ];
      const parts = [];
      if (hasContent(comparisonSections.whatImproved)) {
        parts.push(
          `${sectionHeadings[0]}\n\n${toText(comparisonSections.whatImproved)}`,
        );
      }
      if (hasContent(comparisonSections.whatRemainsWeak)) {
        parts.push(
          `${sectionHeadings[1]}\n\n${toText(comparisonSections.whatRemainsWeak)}`,
        );
      }
      if (
        comparisonSections.netAssessmentLabel ||
        hasContent(
          comparisonSections.netAssessmentSection ??
          comparisonSections.netAssessmentReason ??
          "",
        )
      ) {
        const netContent = [
          comparisonSections.netAssessmentLabel
            ? comparisonSections.netAssessmentLabel
            : "",
          toText(
            comparisonSections.netAssessmentSection ??
            comparisonSections.netAssessmentReason ??
            "",
          ),
        ]
          .filter(Boolean)
          .join("\n\n");
        parts.push(`${sectionHeadings[2]}\n\n${netContent}`);
      }
      if (hasContent(comparisonSections.aiConsensusOnImprovement)) {
        parts.push(
          `${sectionHeadings[3]}\n\n${toText(comparisonSections.aiConsensusOnImprovement)}`,
        );
      }
      if (hasContent(comparisonSections.remainingLitigationRisks)) {
        parts.push(
          `${sectionHeadings[4]}\n\n${toText(comparisonSections.remainingLitigationRisks)}`,
        );
      }
      comparisonText = parts.join("\n\n");
    } else if (hasStreamingContent) {
      const sectionHeadings = [
        "I. WHAT IMPROVED",
        "II. WHAT REMAINS WEAK",
        "III. NET ASSESSMENT",
        "IV. AREAS OF AI CONSENSUS ON IMPROVEMENT",
        "V. REMAINING LITIGATION RISKS",
      ];
      const parts = [];
      if (hasContent(comparisonStreamingBullets.whatImproved)) {
        parts.push(
          `${sectionHeadings[0]}\n\n${toText(comparisonStreamingBullets.whatImproved)}`,
        );
      }
      if (hasContent(comparisonStreamingBullets.whatRemainsWeak)) {
        parts.push(
          `${sectionHeadings[1]}\n\n${toText(comparisonStreamingBullets.whatRemainsWeak)}`,
        );
      }
      if (hasContent(comparisonStreamingBullets.netAssessment)) {
        const netLines = [];
        if (comparisonStreamingNetLabel) {
          netLines.push(comparisonStreamingNetLabel);
        }
        netLines.push(toText(comparisonStreamingBullets.netAssessment));
        parts.push(`${sectionHeadings[2]}\n\n${netLines.join("\n\n")}`);
      }
      if (hasContent(comparisonStreamingBullets.aiConsensusOnImprovement)) {
        parts.push(
          `${sectionHeadings[3]}\n\n${toText(comparisonStreamingBullets.aiConsensusOnImprovement)}`,
        );
      }
      if (hasContent(comparisonStreamingBullets.remainingLitigationRisks)) {
        parts.push(
          `${sectionHeadings[4]}\n\n${toText(comparisonStreamingBullets.remainingLitigationRisks)}`,
        );
      }
      comparisonText = parts.join("\n\n");
    }

    if (!comparisonText || !comparisonText.trim()) {
      showError("No comparison content available to download.");
      return;
    }

    try {
      showInfo("Generating PDF... This may take a moment.");

      const doc = (
        <ComparisonDashboardPDFDocument
          comparisonText={comparisonText.trim()}
          netAssessmentLabel={comparisonSections?.netAssessmentLabel || comparisonNetAssessment || null}
        />
      );
      const blob = await pdf(doc).toBlob();
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `comparison-dashboard_${dateStr}.pdf`;
      await saveBlobWithPlatformSupport(blob, filename, "application/pdf");

      showSuccess("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating comparison PDF:", error);
      showError("Failed to generate PDF. Please try again.");
    }
  }, [
    comparisonCardsGenerating,
    comparisonDashboard,
    comparisonSections,
    comparisonStreamingBullets,
    comparisonStreamingNetLabel,
    showInfo,
    showSuccess,
    showError,
  ]);

  const comparisonDownloadAvailable = useMemo(() => {
    if (comparisonCardsGenerating) return false;
    const hasStreamingContent =
      comparisonStreamingBullets &&
      typeof comparisonStreamingBullets === "object" &&
      Object.values(comparisonStreamingBullets).some(
        (v) => (v || "").toString().trim().length > 0,
      );

    return Boolean(
      (comparisonDashboard && comparisonDashboard.trim()) ||
      comparisonSections ||
      hasStreamingContent,
    );
  }, [
    comparisonCardsGenerating,
    comparisonDashboard,
    comparisonSections,
    comparisonStreamingBullets,
  ]);

  // Editor state for edit/export functionality
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [editorTitle, setEditorTitle] = useState("");
  const [editorMeta, setEditorMeta] = useState({
    type: null,
    roundIndex: null,
    role: null,
  });
  const [showPreRunConfirm, setShowPreRunConfirm] = useState(false);
  // New debate chat to show in sidebar as soon as processing starts
  const [newChatForSidebar, setNewChatForSidebar] = useState(null);

  // Get chatId from URL params
  const { chatId } = useParams();

  // Existing refs
  const debateFileInputRef = useRef(null);
  // New refs
  const debateEndRef = useRef(null);

  // Refs for auto-scrolling streaming responses
  const streamingResponseRefs = useRef({}); // { roundNumber: { model: element } }
  const streamingConsensusRef = useRef(null);

  // Track user scroll state for each response container
  const userScrolledRefs = useRef({}); // { refKey: boolean }
  const lastScrollTopRefs = useRef({}); // { refKey: number }

  // Helper to check if user is near bottom
  const isNearBottom = (element, threshold = 100) => {
    if (!element) return true;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= threshold;
  };

  // Helper to find the actual scrollable element
  const getScrollableElement = (element) => {
    if (!element) return null;
    const style = window.getComputedStyle(element);
    if (
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll"
    ) {
      return element;
    }
    return element;
  };

  // Refs for stop debate functionality
  const streamReaderRef = useRef(null);
  const abortControllerRef = useRef(null);
  const operationIdRef = useRef(null); // So we can tell backend to stop via /ai/debate-stop
  const debateStoppedRef = useRef(false);
  const isStreamingDebateRef = useRef(false); // Track if we're actively streaming a debate
  const currentDebateDataRef = useRef({
    rounds: [],
    consensus: "",
    prompt: "",
    maxRounds: 0,
  });
  const streamingDebateChatIdRef = useRef(null);
  const debateWakeLockRef = useRef(null);

  const releaseDebateWakeLock = useCallback(async () => {
    try {
      const wl = debateWakeLockRef.current;
      if (wl && typeof wl.release === "function") {
        await wl.release();
      }
    } catch (_) {
      /* ignore */
    }
    debateWakeLockRef.current = null;
  }, []);

  const acquireDebateWakeLock = useCallback(async () => {
    try {
      if (!("wakeLock" in navigator)) return;
      await releaseDebateWakeLock();
      debateWakeLockRef.current = await navigator.wakeLock.request("screen");
      const wl = debateWakeLockRef.current;
      if (wl && typeof wl.addEventListener === "function") {
        wl.addEventListener("release", () => {
          debateWakeLockRef.current = null;
        });
      }
    } catch (_) {
      /* permission denied or unsupported */
    }
  }, [releaseDebateWakeLock]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!isStreamingDebateRef.current) return;
      void acquireDebateWakeLock();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [acquireDebateWakeLock]);

  // Helper to fully reset debate state for a fresh debate
  const resetDebate = useCallback(() => {
    setPrompt(DEFAULT_DEBATE_PROMPT);
    setDebating(false);
    setQueryLockedAfterStop(false);
    setQueryLockedForComparison(false);
    setRound(0);
    setMaxRounds(defaultDebateRounds);
    setShowTooltip(false);
    setDebateData(null);
    setError(null);
    setExpandedDebateRounds({});
    setStreamingRounds({});
    setStreamingConsensus("");
    setExpandedDebateRounds({});
    setStreamingRounds({});
    setStreamingConsensus("");
    setOpenSection(initialOpenSections);

    // Clear all comparison / original-run tracking so a fresh debate
    // never inherits "document-backed" state from a previous chat.
    setOriginalDebatePrompt(null);
    setOriginalDebateFilesSnapshot(null);
    setOriginalDebateChatId(null);
    setLatestRerunChatId(null);
    setIsComparisonReRun(false);
    setHasRerunOnSameChat(false);
    setDebateRerunCount(0);
    setComparisonDashboard(null);
    setComparisonSections(null);
    setComparisonNetAssessment(null);
    setComparisonError(null);
    setShowComparisonDashboard(false);
    setManualCompareMode(false);
    setSelectedChatForComparison(null);
    setComparisonStreamingBullets({});
    setComparisonStreamingNetLabel(null);
    setComparisonGeneratingDetails(false);

    // Executive Summary is per-chat; hide panel when resetting
    setShowExecutiveSummary(false);
    setExecutiveSummaryLoading(false);

    // debateFile state removed
    removeChatFile("debateFiles", null); // Clear all debate files
    setLoadingChat(false);
    setCurrentChatId(null);

    // Navigate to fresh debate page (no chatId) to ensure clean state
    navigate("/debate-mode", { replace: true });
  }, [
    initialOpenSections,
    removeChatFile,
    navigate,
    setOriginalDebatePrompt,
    setOriginalDebateFilesSnapshot,
    setOriginalDebateChatId,
    setLatestRerunChatId,
    setIsComparisonReRun,
    setHasRerunOnSameChat,
    setDebateRerunCount,
    setComparisonDashboard,
    setComparisonSections,
    setComparisonNetAssessment,
    setComparisonError,
    setShowComparisonDashboard,
    setManualCompareMode,
    setSelectedChatForComparison,
    setComparisonStreamingBullets,
    setComparisonStreamingNetLabel,
    setComparisonGeneratingDetails,
  ]);

  // Logged-in: sync subscription; hard-lock UI on failed payment; otherwise gate first-time users to Pricing
  useEffect(() => {
    if (!isLoggedIn) {
      setSubscriptionPaymentBlocked(false);
      subscriptionNoticeShownRef.current = false;
      paymentFailedToastShownRef.current = false;
      return;
    }
    const checkSubscription = async () => {
      try {
        const token = localStorage.getItem("token");
        const config = { withCredentials: true };
        if (token) config.headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${API_URL}/payments/subscription-status`,
          config,
        );
        const sub = res.data;
        const mergedUser = {
          ...user,
          subscriptionStatus: sub.subscriptionStatus,
          subscriptionAccessUntil: sub.subscriptionAccessUntil,
        };
        const paymentBlocked =
          sub.paymentAccessBlocked ?? shouldBlockSubscriptionAccess(mergedUser);
        const paymentWarning = isSubscriptionPaymentWarning(mergedUser);
        const paymentMessage = getSubscriptionPaymentMessage(user, sub);

        setSubscriptionPaymentBlocked(!!paymentBlocked);
        setSubscriptionPaymentMessage(paymentMessage || "");

        if (paymentBlocked) {
          if (!paymentFailedToastShownRef.current) {
            paymentFailedToastShownRef.current = true;
            showWarning(
              paymentMessage ||
              "Subscription suspended — update your payment method to restore access.",
              "Payment Required",
            );
          }
          return;
        }
        paymentFailedToastShownRef.current = false;

        if (paymentWarning && !paymentGraceToastShownRef.current) {
          paymentGraceToastShownRef.current = true;
          showWarning(
            paymentMessage ||
            "Payment failed — update billing to avoid losing access.",
            "Payment Failed",
          );
        } else if (!paymentWarning) {
          paymentGraceToastShownRef.current = false;
        }

        const hasActive =
          sub.subscriptionStatus === "active" ||
          sub.subscriptionStatus === "trialing";
        const hasUsage =
          (sub.aiMonthlyLimit || 0) > 0 ||
          (sub.subscriptionActionsLimit || 0) > 0 ||
          (user?.aiMonthlyLimit || 0) > 0 ||
          (user?.subscriptionActionsLimit || 0) > 0;
        const hasAdminAllocatedActions =
          (sub.adminAllocatedActionsTotal || 0) > 0 ||
          (user?.adminAllocatedActionsTotal || 0) > 0;
        if (!hasActive && !hasUsage && !hasAdminAllocatedActions) {
          if (!subscriptionNoticeShownRef.current) {
            subscriptionNoticeShownRef.current = true;
            showInfo(
              "Please subscribe to a plan or get usage allotted from admin to use AI features.",
              "Subscription Required",
            );
          }
          navigate("/pricing", { replace: true });
        } else {
          subscriptionNoticeShownRef.current = false;
        }
      } catch {
        /* ignore */
      }
    };
    checkSubscription();
  }, [isLoggedIn, navigate, showInfo, showWarning, user]);

  useEffect(() => {
    const statePrompt = location.state?.prompt;
    if (statePrompt) {
      setPrompt(statePrompt);
    }
    if (location.state?.forceFreshDebate) {
      resetDebate();
    }
  }, [location.state, resetDebate]);

  // Cleanup file URL on unmount (only for object URLs, not S3 URLs)
  useEffect(() => {
    return () => {
      // Only revoke object URLs (temporary), not S3 URLs (permanent)
      const currentFiles = chatFiles[currentChatId || "new"]?.debateFiles || [];
      currentFiles.forEach((file) => {
        if (file?.fileUrl && !file?.s3Url) {
          URL.revokeObjectURL(file.fileUrl);
        }
      });
    };
  }, [chatFiles, currentChatId]);

  // Keep current, next, and (when in summary phase) final summary round auto-expanded during streaming
  useEffect(() => {
    if (!debating || !maxRounds) return;
    const currentRound = round || 1;
    setExpandedDebateRounds((prev) => {
      const next = { ...prev };
      if (currentRound >= 1) next[currentRound - 1] = true;
      if (currentRound < maxRounds) next[currentRound] = true;
      if (currentRound > maxRounds) next[maxRounds] = true;
      return next;
    });
  }, [debating, round, maxRounds]);

  // When navigating to /debate-mode without a chatId, ensure we start a fresh debate
  // BUT don't reset if we're actively streaming (to avoid interrupting navigation)
  useEffect(() => {
    if (!chatId && !isStreamingDebateRef.current) {
      console.log(
        "[RESET CHECK] No chatId and not streaming, resetting debate",
      );
      resetDebate();
    } else if (!chatId && isStreamingDebateRef.current) {
      console.log("[RESET CHECK] No chatId but streaming, skipping reset");
    }
  }, [chatId, resetDebate]);

  // Handle resize for sidebar
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobileScreen(width <= 500);
      setIsDebateIntroMobile(width <= 940);
    };
    // The drawer only opens or closes on its own when the layout actually
    // crosses the overlay breakpoint — not on every resize event, which on a
    // phone fires for the keyboard and for zoom.
    const mq = window.matchMedia(sidebarOverlayQuery);
    const handleOverlayChange = (e) => {
      setIsSidebarOverlay(e.matches);
      setSidebarOpen(!e.matches);
    };
    window.addEventListener("resize", handleResize);
    if (mq.addEventListener) mq.addEventListener("change", handleOverlayChange);
    else mq.addListener(handleOverlayChange);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (mq.removeEventListener) mq.removeEventListener("change", handleOverlayChange);
      else mq.removeListener(handleOverlayChange);
    };
  }, []);

  // Load debate chat from URL parameter
  useEffect(() => {
    if (!chatId || !user) return;

    let cancelled = false;
    const ac = new AbortController();

    const loadDebateChat = async () => {
      // Don't reload if we're actively streaming a debate
      if (isStreamingDebateRef.current) {
        console.log("[LOAD DEBATE] Skipping load - debate is streaming");
        return;
      }

      debateStoppedRef.current = false;

      try {
        setLoadingChat(true);
        setError(null);
        const token = localStorage.getItem("token");
        const config = {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          signal: ac.signal,
        };

        const response = await axios.get(
          `${API_URL}/ai/chats/${encodeURIComponent(chatId)}`,
          config,
        );
        if (cancelled) return;

        const chat = response.data.chat;

        if (!chat) {
          showError("Chat not found");
          navigate("/debate-mode");
          return;
        }

        // Only load if this is a debate chat
        if (
          chat.chatType !== "debate" &&
          (!chat.debateRounds || chat.debateRounds.length === 0)
        ) {
          showError("This is not a debate chat");
          navigate("/debate-mode");
          return;
        }

        // Populate state with chat data
        setCurrentChatId(chatId);
        setPrompt(chat.query || "");
        setMaxRounds(chat.maxRounds || 5);

        const reruns = [
          ...(Array.isArray(chat.debateRerunHistory)
            ? chat.debateRerunHistory
            : []),
          ...(chat.debateRerun ? [chat.debateRerun] : []),
        ].filter(Boolean);

        const hasReruns = reruns.length > 0;
        setDebateRerunCount(reruns.length);

        // For chats with reruns, the original run lives in debateRun1.
        // For chats with no reruns, fall back to the top-level debateRounds/finalSummary.
        const run1 = hasReruns
          ? chat.debateRun1 || null
          : chat.debateRun1 ||
          (chat.debateRounds && chat.debateRounds.length > 0
            ? {
              debateRounds: chat.debateRounds,
              finalSummary: chat.finalSummary || "",
              maxRounds: chat.maxRounds || 5,
            }
            : null);

        const pickRunPayload = () => {
          if (runSelectionKey === "initial") {
            return run1;
          }
          if (runSelectionKey && runSelectionKey.startsWith("rerun:")) {
            const idx = parseInt(runSelectionKey.split(":")[1], 10) - 1;
            return reruns[idx] || null;
          }
          // latest (default): show latest rerun if present, else original
          return reruns[reruns.length - 1] || run1;
        };

        let selected = pickRunPayload();

        console.log("[DEBATE][LOAD] Run selection resolved", {
          chatId,
          runSelectionKey,
          hasRun1: !!run1,
          run1Rounds:
            run1 && Array.isArray(run1.debateRounds)
              ? run1.debateRounds.length
              : 0,
          rerunCount: reruns.length,
          selectedType: selected
            ? selected === run1
              ? "run1"
              : "rerun"
            : null,
          selectedRounds:
            selected && Array.isArray(selected.debateRounds)
              ? selected.debateRounds.length
              : 0,
        });


        const selectedHasRounds =
          selected &&
          Array.isArray(selected.debateRounds) &&
          selected.debateRounds.length > 0;

        // Set debate data only from the explicitly selected snapshot (no fallbacks)
        if (selectedHasRounds) {
          const normalizedSelectedRounds = normalizeDebateRounds(
            selected.debateRounds,
          );

          // CRITICAL: Only populate consensusSummary if there is NO summary round
          // already embedded inside the rounds array. Both code paths render the
          // final consensus, so only ONE of them must hold the data at a time.
          const hasSummaryRoundInLoaded = normalizedSelectedRounds.some(
            (r) =>
              ((chat.maxRounds || 5) > 0 && r.round > (chat.maxRounds || 5)) ||
              (r.arguments?.consensus && !r.arguments?.advocate),
          );

          const maxForStop = chat.maxRounds || 5;
          let resolvedStopped = !!(chat.stopped ?? false);
          let resolvedStoppedAtRound = chat.stoppedAtRound || 0;

          if (typeof selected?.stopped === "boolean") {
            resolvedStopped = !!selected.stopped;
            resolvedStoppedAtRound =
              selected.stoppedAtRound ?? resolvedStoppedAtRound;
          } else if (
            selected.status === "failed" ||
            selected?.debateError?.type === "user_stop"
          ) {
            resolvedStopped = true;
            resolvedStoppedAtRound =
              selected.stoppedAtRound ?? resolvedStoppedAtRound;
          } else if (
            resolvedStopped &&
            typeof selected?.stopped !== "boolean" &&
            !selected?.debateError &&
            !!(chat?.stopped ?? false)
          ) {
            // Rare legacy/stale docs: root `stopped` was true while the selected run actually
            // finished (embedded summary round). Avoid using finalSummary text alone —
            // stopped saves often use "**[Debate Stopped]**" placeholders there.
            const advocateRoundsCompleted = normalizedSelectedRounds.filter(
              (r) =>
                typeof r.round === "number" &&
                r.round >= 1 &&
                r.round <= maxForStop &&
                r.arguments?.advocate,
            ).length;
            if (
              advocateRoundsCompleted >= maxForStop &&
              hasSummaryRoundInLoaded &&
              selected.status !== "failed"
            ) {
              resolvedStopped = false;
              resolvedStoppedAtRound = 0;
            }
          }

          const debateErrorPayload = resolvedStopped
            ? selected.debateError ||
            chat.debateError ||
            (selected.error
              ? {
                message: selected.error,
                type: selected.status || "failed",
              }
              : null)
            : null;

          setDebateData({
            rounds: normalizedSelectedRounds,
            consensusSummary: hasSummaryRoundInLoaded
              ? ""
              : removeSeparatorLines(
                removeCitationMarkers(selected.finalSummary || ""),
              ),
            maxRounds: chat.maxRounds || 5,
            stopped: resolvedStopped,
            stoppedAtRound: resolvedStoppedAtRound,
            debateError: debateErrorPayload,
          });
          lastAppliedRunSelectionRef.current = runSelectionKey;

          // Restore panel composition used for this debate (lock will show it)
          if (chat.composition) {
            const value = chat.composition?.value ?? chat.composition;
            const matched = compositionOptions.find(
              (opt) => opt.value === value || opt.key === value,
            );
            if (matched) setComposition(matched);
          }

          // Auto-expand all rounds for the selected run so the user never
          // lands on a visually "empty" page when switching runs in the sidebar.
          const allExpanded = {};
          normalizedSelectedRounds.forEach((_, idx) => {
            allExpanded[idx] = true;
          });
          setExpandedDebateRounds(allExpanded);

          setQueryLockedAfterStop(!!resolvedStopped);
          if (resolvedStopped) {
            console.log("[LOAD DEBATE] Debate was stopped, locking query");
          }
        }

        // Reset original document-backed state for this chat; we'll enable it
        // again below only if we detect real attached documents.
        setOriginalDebateFilesSnapshot(null);
        setOriginalDebatePrompt(null);

        // Restore files if attached
        if (chat.attachedFiles) {
          let filesToLoad = [];

          // Preferred: explicit debateFiles array saved by newer frontend
          if (Array.isArray(chat.attachedFiles.debateFiles)) {
            filesToLoad = chat.attachedFiles.debateFiles;
          }
          // Backward-compat: single 'debate' file field
          else if (chat.attachedFiles.debate) {
            filesToLoad = [chat.attachedFiles.debate];
          }
          // Fallback: treat all values on attachedFiles as document entries
          else if (chat.attachedFiles instanceof Map) {
            filesToLoad = Array.from(chat.attachedFiles.values());
          } else if (typeof chat.attachedFiles === "object") {
            filesToLoad = Object.values(chat.attachedFiles);
          }

          if (filesToLoad.length > 0) {
            // Optimistically set files before fetching signed URLs
            setChatFilesList("debateFiles", filesToLoad, chatId);

            // Fetch signed URLs asynchronously to avoid blocking the UI
            (async () => {
              const processedFiles = [];
              let updated = false;
              for (const fileData of filesToLoad) {
                try {
                  if (fileData.s3Key && !fileData.s3Key.startsWith("temp_")) {
                    const signedUrlResponse = await axios.get(
                      `${API_URL}/files/signed-url/${encodeURIComponent(
                        fileData.s3Key,
                      )}`,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    processedFiles.push({
                      ...fileData,
                      fileUrl: signedUrlResponse.data.url,
                      s3Url: signedUrlResponse.data.url,
                    });
                    updated = true;
                  } else {
                    processedFiles.push(fileData);
                  }
                } catch (fileError) {
                  console.error("Error restoring debate file:", fileError);
                  processedFiles.push(fileData);
                }
              }
              if (updated) {
                setChatFilesList("debateFiles", processedFiles, chatId);
              }
            })();

            // Restore snapshot so "Re-run with Revised Document" shows ONLY when
            // the original run actually had one or more real documents.
            const hasRounds =
              chat.debateRounds?.length > 0 ||
              chat.debateRun1?.debateRounds?.length > 0;
            if (hasRounds) {
              // Treat as a document-backed debate only if at least one file
              // has a persistent identifier (s3Key or documentId).
              const docFiles = filesToLoad.filter(
                (f) => f && (f.s3Key || f.documentId),
              );

              if (docFiles.length > 0) {
                setOriginalDebateFilesSnapshot(
                  docFiles.map((f) => ({
                    fileName: f.fileName,
                    s3Key: f.s3Key,
                    documentId: f.documentId,
                  })),
                );
                if (chat.query) setOriginalDebatePrompt(chat.query.trim());
              } else {
                // Explicitly clear any stale snapshot so a query-only debate
                // never shows document re-run UI after refresh.
                setOriginalDebateFilesSnapshot(null);
                setOriginalDebatePrompt(null);
              }
            }
          }
        }

        // Restore first run / latest run so "Generate Comparison Dashboard" works when returning
        if (chat.originalDebateChatId) {
          setOriginalDebateChatId(chat.originalDebateChatId);
          setLatestRerunChatId(null);
        } else if (chat.latestRerunChatId) {
          setOriginalDebateChatId(chatId);
          setLatestRerunChatId(chat.latestRerunChatId);
        } else {
          // For chats with completed rounds and documents, use the chat's own ID
          // so re-run can be triggered (originalDebateChatId is required for didSendRerun)
          const hasCompletedRounds = chat.debateRounds?.length > 0 || chat.debateRun1?.debateRounds?.length > 0;
          const attachedFiles = chat.attachedFiles?.debateFiles || (chat.attachedFiles?.debate ? [chat.attachedFiles.debate] : Object.values(chat.attachedFiles || {}));
          const chatHasDocs = Array.isArray(attachedFiles) && attachedFiles.some((f) => f && (f.s3Key || f.documentId));
          if (hasCompletedRounds && chatHasDocs) {
            setOriginalDebateChatId(chatId);
          } else {
            setOriginalDebateChatId(null);
          }
          setLatestRerunChatId(null);
        }
        // Restore saved Comparison Dashboard for this chat (so it persists on refresh)
        const saved = chat.debateComparisonDashboard;
        if (saved && (saved.comparison || saved.sections)) {
          setComparisonDetailChatId(chatId);
          setComparisonDashboard(saved.comparison || null);
          if (saved.originalChatId && !chat.originalDebateChatId) {
            setOriginalDebateChatId(saved.originalChatId);
          }
          const savedSections = saved.sections || null;
          setComparisonNetAssessment(
            saved.netAssessment || savedSections?.netAssessmentLabel || null,
          );
          if (savedSections) {
            const toArray = (value) => {
              if (!value) return [];
              if (Array.isArray(value)) return value;
              return String(value)
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean);
            };

            setComparisonSections({
              whatImproved: toArray(savedSections.whatImproved),
              whatRemainsWeak: toArray(savedSections.whatRemainsWeak),
              netAssessmentSection: savedSections.netAssessmentSection || "",
              aiConsensusOnImprovement: toArray(
                savedSections.aiConsensusOnImprovement,
              ),
              remainingLitigationRisks: toArray(
                savedSections.remainingLitigationRisks,
              ),
              netAssessmentLabel:
                savedSections.netAssessmentLabel || saved.netAssessment || null,
            });
          } else {
            setComparisonSections(
              parseComparisonSections(saved.comparison, saved.netAssessment),
            );
          }
          setShowComparisonDashboard(saved.collapsed !== true);
          setComparisonLoading(false);
          setComparisonError(null);
          setComparisonStreamingBullets(null);
          setComparisonStreamingNetLabel(null);
          setComparisonGeneratingDetails(false);
        } else {
          // No saved comparison dashboard for this chat – clear any previous chat's state
          setComparisonDashboard(null);
          setComparisonNetAssessment(null);
          setComparisonSections(null);
          setComparisonError(null);
          setShowComparisonDashboard(false);
          setComparisonLoading(false);
          setComparisonStreamingBullets(null);
          setComparisonStreamingNetLabel(null);
          setComparisonGeneratingDetails(false);
        }
        // Restore executive summary from backend (survives refresh)
        if (chat.debateExecutiveSummary) {
          setExecutiveSummaryByChatId((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            [chatId]: chat.debateExecutiveSummary,
          }));
          setShowExecutiveSummary(true);
        }

        // Same-chat model: Run 1 + re-run stored under one chatId
        const hasRerun = !!(chat.debateRun1 && chat.debateRerun);
        setHasRerunOnSameChat(hasRerun);
        setComparisonLoading(false);
        // Restore locked-query UI when this chat has a re-run + comparison (so refresh shows lock)
        // Lock query when: has rerun, has comparison dashboard, OR original debate had documents (ready for re-run)
        const attachedArr = chat.attachedFiles?.debateFiles || (chat.attachedFiles?.debate ? [chat.attachedFiles.debate] : Object.values(chat.attachedFiles || {}));
        const chatHadDocuments = Array.isArray(attachedArr) && attachedArr.some((f) => f && (f.s3Key || f.documentId));
        const chatHasRounds = chat.debateRounds?.length > 0 || chat.debateRun1?.debateRounds?.length > 0;
        setQueryLockedForComparison(
          !!(hasRerun || chat.debateComparisonDashboard?.comparison || (chatHasRounds && chatHadDocuments)),
        );
      } catch (error) {
        if (
          cancelled ||
          axios.isCancel?.(error) ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("Error loading debate chat:", error);
        if (error.response?.status === 404) {
          showError("Chat not found");
          navigate("/debate-mode");
        } else {
          showError(
            "Error loading chat: " +
            (error.response?.data?.message || error.message),
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingChat(false);
        }
      }
    };

    loadDebateChat();
    return () => {
      cancelled = true;
      ac.abort();
    };
    // parseComparisonSections is intentionally omitted: it changes with comparison UI state and retriggered this effect, causing duplicate fetches and stale overwrites (SCRUM-387).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, user, navigate, showError, runSelectionKey]);

  // Clear comparison and debate state when on new-debate page (no chatId) so first run never shows stale dashboard
  // or triggers comparison (e.g. hadExistingRoundsBeforeRun / originalDebateChatId from previous chat).
  useEffect(() => {
    if (!chatId) {
      setComparisonDashboard(null);
      setComparisonSections(null);
      setComparisonError(null);
      setComparisonStreamingBullets(null);
      setComparisonStreamingNetLabel(null);
      setComparisonGeneratingDetails(false);
      setShowComparisonDashboard(false);
      setDebateData(null);
      setOriginalDebateChatId(null);
      setHasRerunOnSameChat(false);
      setDebateRerunCount(0);
    }
  }, [chatId]);

  const handlePrimaryCta = () => {
    if (isAuthenticated) {
      navigate("/pricing");
    } else {
      navigate("/signup");
    }
  };

  const toggleSection = (sectionKey) => {
    setOpenSection((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const toggleDebateRound = (roundIndex) => {
    setExpandedDebateRounds((prev) => ({
      ...prev,
      [roundIndex]: !prev[roundIndex],
    }));
  };

  const handleSampleClick = (text) => {
    setPrompt(text);
  };

  // Editor helper functions
  const extractPlainText = (html = "") => {
    if (typeof window === "undefined") return html || "";
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        ["link", "blockquote"],
        ["clean"],
      ],
    }),
    [],
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "indent",
      "color",
      "background",
      "link",
      "blockquote",
    ],
    [],
  );

  // Editor handlers
  const handleOpenEditor = (type, title, value, meta = {}) => {
    setEditorMeta({ type, ...meta });
    setEditorTitle(title);
    // If value is already HTML (contains HTML tags), use it directly without conversion
    const isHtml =
      value &&
      (value.includes("<p>") ||
        value.includes("<h") ||
        value.includes("<ol>") ||
        value.includes("<ul>"));
    setEditorContent(isHtml ? value : convertTextToHtml(value));
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
  };

  // PDF Generation Functions - Parse HTML and convert to react-pdf components
  // Uses pure string parsing (no DOM) since react-pdf doesn't have DOM access
  const parseHtmlToPDFComponents = (text, styles, options = {}) => {
    if (!text) return [<Text key="empty" style={styles.paragraph}></Text>];

    // Use the same conversion function as frontend to get HTML with headings
    // Remove separator lines and citation markers before processing
    let processedHtml = convertTextToHtml(
      removeCitationMarkers(removeSeparatorLines(text)),
    );

    const components = [];
    let keyCounter = 0;

    // Helper to strip HTML tags and get plain text
    const stripHtmlTags = (html) => {
      return html
        .replace(/<[^>]+>/g, "") // Remove all HTML tags
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
    };

    // Helper to extract text with inline formatting
    const extractFormattedText = (html) => {
      const parts = [];
      let currentIndex = 0;

      // Find all <strong> and <b> tags
      const boldRegex = /<(strong|b)>(.*?)<\/\1>/gi;
      let match;
      const matches = [];

      while ((match = boldRegex.exec(html)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: stripHtmlTags(match[2]),
          isBold: true,
        });
      }

      // Sort matches by position
      matches.sort((a, b) => a.start - b.start);

      // Build parts array
      matches.forEach((boldMatch) => {
        // Add text before bold
        if (boldMatch.start > currentIndex) {
          const beforeText = stripHtmlTags(
            html.substring(currentIndex, boldMatch.start),
          );
          if (beforeText) {
            parts.push(beforeText);
          }
        }
        // Add bold text
        if (boldMatch.text) {
          parts.push({ bold: true, text: boldMatch.text });
        }
        currentIndex = boldMatch.end;
      });

      // Add remaining text
      if (currentIndex < html.length) {
        const remaining = stripHtmlTags(html.substring(currentIndex));
        if (remaining) {
          parts.push(remaining);
        }
      }

      // If no formatting found, return plain text
      if (parts.length === 0) {
        const plainText = stripHtmlTags(html);
        if (plainText) {
          parts.push(plainText);
        }
      }

      return parts;
    };

    // Remove empty paragraphs and excessive spacing before parsing
    // This prevents "P" characters from empty paragraph tags
    processedHtml = processedHtml.replace(/<p[^>]*>\s*<\/p>/gi, ""); // Remove empty paragraphs
    processedHtml = processedHtml.replace(
      /<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi,
      "",
    ); // Remove paragraphs with only line breaks
    processedHtml = processedHtml.replace(/(<p[^>]*>)\s*(<\/p>)/gi, ""); // Remove any remaining empty paragraphs
    // Strip Unicode box-drawing/typographic separator chars that Helvetica cannot render
    // (they appear as repeated "P" fallback glyphs in PDF output)
    processedHtml = processedHtml.replace(/[─-╿‒-―‗┄-╋═-╬]+/gu, "");

    // Extract headings with improved regex
    const headingRegex = /<h([1-6])[^>]*>(.*?)<\/h[1-6]>/gi;
    const headingMatches = [];
    let headingMatch;

    while ((headingMatch = headingRegex.exec(processedHtml)) !== null) {
      const headingText = stripHtmlTags(headingMatch[2]);
      if (headingText.trim().length > 0) {
        headingMatches.push({
          level: parseInt(headingMatch[1]) || 2,
          text: headingText,
          index: headingMatch.index,
        });
      }
    }

    // Extract paragraphs (including those with attributes)
    // Skip empty paragraphs and handle separation/spacing
    const paragraphRegex = /<p[^>]*>(.*?)<\/p>/gi;
    const paragraphMatches = [];
    let paraMatch;

    while ((paraMatch = paragraphRegex.exec(processedHtml)) !== null) {
      const paraContent = paraMatch[1];
      // Skip empty paragraphs or paragraphs with only whitespace/line breaks
      const textContent = stripHtmlTags(paraContent);
      if (textContent.trim().length > 0) {
        paragraphMatches.push({
          text: paraContent, // Keep HTML for formatting extraction
          index: paraMatch.index,
        });
      }
    }

    // Extract lists
    const listRegex = /<(ul|ol)[^>]*>(.*?)<\/\1>/gi;
    const listMatches = [];
    let listMatch;

    while ((listMatch = listRegex.exec(processedHtml)) !== null) {
      const listContent = listMatch[2];
      const items = [];
      const itemRegex = /<li[^>]*>(.*?)<\/li>/gi;
      let itemMatch;

      while ((itemMatch = itemRegex.exec(listContent)) !== null) {
        const itemText = stripHtmlTags(itemMatch[1]);
        if (itemText && itemText.trim().length > 0) {
          items.push(itemText.trim());
        }
      }

      if (items.length > 0) {
        listMatches.push({
          items: items,
          index: listMatch.index,
        });
      }
    }

    // Also handle horizontal rules and separators
    // Replace <hr> tags with spacing (remove them completely)
    processedHtml = processedHtml.replace(/<hr[^>]*>/gi, "");


    processedHtml = processedHtml.replace(/^[-=_*~]{3,}\s*$/gm, "");
    processedHtml = processedHtml.replace(/^[─━═―—\u2500-\u257F]{2,}\s*$/gmu, "");

    // Remove any div or span elements that might be used for separators
    processedHtml = processedHtml.replace(
      /<div[^>]*class="[^"]*separator[^"]*"[^>]*>.*?<\/div>/gi,
      "",
    );
    processedHtml = processedHtml.replace(
      /<div[^>]*style="[^"]*border[^"]*"[^>]*>.*?<\/div>/gi,
      "",
    );

    // Remove any border styling from paragraphs that might create visual separators
    processedHtml = processedHtml.replace(/style="[^"]*border[^"]*"/gi, "");

    // Combine all matches and sort by position
    const allMatches = [
      ...headingMatches.map((m) => ({ ...m, type: "heading" })),
      ...paragraphMatches.map((m) => ({ ...m, type: "paragraph" })),
      ...listMatches.map((m) => ({ ...m, type: "list" })),
    ].sort((a, b) => a.index - b.index);

    // Convert to components
    allMatches.forEach((match) => {
      if (match.type === "heading") {
        const headingStyle =
          match.level === 1
            ? {
              fontSize: 18,
              fontWeight: "bold",
              marginTop: 12,
              marginBottom: 8,
              color: "#193CB9",
            }
            : match.level === 2
              ? {
                fontSize: 16,
                fontWeight: "bold",
                marginTop: 10,
                marginBottom: 6,
                color: "#193CB9",
              }
              : {
                fontSize: 14,
                fontWeight: "bold",
                marginTop: 8,
                marginBottom: 4,
                color: "#2563eb",
              };
        if (match.text) {
          components.push(
            <Text key={`heading-${keyCounter++}`} style={headingStyle}>
              {match.text}
            </Text>,
          );
          const normalizedHeading = (match.text || "")
            .toUpperCase()
            .replace(/\s+/g, " ")
            .trim();
          if (
            normalizedHeading === "III. NET ASSESSMENT" &&
            options.netAssessmentLabel
          ) {
            const badgeColors = options.getBadgeColors
              ? options.getBadgeColors(options.netAssessmentLabel)
              : null;
            if (badgeColors) {
              components.push(
                <View
                  key={`net-assessment-badge-${keyCounter++}`}
                  style={{
                    alignSelf: "flex-start",
                    backgroundColor: badgeColors.bg,
                    borderColor: badgeColors.border,
                    borderWidth: 1,
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    marginBottom: 8,
                  }}
                >
                  <Text
                    style={{
                      color: badgeColors.color,
                      fontSize: 10,
                      fontWeight: "bold",
                    }}
                  >
                    {options.netAssessmentLabel}
                  </Text>
                </View>,
              );
            }
          }
        }
      } else if (match.type === "paragraph") {
        const parts = extractFormattedText(match.text);
        if (parts.length > 0) {
          components.push(
            <View key={`para-${keyCounter++}`} style={{ marginBottom: 6 }}>
              <Text style={styles.paragraph}>
                {parts.map((part, idx) => {
                  if (typeof part === "object" && part.bold) {
                    return (
                      <Text key={idx} style={{ fontWeight: "bold" }}>
                        {part.text}
                      </Text>
                    );
                  }
                  return <Text key={idx}>{part}</Text>;
                })}
              </Text>
            </View>,
          );
        }
      } else if (match.type === "list") {
        components.push(
          <View
            key={`list-${keyCounter++}`}
            style={{ marginBottom: 8, marginLeft: 20 }}
          >
            {match.items.map((item, idx) => (
              <Text
                key={`item-${idx}`}
                style={{ marginBottom: 4, fontSize: 11 }}
              >
                • {item}
              </Text>
            ))}
          </View>,
        );
      }
    });

    // If no structured content found, use plain text fallback
    if (components.length === 0) {
      const plainText = stripHtmlTags(processedHtml);
      if (plainText.trim()) {
        // Split by double newlines for paragraphs
        const paragraphs = plainText.split(/\n\s*\n/).filter((p) => p.trim());
        if (paragraphs.length > 0) {
          return paragraphs.map((para, idx) => (
            <Text key={`fallback-${idx}`} style={styles.paragraph}>
              {para.trim()}
            </Text>
          ));
        }
        // If no double newlines, use single newlines
        const lines = plainText.split(/\n/).filter((p) => p.trim());
        if (lines.length > 0) {
          return lines.map((line, idx) => (
            <Text key={`fallback-${idx}`} style={styles.paragraph}>
              {line.trim()}
            </Text>
          ));
        }
        // Last resort: use entire text as one paragraph
        return [
          <Text key="fallback-0" style={styles.paragraph}>
            {plainText.trim()}
          </Text>,
        ];
      }
    }

    return components.length > 0
      ? components
      : [<Text key="empty" style={styles.paragraph}></Text>];
  };

  // PDF Document Component
  const DebatePDFDocument = ({
    prompt,
    rounds,
    consensus,
    maxRounds,
    comparison,
  }) => {
    const styles = StyleSheet.create({
      page: {
        paddingTop: 50,
        paddingBottom: 60,
        paddingLeft: 45,
        paddingRight: 45,
        fontSize: 11,
        fontFamily: "Helvetica",
        lineHeight: 1.5,
      },
      header: {
        marginBottom: 20,
        paddingBottom: 10,
        borderBottom: "2 solid #193CB9",
      },
      title: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#193CB9",
        marginBottom: 5,
      },
      subtitle: {
        fontSize: 12,
        color: "#666",
        marginBottom: 3,
      },
      section: {
        marginBottom: 25,
      },
      comparisonHeader: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#111827",
        marginBottom: 6,
      },
      comparisonSubheader: {
        fontSize: 11,
        color: "#6b7280",
        marginBottom: 10,
      },
      roundTitle: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#193CB9",
        marginTop: 15,
        marginBottom: 10,
        paddingBottom: 5,
        borderBottom: "1 solid #ccc",
        break: true,
      },
      roleTitle: {
        fontSize: 13,
        fontWeight: "bold",
        color: "#2563eb",
        marginTop: 10,
        marginBottom: 5,
      },
      paragraph: {
        marginBottom: 8,
        textAlign: "justify",
      },
      consensusTitle: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#193CB9",
        marginTop: 20,
        marginBottom: 10,
        paddingTop: 10,
        borderTop: "2 solid #193CB9",
        break: true,
      },
      footer: {
        position: "absolute",
        bottom: 25,
        left: 45,
        right: 45,
        textAlign: "center",
        fontSize: 9,
        color: "#666",
      },
    });

    const roleDisplayNames = {
      advocate: "Advocate",
      critic: "Critical Analyst",
      strategist: "Strategic/Procedural Analyst",
      consensus: "Final Consensus Summary",
    };

    return (
      <PDFDocument>
        <Page size="A4" style={styles.page}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>AI Debate Transcript</Text>
            <Text style={styles.subtitle}>
              Generated: {new Date().toLocaleString()}
            </Text>
            <Text style={styles.subtitle}>Total Rounds: {maxRounds}</Text>
          </View>

          {/* Comparison Dashboard (if available) */}
          {comparison && comparison.trim().length > 0 && (
            <View style={styles.section}>
              <Text style={styles.comparisonHeader}>Comparison Dashboard</Text>
              <Text style={styles.comparisonSubheader}>
                Original Brief vs. Revised Brief
              </Text>
              {parseHtmlToPDFComponents(comparison, styles)}
            </View>
          )}

          {/* Prompt */}
          <View style={styles.section}>
            <Text style={styles.roleTitle}>Debate Topic / Prompt:</Text>
            {parseHtmlToPDFComponents(prompt, styles)}
          </View>

          {/* Rounds */}
          {(() => {
            const roundKeys = Object.keys(rounds).sort((a, b) => Number(a) - Number(b));
            let didPrintFinalConsensus = false;

            return (
              <>
                {roundKeys.map((roundNum) => {
                  const roundData = rounds[roundNum];
                  const roundNumber = Number(roundNum);
                  // Identify if this is a final summary round (often created after the max rounds)

                  const isSummaryRound =
                    (maxRounds > 0 && roundNumber > maxRounds) ||
                    (!!(roundData.consensus) && !(roundData.advocate));

                  if (isSummaryRound) didPrintFinalConsensus = true;

                  return (
                    <View key={roundNum} style={styles.section}>
                      {isSummaryRound ? (
                        <Text style={styles.consensusTitle}>
                          Final Consensus & Summary
                        </Text>
                      ) : (
                        <Text style={styles.roundTitle}>
                          Round {roundNumber}/{maxRounds}
                        </Text>
                      )}

                      {/* Advocate */}
                      {roundData.advocate &&
                        (roundData.advocate.text ||
                          roundData.advocate.fullText) && (
                          <View>
                            <Text style={styles.roleTitle}>
                              {roundData.advocate.model || "ChatGPT"}'s Argument (
                              {roleDisplayNames.advocate})
                            </Text>
                            {parseHtmlToPDFComponents(
                              roundData.advocate.fullText ||
                              roundData.advocate.text,
                              styles,
                            )}
                          </View>
                        )}

                      {/* Critic */}
                      {roundData.critic &&
                        (roundData.critic.text || roundData.critic.fullText) && (
                          <View>
                            <Text style={styles.roleTitle}>
                              {roundData.critic.model || "Claude"}'s Argument (
                              {roleDisplayNames.critic})
                            </Text>
                            {parseHtmlToPDFComponents(
                              roundData.critic.fullText || roundData.critic.text,
                              styles,
                            )}
                          </View>
                        )}

                      {/* Strategist */}
                      {roundData.strategist &&
                        (roundData.strategist.text ||
                          roundData.strategist.fullText) && (
                          <View>
                            <Text style={styles.roleTitle}>
                              {roundData.strategist.model || "Gemini"}'s Argument (
                              {roleDisplayNames.strategist})
                            </Text>
                            {parseHtmlToPDFComponents(
                              roundData.strategist.fullText ||
                              roundData.strategist.text,
                              styles,
                            )}
                          </View>
                        )}

                      {/* Per-round or Final Consensus Summary */}
                      {roundData.consensus &&
                        (roundData.consensus.text ||
                          roundData.consensus.fullText) && (
                          <View>
                            {!isSummaryRound && (
                              <Text style={styles.roleTitle}>
                                Round {roundNumber} Consensus Summary
                              </Text>
                            )}
                            {parseHtmlToPDFComponents(
                              roundData.consensus.fullText ||
                              roundData.consensus.text,
                              styles,
                            )}
                          </View>
                        )}
                    </View>
                  );
                })}

                {/* Final Consensus (Show only if not already printed in the rounds loop) */}
                {consensus && !didPrintFinalConsensus && (
                  <View style={styles.section}>
                    <Text style={styles.consensusTitle}>
                      Final Consensus & Summary
                    </Text>
                    {parseHtmlToPDFComponents(consensus, styles)}
                  </View>
                )}
              </>
            );
          })()}
        </Page>
      </PDFDocument>
    );
  };

  // Comparison Dashboard PDF Document — same structure and styling as debate rounds PDF
  const ComparisonDashboardPDFDocument = ({ comparisonText, netAssessmentLabel }) => {
    const getBadgeColors = (label) => {
      const norm = (label || "").toUpperCase().trim();
      if (norm === "STRONGER") return { bg: "#dcfce7", color: "#166534", border: "#22c55e" };
      if (norm === "MARGINALLY STRONGER") return { bg: "#fef9c3", color: "#854d0e", border: "#eab308" };
      if (norm === "NO MEANINGFUL IMPROVEMENT") return { bg: "#fee2e2", color: "#b91c1c", border: "#ef4444" };
      return null;
    };
    const styles = StyleSheet.create({
      page: {
        paddingTop: 50,
        paddingBottom: 60,
        paddingLeft: 45,
        paddingRight: 45,
        fontSize: 11,
        fontFamily: "Helvetica",
        lineHeight: 1.5,
      },
      header: {
        marginBottom: 20,
        paddingBottom: 10,
        borderBottom: "2 solid #193CB9",
      },
      title: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#193CB9",
        marginBottom: 5,
      },
      subtitle: {
        fontSize: 12,
        color: "#666",
        marginBottom: 3,
      },
      section: {
        marginBottom: 25,
      },
      comparisonHeader: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#111827",
        marginBottom: 6,
      },
      comparisonSubheader: {
        fontSize: 11,
        color: "#6b7280",
        marginBottom: 10,
      },
      paragraph: {
        marginBottom: 8,
        textAlign: "justify",
      },
      disclaimer: {
        marginTop: 20,
        paddingTop: 10,
        borderTop: "1 solid #e5e7eb",
        fontSize: 9,
        color: "#6b7280",
      },
      footer: {
        position: "absolute",
        bottom: 25,
        left: 45,
        right: 45,
        textAlign: "center",
        fontSize: 9,
        color: "#666",
      },
    });

    return (
      <PDFDocument>
        <Page size="A4" style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.title}>Comparison Dashboard</Text>
            <Text style={styles.subtitle}>
              Generated: {new Date().toLocaleString()}
            </Text>
            <Text style={styles.subtitle}>
              Original Brief vs. Revised Brief
            </Text>
          </View>

          {comparisonText && comparisonText.trim().length > 0 && (
            <View style={styles.section}>
              {parseHtmlToPDFComponents(comparisonText, styles, {
                netAssessmentLabel,
                getBadgeColors,
              })}
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.disclaimer}>
              Disclaimer: This comparison is generated by AI based on the two
              debate runs. It is not legal advice, does not replace independent
              legal judgment, and should be reviewed by a licensed attorney
              before any reliance.
            </Text>
          </View>
        </Page>
      </PDFDocument>
    );
  };

  const normalizeDebateRounds = useCallback((rawRounds) => {
    if (Array.isArray(rawRounds)) {
      return rawRounds.filter((r) => r && typeof r === "object");
    }
    if (rawRounds && typeof rawRounds === "object") {
      return Object.values(rawRounds).filter((r) => r && typeof r === "object");
    }
    return [];
  }, []);

  // Helper function to convert debateData format to streamingRounds format for PDF
  const convertDebateDataToRounds = (debateData) => {
    if (!debateData || !debateData.rounds) return {};
    const roundsObj = {};
    const normalizedRounds = normalizeDebateRounds(debateData.rounds);
    normalizedRounds.forEach((round) => {
      const roundNum = round.round || round.roundNumber;
      if (roundNum) {
        roundsObj[roundNum] = {};
        // Convert arguments to the format expected by PDF component
        if (round.arguments) {
          Object.keys(round.arguments).forEach((role) => {
            const argument = round.arguments[role];
            if (argument) {
              if (typeof argument === "string") {
                roundsObj[roundNum][role] = { text: argument };
              } else if (typeof argument === "object" && argument !== null) {
                roundsObj[roundNum][role] = {
                  text: argument.text || "",
                  fullText: argument.fullText || argument.text || "",
                  model: argument.model || "Unknown",
                };
              }
            }
          });
        }
      }
    });
    return roundsObj;
  };

  const [downloadStreamingMenuOpen, setDownloadStreamingMenuOpen] =
    useState(false);
  const [downloadSummaryMenuOpen, setDownloadSummaryMenuOpen] = useState(false);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [executiveSummaryByChatId, setExecutiveSummaryByChatId] = useState({});
  const [showExecutiveSummary, setShowExecutiveSummary] = useState(false);
  const [executiveSummaryCollapsed, setExecutiveSummaryCollapsed] = useState(false);
  const executiveSummaryChatKey = currentChatId || chatId || "new";
  const executiveSummaryText =
    (executiveSummaryByChatId &&
      typeof executiveSummaryByChatId === "object" &&
      executiveSummaryByChatId[executiveSummaryChatKey]
      ? executiveSummaryByChatId[executiveSummaryChatKey]
      : "") || "";

  // Prevent showing the last chat's executive summary when switching chats.
  useEffect(() => {
    setShowExecutiveSummary(false);
    setExecutiveSummaryLoading(false);
  }, [executiveSummaryChatKey]);

  const getFinalConsensusSummaryText = useCallback(() => {
    const fromDebateData =
      (debateData?.consensusSummary || debateData?.finalSummary || "")
        ?.toString?.() || "";
    const cleanedFromDebateData = removeSeparatorLines(fromDebateData);
    if (cleanedFromDebateData && cleanedFromDebateData.trim()) {
      return cleanedFromDebateData.trim();
    }

    const fromStreaming = removeSeparatorLines(streamingConsensus || "");
    if (fromStreaming && fromStreaming.trim()) return fromStreaming.trim();

    // Fallback: try to locate the final consensus round text
    const rounds = Array.isArray(debateData?.rounds) ? debateData.rounds : [];
    for (let i = rounds.length - 1; i >= 0; i--) {
      const r = rounds[i];
      const t =
        typeof r?.arguments?.consensus?.text === "string"
          ? r.arguments.consensus.text
          : typeof r?.arguments?.consensus === "string"
            ? r.arguments.consensus
            : "";
      const cleaned = removeSeparatorLines(t || "");
      if (cleaned && cleaned.trim()) return cleaned.trim();
    }

    return "";
  }, [debateData, streamingConsensus]);

  const handleCreateExecutiveSummary = useCallback(async () => {
    try {
      if (isViewingRerunSnapshot) {
        return;
      }
      if (debateData?.stopped) {
        showError(
          "Executive Summary is unavailable for stopped debates. Start a new debate and run it to completion.",
        );
        return;
      }

      const finalConsensusSummaryText = getFinalConsensusSummaryText();
      const originalQuery =
        (debateData?.prompt || prompt || DEFAULT_DEBATE_PROMPT).toString();

      if (!finalConsensusSummaryText || !finalConsensusSummaryText.trim()) {
        showError(
          "No Final Consensus Summary found yet. Run the debate to completion first.",
        );
        return;
      }

      setExecutiveSummaryLoading(true);
      setExecutiveSummaryByChatId((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        [executiveSummaryChatKey]: "",
      }));
      setShowExecutiveSummary(true);

      let authToken = localStorage.getItem("token");
      if (!authToken) {
        try {
          const tokenResponse = await axios.get(`${API_URL}/auth/token`);
          authToken = tokenResponse.data?.token;
          if (authToken) {
            localStorage.setItem("token", authToken);
          }
        } catch (tokenError) {
          console.warn("Could not get token from backend:", tokenError);
        }
      }

      const headers = { "Content-Type": "application/json" };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const response = await fetch(
        `${API_URL}/ai/executive-summary-from-consensus-stream`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            finalConsensusSummaryText,
            originalQuery,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.message ||
          `Failed to generate executive summary (${response.status})`,
        );
      }

      if (!response.body || typeof response.body.getReader !== "function") {
        throw new Error(
          "Connection was interrupted while the app was in the background. Please try again.",
        );
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalSummaryText = "";

      const processEvent = (eventName, data) => {
        if (eventName === "chunk") {
          const fullText =
            typeof data?.fullText === "string" ? data.fullText : "";
          if (fullText) {
            finalSummaryText = fullText;
            setExecutiveSummaryByChatId((prev) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              [executiveSummaryChatKey]: fullText,
            }));
          }
          return;
        }
        if (eventName === "done") {
          const summary = typeof data?.summary === "string" ? data.summary : "";
          if (summary.trim()) {
            finalSummaryText = summary.trim();
            setExecutiveSummaryByChatId((prev) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              [executiveSummaryChatKey]: summary.trim(),
            }));
          }
          return;
        }
        if (eventName === "error") {
          throw new Error(data?.message || "Executive summary failed");
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const block of parts) {
          const dataLine = block
            .split("\n")
            .find((l) => l.startsWith("data: "));
          const eventLine = block
            .split("\n")
            .find((l) => l.startsWith("event: "));
          const eventName = eventLine ? eventLine.slice(7).trim() : "";
          if (dataLine) {
            try {
              const data = JSON.parse(dataLine.slice(6));
              processEvent(eventName, data);
            } catch {
              // ignore
            }
          }
        }
      }

      if (buffer.trim()) {
        const dataLine = buffer
          .split("\n")
          .find((l) => l.startsWith("data: "));
        const eventLine = buffer
          .split("\n")
          .find((l) => l.startsWith("event: "));
        const eventName = eventLine ? eventLine.slice(7).trim() : "";
        if (dataLine) {
          try {
            const data = JSON.parse(dataLine.slice(6));
            processEvent(eventName, data);
          } catch { }
        }
      }

      showSuccess("Executive Summary created.");

      // Persist executive summary to backend so it survives refresh
      if (finalSummaryText && executiveSummaryChatKey && executiveSummaryChatKey !== "new") {
        try {
          const token = localStorage.getItem("token");
          const saveHeaders = { "Content-Type": "application/json" };
          if (token) saveHeaders.Authorization = `Bearer ${token}`;
          await fetch(`${API_URL}/ai/chats/${executiveSummaryChatKey}`, {
            method: "PUT",
            headers: saveHeaders,
            credentials: "include",
            body: JSON.stringify({ debateExecutiveSummary: finalSummaryText }),
          });
        } catch (saveErr) {
          console.warn("Failed to persist executive summary:", saveErr);
        }
      }
    } catch (e) {
      console.error("Executive summary error:", e);
      showError(e?.message || "Failed to create Executive Summary");
      setShowExecutiveSummary(false);
    } finally {
      setExecutiveSummaryLoading(false);
    }
  }, [
    debateData,
    executiveSummaryChatKey,
    getFinalConsensusSummaryText,
    isViewingRerunSnapshot,
    prompt,
    showError,
    showSuccess,
  ]);

  const handleDownloadExecutiveSummaryPDF = useCallback(async () => {
    let wrapper = null;
    let style = null;
    try {
      const text = executiveSummaryText || "";
      if (!text.trim()) {
        showError("No Executive Summary available to download yet.");
        return;
      }

      const PDF_CONTENT_WIDTH_IN = 7.2;
      const PDF_CONTENT_WIDTH_PX = Math.ceil(PDF_CONTENT_WIDTH_IN * 96);

      wrapper = document.createElement("div");
      wrapper.style.cssText =
        `position: fixed; left: -9999px; top: 0; width: ${PDF_CONTENT_WIDTH_IN}in; max-width: ${PDF_CONTENT_WIDTH_IN}in; visibility: hidden; pointer-events: none; overflow: visible;`;
      const element = document.createElement("div");
      element.innerHTML = convertTextToHtml(text);
      element.style.cssText = `
        background: #ffffff !important;
        color: #111827 !important;
        padding: 0;
        margin: 0;
        width: ${PDF_CONTENT_WIDTH_IN}in;
        max-width: ${PDF_CONTENT_WIDTH_IN}in;
        font-family: 'Times New Roman', Times, serif;
        font-size: 12pt;
        line-height: 1.6;
        word-spacing: 0.05em;
        letter-spacing: 0.01em;
        overflow: visible;
      `;

      style = document.createElement("style");
      style.textContent = `
        #executive-summary-pdf * {
          box-sizing: border-box;
          max-width: 100%;
          overflow-wrap: anywhere;
          word-break: normal;
        }
        #executive-summary-pdf p {
          margin: 0.2em 0;
          line-height: 1.6;
          text-align: justify;
          text-justify: inter-word;
          page-break-inside: avoid;
        }
        #executive-summary-pdf h1,
        #executive-summary-pdf h2,
        #executive-summary-pdf h3,
        #executive-summary-pdf h4,
        #executive-summary-pdf h5,
        #executive-summary-pdf h6 {
          page-break-after: avoid;
          page-break-inside: avoid;
          margin: 0.4em 0 0.15em 0;
          line-height: 1.2;
        }
        #executive-summary-pdf ul,
        #executive-summary-pdf ol,
        #executive-summary-pdf li,
        #executive-summary-pdf blockquote,
        #executive-summary-pdf pre,
        #executive-summary-pdf table {
          page-break-inside: avoid;
        }
      `;
      document.head.appendChild(style);

      element.id = "executive-summary-pdf";
      wrapper.appendChild(element);
      document.body.appendChild(wrapper);

      // Ensure DOM/layout has settled before canvas capture.
      await new Promise((resolve) => setTimeout(resolve, 150));

      const dateStr = new Date().toISOString().split("T")[0];
      const baseTitle = (debateData?.prompt || prompt || "executive_summary")
        .toString()
        .substring(0, 30)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_");
      const filename = `executive_summary_${baseTitle || "lawfusion"}_${dateStr}.pdf`;

      const pdf = await html2pdf()
        .set({
          margin: [0.75, 0.6, 0.75, 0.6],
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            letterRendering: true,
            allowTaint: false,
            imageTimeout: 0,
            scrollY: 0,
            scrollX: 0,
            width: PDF_CONTENT_WIDTH_PX,
            windowWidth: PDF_CONTENT_WIDTH_PX,
          },
          jsPDF: {
            unit: "in",
            format: "letter",
            orientation: "portrait",
            compress: true,
            precision: 16,
          },
          pagebreak: {
            mode: ["css", "legacy"],
            avoid: ["h1", "h2", "h3", "h4", "h5", "h6", "li", "table", "pre"],
          },
        })
        .from(element)
        .toPdf()
        .get("pdf");
      const outBlob = pdf.output("blob");
      await saveBlobWithPlatformSupport(
        new Blob([outBlob], { type: "application/pdf" }),
        filename,
        "application/pdf",
      );

      showSuccess("Executive Summary PDF downloaded.");
    } catch (e) {
      console.error("Executive Summary PDF download failed:", e);
      showError("Failed to download Executive Summary PDF.");
    } finally {
      if (style && document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (wrapper && document.body.contains(wrapper)) {
        document.body.removeChild(wrapper);
      }
    }
  }, [
    debateData,
    executiveSummaryByChatId,
    executiveSummaryText,
    prompt,
    showError,
    showSuccess,
  ]);

  // Download Debate PDF Handler
  // If roundFilter is an array of round numbers, only those rounds are included; otherwise all rounds.
  // If consensusOnly is true, only the final consensus & summary are included in the PDF.
  const handleDownloadDebatePDF = async (
    roundFilter = null,
    consensusOnly = false,
  ) => {
    try {
      // Check if we have debateData (completed debate) or streamingRounds (active debate)
      let roundsToUse = {};
      let consensusToUse = "";
      let promptToUse = prompt || DEFAULT_DEBATE_PROMPT;
      let maxRoundsToUse = maxRounds || 5;

      if (debateData && debateData.rounds && debateData.rounds.length > 0) {
        // Use completed debate data
        const fullRounds = convertDebateDataToRounds(debateData);
        if (consensusOnly) {
          roundsToUse = {};
        } else if (Array.isArray(roundFilter) && roundFilter.length > 0) {
          const filtered = {};
          Object.entries(fullRounds).forEach(([roundNum, data]) => {
            const n = Number(roundNum);
            if (roundFilter.includes(n)) {
              filtered[roundNum] = data;
            }
          });
          roundsToUse = filtered;
        } else {
          roundsToUse = fullRounds;
        }
        // consensusSummary may be intentionally empty when the final summary lives
        // inside a summary round (to prevent duplication in the UI). In that case,
        // fall back to scanning the rounds array for the consensus text.
        const consensusFromTopLevel = debateData.consensusSummary || debateData.finalSummary || "";
        let consensusFromRound = "";
        if (!consensusFromTopLevel.trim()) {
          const sortedRounds = [...(debateData.rounds || [])].reverse();
          for (const r of sortedRounds) {
            const t =
              typeof r?.arguments?.consensus?.text === "string"
                ? r.arguments.consensus.text
                : typeof r?.arguments?.consensus === "string"
                  ? r.arguments.consensus
                  : "";
            if (t.trim()) { consensusFromRound = t; break; }
          }
        }
        consensusToUse = removeSeparatorLines(consensusFromTopLevel || consensusFromRound);
        promptToUse = debateData.prompt || prompt || DEFAULT_DEBATE_PROMPT;
        maxRoundsToUse = debateData.maxRounds || maxRounds || 5;
      } else {
        // Use streaming rounds data
        if (consensusOnly) {
          roundsToUse = {};
        } else if (Array.isArray(roundFilter) && roundFilter.length > 0) {
          const filtered = {};
          Object.entries(streamingRounds).forEach(([roundNum, data]) => {
            const n = Number(roundNum);
            if (roundFilter.includes(n)) {
              filtered[roundNum] = data;
            }
          });
          roundsToUse = filtered;
        } else {
          roundsToUse = streamingRounds;
        }
        consensusToUse = removeSeparatorLines(streamingConsensus);
      }

      const hasRounds = Object.keys(roundsToUse).length > 0;
      const hasConsensus = consensusToUse && consensusToUse.trim().length > 0;

      if (!hasRounds && !hasConsensus) {
        showError(
          "No debate data available to download. Please run a debate first.",
        );
        return;
      }

      if (consensusOnly && !hasConsensus) {
        showError(
          "No consensus summary available to download. Please run a debate first.",
        );
        return;
      }

      showInfo("Generating PDF... This may take a moment.");

      // Create the PDF document
      const doc = (
        <DebatePDFDocument
          prompt={promptToUse}
          rounds={roundsToUse}
          consensus={consensusToUse}
          maxRounds={maxRoundsToUse}
          comparison=""
        />
      );

      const blob = await pdf(doc).toBlob();

      const dateStr = new Date().toISOString().split("T")[0];
      const promptPreview = promptToUse
        ? promptToUse
          .substring(0, 30)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
        : "debate";
      const filename = consensusOnly
        ? `debate_consensus_summary_${promptPreview}_${dateStr}.pdf`
        : `debate_${promptPreview}_${dateStr}.pdf`;
      await saveBlobWithPlatformSupport(blob, filename, "application/pdf");

      showSuccess("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showError("Failed to generate PDF. Please try again.");
    }
  };

  const handleEditorSave = () => {
    // Use HTML content directly to preserve formatting
    const htmlContent = editorContent;
    const plainText = extractPlainText(htmlContent);

    if (
      editorMeta.type === "debate-round" &&
      editorMeta.roundIndex !== null &&
      editorMeta.role
    ) {
      // Update debate round argument
      setDebateData((prev) => {
        if (!prev || !prev.rounds) return prev;
        const newRounds = [...prev.rounds];
        const round = newRounds[editorMeta.roundIndex];
        if (round && round.arguments) {
          const newArguments = { ...round.arguments };
          if (typeof newArguments[editorMeta.role] === "string") {
            newArguments[editorMeta.role] = htmlContent;
          } else if (
            typeof newArguments[editorMeta.role] === "object" &&
            newArguments[editorMeta.role] !== null
          ) {
            newArguments[editorMeta.role] = {
              ...newArguments[editorMeta.role],
              text: htmlContent, // Store HTML in text field since it's rendered via convertTextToHtml
            };
          } else {
            newArguments[editorMeta.role] = htmlContent;
          }
          newRounds[editorMeta.roundIndex] = {
            ...round,
            arguments: newArguments,
          };
        }
        return { ...prev, rounds: newRounds };
      });
    } else if (editorMeta.type === "debate-consensus") {
      // Update consensus summary - keep in sync with whichever storage the
      // current debate uses (a summary round OR top-level fields) so we never
      // render two "Final Consensus & Summary" blocks simultaneously.
      setDebateData((prev) => {
        if (!prev) return prev;

        // Sync edits to the existing summary round if one exists in the rounds array.
        // A summary round is identified by being beyond maxRounds OR by having consensus but no advocate.
        const summaryRoundIndex = (prev.rounds || []).findIndex(
          (r) =>
            (prev.maxRounds > 0 && r.round > prev.maxRounds) ||
            (!!r.arguments?.consensus && !r.arguments?.advocate),
        );

        if (summaryRoundIndex !== -1) {
          const updatedRounds = prev.rounds.map((r, i) => {
            if (i !== summaryRoundIndex) return r;
            const prevConsensus = r.arguments?.consensus;
            return {
              ...r,
              arguments: {
                ...r.arguments,
                consensus:
                  prevConsensus && typeof prevConsensus === "object"
                    ? { ...prevConsensus, text: htmlContent }
                    : htmlContent,
              },
            };
          });

          // CRITICAL: If we found a summary round, we must NOT have content in top-level
          // fields that would trigger a standalone DebateSummary component to render,
          // as that would cause duplication in the UI.
          return {
            ...prev,
            rounds: updatedRounds,
            consensusSummary: "",
            finalSummary: "",
          };
        }

        // Fallback: No summary round exists in the array, update top-level fields only.
        return {
          ...prev,
          consensusSummary: htmlContent,
          finalSummary: htmlContent,
        };
      });
    }
    setEditorOpen(false);
  };

  const handleDownloadPDF = () =>
    exportEditorHtmlToPdf(editorContent, editorTitle);

  const handleDownloadDoc = () =>
    exportEditorHtmlToDocx(editorContent, editorTitle, showError);

  // Auto-scroll for streaming debate rounds - enhanced with MutationObserver
  useEffect(() => {
    if (!debating || Object.keys(streamingRounds).length === 0) return;

    // Function to scroll to bottom (only if user hasn't scrolled up)
    const scrollToBottom = (el, refKey) => {
      if (!el) return;
      const scrollableEl = getScrollableElement(el);
      if (!scrollableEl) return;

      // Keep following streamed content unless user explicitly scrolled up.
      // The previous near-bottom gate could block auto-scroll on larger chunks,
      // leaving some cards (often 1st/3rd slots) stuck behind latest text.
      if (userScrolledRefs.current[refKey]) {
        return;
      }

      scrollableEl.scrollTop = scrollableEl.scrollHeight;
      lastScrollTopRefs.current[refKey] = scrollableEl.scrollTop;
    };

    // Role to slot mapping
    const roleSlots = ["advocate", "critic", "strategist", "consensus"];
    const observers = [];

    // Set up MutationObserver for each streaming response
    Object.keys(streamingRounds).forEach((roundNumber) => {
      roleSlots.forEach((role) => {
        const refKey = `${roundNumber}-${role}`;
        const el = streamingResponseRefs.current[refKey];
        if (!el) return;

        const scrollableEl = getScrollableElement(el);
        if (!scrollableEl) return;

        // Watch the inner content div (where dangerouslySetInnerHTML updates)
        const innerDiv = scrollableEl.querySelector("div");
        const targetToObserve = innerDiv || scrollableEl;

        // Set up scroll tracking for this element
        const handleScroll = () => {
          if (!scrollableEl) return;

          const currentScrollTop = scrollableEl.scrollTop;
          const isAtBottom = isNearBottom(scrollableEl);

          // If user scrolled up (not at bottom), mark as user-interacted
          if (
            !isAtBottom &&
            currentScrollTop < (lastScrollTopRefs.current[refKey] || 0)
          ) {
            userScrolledRefs.current[refKey] = true;
          } else if (isAtBottom) {
            // If user scrolls back to bottom, reset the flag
            userScrolledRefs.current[refKey] = false;
          }

          lastScrollTopRefs.current[refKey] = currentScrollTop;
        };

        scrollableEl.addEventListener("scroll", handleScroll, {
          passive: true,
        });

        // Create observer to watch for content changes
        const observer = new MutationObserver(() => {
          // Scroll immediately when content changes (only if user hasn't scrolled up)
          scrollToBottom(scrollableEl, refKey);
          requestAnimationFrame(() => {
            scrollToBottom(scrollableEl, refKey);
            // Double-check after microtask
            Promise.resolve().then(() => {
              scrollToBottom(scrollableEl, refKey);
            });
          });
        });

        // Observe the inner content div (where dangerouslySetInnerHTML updates)
        if (targetToObserve) {
          observer.observe(targetToObserve, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        // Also observe the container itself for structural changes
        if (targetToObserve !== scrollableEl) {
          observer.observe(scrollableEl, {
            childList: true,
          });
        }

        observers.push({
          observer,
          refKey,
          scrollHandler: handleScroll,
          element: scrollableEl,
        });
      });
    });

    // Use interval as backup during streaming
    const scrollInterval = setInterval(() => {
      Object.keys(streamingRounds).forEach((roundNumber) => {
        roleSlots.forEach((role) => {
          const refKey = `${roundNumber}-${role}`;
          const el = streamingResponseRefs.current[refKey];
          scrollToBottom(el, refKey);
        });
      });
    }, 50); // Check every 50ms during streaming

    // Cleanup
    return () => {
      observers.forEach(({ observer, scrollHandler, element }) => {
        observer.disconnect();
        // Clean up scroll listeners
        if (element && scrollHandler) {
          element.removeEventListener("scroll", scrollHandler);
        }
      });
      clearInterval(scrollInterval);
    };
  }, [streamingRounds, debating]);

  // Immediate scroll on streaming rounds changes
  useEffect(() => {
    if (!debating || Object.keys(streamingRounds).length === 0) return;

    const roleSlots = ["advocate", "critic", "strategist", "consensus"];

    const scrollAllToBottom = () => {
      Object.keys(streamingRounds).forEach((roundNumber) => {
        roleSlots.forEach((role) => {
          const refKey = `${roundNumber}-${role}`;
          const el = streamingResponseRefs.current[refKey];
          if (el) {
            const scrollableEl = getScrollableElement(el);
            if (scrollableEl && !userScrolledRefs.current[refKey]) {
              scrollableEl.scrollTop = scrollableEl.scrollHeight;
              lastScrollTopRefs.current[refKey] = scrollableEl.scrollTop;
            }
          }
        });
      });
    };

    // Scroll immediately when rounds change
    scrollAllToBottom();

    // Scroll after DOM updates with multiple timing strategies
    requestAnimationFrame(() => {
      scrollAllToBottom();
      setTimeout(() => {
        scrollAllToBottom();
      }, 0);
      setTimeout(() => {
        scrollAllToBottom();
      }, 10);
    });
  }, [streamingRounds, debating]);

  // Set up scroll tracking for consensus
  useEffect(() => {
    if (!streamingConsensusRef.current) return;

    const scrollableEl = streamingConsensusRef.current;
    if (!scrollableEl) return;

    const handleScroll = () => {
      if (!scrollableEl) return;

      const currentScrollTop = scrollableEl.scrollTop;
      const isAtBottom = isNearBottom(scrollableEl);

      // If user scrolled up (not at bottom), mark as user-interacted
      if (
        !isAtBottom &&
        currentScrollTop < (lastScrollTopRefs.current["consensus"] || 0)
      ) {
        userScrolledRefs.current["consensus"] = true;
      } else if (isAtBottom) {
        // If user scrolls back to bottom, reset the flag
        userScrolledRefs.current["consensus"] = false;
      }

      lastScrollTopRefs.current["consensus"] = currentScrollTop;
    };

    scrollableEl.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollableEl.removeEventListener("scroll", handleScroll);
    };
  }, [streamingConsensusRef.current]);

  // Auto-scroll for streaming consensus summary - enhanced with MutationObserver
  useEffect(() => {
    if (!debating || !streamingConsensus || !streamingConsensusRef.current)
      return;

    const scrollableEl = streamingConsensusRef.current;

    // Function to scroll to bottom (only if user hasn't scrolled up)
    const scrollToBottom = () => {
      if (scrollableEl) {
        // Keep following consensus stream unless user explicitly scrolled up.
        if (userScrolledRefs.current["consensus"]) {
          return;
        }
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        lastScrollTopRefs.current["consensus"] = scrollableEl.scrollTop;
      }
    };

    // Scroll immediately when consensus starts
    scrollToBottom();

    // Watch the inner content div (where dangerouslySetInnerHTML updates)
    const innerDiv = scrollableEl.querySelector("div");
    const targetToObserve = innerDiv || scrollableEl;

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // Scroll immediately when content changes
      scrollToBottom();
      requestAnimationFrame(() => {
        scrollToBottom();
        // Double-check after microtask
        Promise.resolve().then(() => {
          scrollToBottom();
        });
      });
    });

    // Observe the inner content div (where dangerouslySetInnerHTML updates)
    if (targetToObserve) {
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Also observe the container itself for structural changes
    if (targetToObserve !== scrollableEl) {
      observer.observe(scrollableEl, {
        childList: true,
      });
    }

    // Use interval as backup during streaming
    const scrollInterval = setInterval(() => {
      scrollToBottom();
    }, 50); // Check every 50ms during streaming

    return () => {
      observer.disconnect();
      clearInterval(scrollInterval);
    };
  }, [streamingConsensus, debating]);

  // Immediate scroll on consensus changes
  useEffect(() => {
    if (!debating || !streamingConsensus || !streamingConsensusRef.current)
      return;

    const scrollableEl = streamingConsensusRef.current;

    const scrollToBottom = () => {
      // Keep following consensus stream unless user explicitly scrolled up.
      if (userScrolledRefs.current["consensus"]) {
        return;
      }
      scrollableEl.scrollTop = scrollableEl.scrollHeight;
      lastScrollTopRefs.current["consensus"] = scrollableEl.scrollTop;
    };

    // Scroll immediately when consensus changes
    scrollToBottom();

    // Scroll after DOM updates with multiple timing strategies
    requestAnimationFrame(() => {
      scrollToBottom();
      setTimeout(() => {
        scrollToBottom();
      }, 0);
      setTimeout(() => {
        scrollToBottom();
      }, 10);
    });
  }, [streamingConsensus, debating]);

  // File upload handler with retry logic
  // handleFileUpload is now imported from hook
  // const handleFileUpload = ... (removed)
  const handleFileRemove = (setFileState) => {
    setFileState(null);
  };

  // Handler to show pre-run confirmation dialog
  const handleStartDebateClick = () => {
    // When query is locked after stop, allow run only if user added/changed documents (re-run with revised doc)
    const allowedReRunWhenLocked =
      queryLockedAfterStop &&
      !!originalDebateFilesSnapshot?.length &&
      isComparisonReRun;
    if (queryLockedAfterStop && !allowedReRunWhenLocked) {
      showError(
        "This debate was stopped. Please click 'New Debate' to start a fresh debate.",
      );
      return;
    }

    // Validate that there's a prompt
    const trimmedPrompt = prompt ? prompt.trim() : "";
    if (!trimmedPrompt) {
      showError("Please enter a prompt before starting the debate.");
      return;
    }

    // UI-level validation for random strings / special characters
    const lettersMatch = trimmedPrompt.match(/\p{L}/gu);
    if (!lettersMatch || lettersMatch.length < 5) {
      showError("Please Enter Valid Query");
      return;
    }

    // Require either a document or user-modified query (not just the pre-filled example)
    const currentDebateFiles = getCurrentChatFiles().debateFiles || [];
    const hasDocument = currentDebateFiles.length > 0;
    const queryModified = prompt.trim() !== DEFAULT_DEBATE_PROMPT;
    if (!hasDocument && !queryModified) {
      showError(
        "Please upload a document or enter your legal query before starting a debate.",
      );
      return;
    }

    // Show confirmation dialog
    setShowPreRunConfirm(true);
  };

  const handleConfirmStartDebate = () => {
    setShowPreRunConfirm(false);
    startDebate();
  };

  // Handler to cancel debate start
  const handleCancelStartDebate = () => {
    setShowPreRunConfirm(false);
  };

  // Require document upload or user-modified query to enable Start AI Debate
  const currentDebateFiles = getCurrentChatFiles().debateFiles || [];
  const hasDocument = currentDebateFiles.length > 0;
  const queryModified = (prompt || "").trim() !== DEFAULT_DEBATE_PROMPT;
  const protectedFileKeys = useMemo(() => {
    if (!originalDebateFilesSnapshot) return [];
    return originalDebateFilesSnapshot
      .map((f) => f.s3Key || f.documentId)
      .filter(Boolean);
  }, [originalDebateFilesSnapshot]);
  const lettersMatch = (prompt || "").match(/\p{L}/gu);
  const isInputInvalid = !lettersMatch || lettersMatch.length < 5;

  // Query-only results: disable Start. Doc-backed results: enable Start only in re-run mode after revised doc uploaded.
  const canStartDebate =
    onResultsScreen && !originalDebateHadDocument
      ? false
      : onResultsScreen && originalDebateHadDocument
        ? queryLockedForComparison && isComparisonReRun && !!prompt?.trim() && !isInputInvalid
        : (hasDocument || queryModified) && !!prompt?.trim() && !isInputInvalid;

  const startTooltipMessage = isInputInvalid && (hasDocument || queryModified) && !!prompt?.trim()
    ? "Please Enter Valid Query"
    : "Please upload a document or enter a legal query before starting a debate.";

  const hideStartTooltip = useCallback(() => {
    if (mobileTooltipTimeoutRef.current) {
      clearTimeout(mobileTooltipTimeoutRef.current);
      mobileTooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  }, []);

  const showStartTooltip = useCallback(() => {
    if (mobileTooltipTimeoutRef.current) {
      clearTimeout(mobileTooltipTimeoutRef.current);
      mobileTooltipTimeoutRef.current = null;
    }

    setShowTooltip(true);

    if (isMobileScreen) {
      mobileTooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
        mobileTooltipTimeoutRef.current = null;
      }, 2500);
    }
  }, [isMobileScreen]);

  useEffect(() => () => {
    if (mobileTooltipTimeoutRef.current) {
      clearTimeout(mobileTooltipTimeoutRef.current);
    }
  }, []);

  const stopDebate = async () => {
    await releaseDebateWakeLock();
    debateStoppedRef.current = true;
    let savedPartialChatId = currentChatId || null;

    // Tell backend to stop this debate immediately (works even behind proxy when connection events don't fire)
    const opId = operationIdRef.current;
    if (opId) {
      try {
        const token = localStorage.getItem("token");
        await fetch(`${API_URL}/ai/debate-stop`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: "include",
          body: JSON.stringify({ operationId: opId }),
        });
      } catch (e) {
        console.warn("[STOP DEBATE] debate-stop request failed:", e);
      }
    }

    // Abort the fetch so the server may also see client disconnect
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
        console.log("[STOP DEBATE] Aborted fetch request");
      } catch (error) {
        console.error("[STOP DEBATE] Error aborting fetch:", error);
      }
      abortControllerRef.current = null;
    }

    // Cancel the stream reader if it exists
    if (streamReaderRef.current) {
      try {
        await streamReaderRef.current.cancel();
      } catch (error) {
        console.error("[STOP DEBATE] Error cancelling stream:", error);
      }
      streamReaderRef.current = null;
    }

    // Save the partial debate state
    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const partialRounds = [];
      Object.keys(streamingRounds).forEach((roundNum) => {
        const roundData = streamingRounds[roundNum];
        const roundArguments = {};

        ["advocate", "critic", "strategist"].forEach((role) => {
          if (roundData[role]) {
            roundArguments[role] = {
              text: roundData[role].fullText || roundData[role].text || "",
              model:
                roundData[role].model ||
                (role === "strategist"
                  ? "Gemini"
                  : role === "advocate"
                    ? "ChatGPT"
                    : "Claude"),
            };
          }
        });

        if (Object.keys(roundArguments).length > 0) {
          partialRounds.push({
            round: parseInt(roundNum),
            arguments: roundArguments,
          });
        }
      });

      // Prepare attachedFiles object using the latest debateFiles for this chat
      const currentFiles = getCurrentChatFiles().debateFiles || [];
      const attachedFilesForSave =
        Array.isArray(currentFiles) && currentFiles.length > 0
          ? {
            debateFiles: currentFiles.map((f) => ({
              fileName: f.fileName,
              s3Key: f.s3Key,
              fileUrl: f.fileUrl,
              fileText: f.fileText,
              fileType: f.fileType,
            })),
          }
          : {};

      const saveData = {
        prompt: currentDebateDataRef.current.prompt,
        maxRounds: currentDebateDataRef.current.maxRounds,
        debateRounds: partialRounds,
        finalSummary:
          streamingConsensus ||
          "**[Debate Stopped]**\n\nThis debate was stopped by the user before completion.",
        chatId: currentChatId || null,
        stopped: true,
        stoppedAtRound: round,
        attachedFiles:
          Object.keys(attachedFilesForSave).length > 0
            ? attachedFilesForSave
            : undefined,
        title:
          currentDebateDataRef.current.title ||
          prompt.trim().substring(0, 50) ||
          "Untitled Debate",
      };

      const response = await fetch(`${API_URL}/ai/save-partial-debate`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify(saveData),
      });

      if (response.ok) {
        const result = await response.json();
        savedPartialChatId = result.chatId || savedPartialChatId;

        const normalizedPartialRounds = normalizeDebateRounds(partialRounds);
        const hasSummaryRoundInPartial = normalizedPartialRounds.some(
          (r) =>
            (currentDebateDataRef.current.maxRounds > 0 && r.round > currentDebateDataRef.current.maxRounds) ||
            (r.arguments?.consensus && !r.arguments?.advocate),
        );
        // Update UI with the stopped state
        setDebateData({
          rounds: normalizedPartialRounds,
          consensusSummary: hasSummaryRoundInPartial
            ? ""
            : (streamingConsensus ||
              "**[Debate Stopped]**\n\nThis debate was stopped by the user before completion."),
          maxRounds: currentDebateDataRef.current.maxRounds,
          stopped: true,
          stoppedAtRound: round,
          debateError: {
            type: "user_stop",
            message:
              "You stopped this debate before it finished. Partial rounds are saved below.",
          },
        });

        // Navigate to the saved chat if we got a chatId
        if (result.chatId && !currentChatId) {
          setCurrentChatId(result.chatId);
          navigate(`/debate-mode/${result.chatId}`, { replace: true });
        }
      } else {
        console.error(
          "[STOP DEBATE] Failed to save partial debate:",
          response.statusText,
        );
      }
    } catch (error) {
      console.error("[STOP DEBATE] Error saving partial debate:", error);
    }

    setDebating(false);
    isStreamingDebateRef.current = false; // Clear streaming flag
    operationIdRef.current = null;
    setQueryLockedAfterStop(true);
    if (savedPartialChatId) {
      setNewChatForSidebar({
        _id: savedPartialChatId,
        chatId: savedPartialChatId,
        sidebarEvent: "status-update",
        chatStatus: "stopped",
      });
    }

    // Clear the newChatForSidebar to prevent duplicate sidebar entries
    setTimeout(() => setNewChatForSidebar(null), 0);

    // DON'T clear currentChatId here - we need it to stay on the stopped debate's URL
    // The user should see the stopped debate, and when they click "New Debate",
    // resetDebate() will clear it and navigate to /debate-mode
    console.log(
      "[STOP DEBATE] Query locked, user must click 'New Debate' to continue",
    );
  };

  const buildPartialDebateState = useCallback(
    (failureMessage) => {
      const partialRounds = [];

      Object.keys(streamingRounds || {}).forEach((roundNum) => {
        const roundData = streamingRounds[roundNum];
        const roundArguments = {};

        ["advocate", "critic", "strategist", "consensus"].forEach((role) => {
          if (!roundData?.[role]) return;
          const text =
            roundData[role].fullText || roundData[role].text || "";
          if (!text.trim()) return;
          roundArguments[role] = {
            text,
            model: roundData[role].model || "Unknown",
            role,
          };
        });

        if (Object.keys(roundArguments).length > 0) {
          partialRounds.push({
            round: parseInt(roundNum, 10),
            arguments: roundArguments,
          });
        }
      });

      const normalizedPartialRounds = normalizeDebateRounds(partialRounds);
      const hasSummaryRoundInPartial = normalizedPartialRounds.some(
        (r) =>
          (maxRounds > 0 && r.round > maxRounds) ||
          (r.arguments?.consensus && !r.arguments?.advocate),
      );

      return {
        rounds: normalizedPartialRounds,
        consensusSummary: hasSummaryRoundInPartial ? "" : streamingConsensus || "",
        maxRounds: maxRounds || 5,
        stopped: true,
        stoppedAtRound:
          normalizedPartialRounds.length > 0
            ? normalizedPartialRounds[normalizedPartialRounds.length - 1].round
            : Math.max(round - 1, 0),
        debateError: failureMessage
          ? {
            message: failureMessage,
            type: "stream_failure",
          }
          : null,
      };
    },
    [maxRounds, normalizeDebateRounds, round, streamingConsensus, streamingRounds],
  );

  const startDebate = async () => {
    const allowedReRunWhenLocked =
      queryLockedAfterStop &&
      !!originalDebateFilesSnapshot?.length &&
      isComparisonReRun;
    if (
      !prompt ||
      debating ||
      (queryLockedAfterStop && !allowedReRunWhenLocked)
    ) {
      console.log("[START DEBATE] Blocked:", {
        prompt: !!prompt,
        debating,
        queryLockedAfterStop,
        allowedReRunWhenLocked,
      });
      return;
    }

    // Once a re-run is started on a document-backed debate, lock the query so it can't be edited.
    // For the very first run (no results yet), keep the query editable.
    if (onResultsScreen && originalDebateHadDocument) {
      setQueryLockedForComparison(true);
    }

    // Reset the stopped flag
    debateStoppedRef.current = false;

    // Create new AbortController for this debate
    abortControllerRef.current = new AbortController();

    // Generate unique operationId for this button press to group all related actions
    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    operationIdRef.current = operationId;
    streamingDebateChatIdRef.current = null;

    // Capture before clearing: only auto-run comparison after 2nd+ run on the SAME chat (current vs first).
    // Require currentChatId === originalDebateChatId so we never trigger comparison on first run or when
    // starting a new debate from another chat (stale state would otherwise set didSendRerun true).
    const hadExistingRoundsBeforeRun = !!debateData?.rounds?.length;
    // Compare chatIds — they may differ in format (16-char vs full), so compare suffixes
    const isSameChatAsOriginal = !!(
      currentChatId &&
      originalDebateChatId &&
      (currentChatId === originalDebateChatId ||
        currentChatId.endsWith(originalDebateChatId) ||
        originalDebateChatId.endsWith(currentChatId))
    );
    const didSendRerun =
      !!originalDebateChatId &&
      hadExistingRoundsBeforeRun &&
      (isSameChatAsOriginal || (onResultsScreen && originalDebateHadDocument && queryLockedForComparison));
    console.log("[DEBATE] Re-run check:", { currentChatId, originalDebateChatId, hadExistingRoundsBeforeRun, isSameChatAsOriginal, didSendRerun, onResultsScreen, originalDebateHadDocument, queryLockedForComparison });

    // Preserve original debate data so we can restore it if the rerun fails
    const previousDebateData = didSendRerun ? debateData : null;

    // Guests: show limit modal immediately if server says debate cap reached (avoids
    // title-gen + fake "debating" UI before 403 on debate-stream).
    if (!isLoggedIn) {
      try {
        const usageRes = await fetch(`${API_URL}/ai/anon-usage`, {
          credentials: "include",
        });
        if (usageRes.ok) {
          const u = await usageRes.json();
          if (u.guest && (u.blocked || u.debateExceeded)) {
            setShowAnonSignupModal(true);
            return;
          }
        }
      } catch (e) {
        // Fail open — debate-stream still enforces limits
      }
    }

    // Logged-in users: block immediately when monthly AI actions are exhausted.
    // This prevents starting a debate only to hit the limit mid-stream.
    if (isLoggedIn) {
      try {
        const token = localStorage.getItem("token");
        const usageRes = await fetch(`${API_URL}/ai/usage`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (usageRes.ok) {
          const u = await usageRes.json();
          const used = Number(u.aiActionsUsed ?? u.actionsUsed ?? 0) || 0;
          const limit = Number(u.aiMonthlyLimit ?? u.actionLimit ?? 0) || 0;
          const isUnlimited = limit >= 1000000 || limit === Infinity;
          if (!isUnlimited && limit > 0 && used >= limit) {
            showWarning(
              `AI action limit reached. You have used ${used} of ${limit} actions. Please upgrade your plan to continue.`,
              "Limit Reached",
            );
            navigate("/pricing");
            return;
          }
        }
      } catch (e) {
        // Fail open — debate-stream still enforces limits
      }
    }

    // Frontend preflight: if a document is attached and extracted text is present but empty,
    // show toast immediately (similar to anon-limit UX).
    const preflightFiles = getCurrentChatFiles().debateFiles || [];
    if (Array.isArray(preflightFiles) && preflightFiles.length > 0) {
      const anyHasTextField = preflightFiles.some((f) => "fileText" in (f || {}));
      if (anyHasTextField) {
        const combined = preflightFiles
          .map((f) => (typeof f?.fileText === "string" ? f.fileText.trim() : ""))
          .join("\n\n");
        if (!hasUsableDocumentText(combined)) {
          showError("Attached document is empty", "Empty Document");
          return;
        }
      }
    }

    setDebating(true);
    isStreamingDebateRef.current = true; // Mark that we're streaming
    setRound(0);
    setDebateData(null);
    setError(null);
    setStreamingRounds({});
    setStreamingConsensus("");

    const syncDebateStateFromLoadedChat = (loaded) => {
      const cid = streamingDebateChatIdRef.current;
      if (!loaded || !cid) return false;
      const snap = pickRecoverySnapshotForStreamResume(loaded, didSendRerun);
      if (
        !snap ||
        !Array.isArray(snap.debateRounds) ||
        snap.debateRounds.length === 0
      ) {
        return false;
      }

      const maxR = snap.maxRounds || maxRounds || 5;
      const normalized = normalizeDebateRounds(snap.debateRounds);
      const hasSummaryRoundInSnap = normalized.some(
        (r) =>
          (maxR > 0 && r.round > maxR) ||
          (r.arguments?.consensus && !r.arguments?.advocate),
      );
      const persistenceComplete = isServerDebateSnapshotComplete(
        snap,
        maxRounds,
      );

      if (!snap.stopped && persistenceComplete) {
        setDebateData({
          rounds: normalized,
          consensusSummary: hasSummaryRoundInSnap
            ? ""
            : removeSeparatorLines(removeCitationMarkers(snap.finalSummary || "")),
          maxRounds: maxR,
          stopped: false,
          stoppedAtRound: 0,
          debateError: null,
        });
        setQueryLockedAfterStop(false);
        setError(null);
        setDebating(false);
        setRound(maxR);
        if (normalized.length > 0) {
          const summaryRoundIndex = normalized.findIndex(
            (r) =>
              (maxR > 0 && r.round > maxR) ||
              (r.arguments?.consensus && !r.arguments?.advocate),
          );
          const expanded = { 0: true };
          if (summaryRoundIndex > -1) expanded[summaryRoundIndex] = true;
          setExpandedDebateRounds(expanded);
        }
        setTimeout(() => {
          setStreamingRounds({});
          setStreamingConsensus("");
        }, 500);
        setNewChatForSidebar({
          _id: cid,
          chatId: cid,
          sidebarEvent: "status-update",
          chatStatus: "done",
        });

        if (didSendRerun) {
          setHasRerunOnSameChat(true);
          setDebateRerunCount((prev) => prev + 1);
        }

        if (
          didSendRerun &&
          originalDebateChatId &&
          originalDebateHadDocument
        ) {
          const rerunChatId = originalDebateChatId;
          setTimeout(() => {
            runDebateComparison(rerunChatId, rerunChatId)
              .then(() => {
                setShowComparisonDashboard(true);
              })
              .catch(() => { });
          }, 800);
        }
        return true;
      }

      if (!snap.stopped) {
        return false;
      }

      const serverErr = snap.debateError;
      const fallbackHint = formatClassifiedDebateFailure(
        classifyDebateStreamFailure(new Error("interrupted")),
      );
      const bannerMessage =
        (serverErr && String(serverErr.message || "").trim()) || fallbackHint;

      setDebateData({
        rounds: normalized,
        consensusSummary: hasSummaryRoundInSnap
          ? ""
          : removeSeparatorLines(removeCitationMarkers(snap.finalSummary || "")),
        maxRounds: maxR,
        stopped: true,
        stoppedAtRound: snap.stoppedAtRound,
        debateError:
          serverErr ||
          (bannerMessage
            ? { message: bannerMessage, type: "stream_failure" }
            : null),
      });
      setQueryLockedAfterStop(true);
      setError(serverErr?.type === "user_stop" ? null : bannerMessage);
      setDebating(false);
      setRound(0);
      setStreamingRounds({});
      setStreamingConsensus("");
      setNewChatForSidebar({
        _id: cid,
        chatId: cid,
        sidebarEvent: "status-update",
        chatStatus: serverErr?.type === "user_stop" ? "stopped" : "done",
      });
      return true;
    };

    const tryRecoverServerSnapshot = async () => {
      const cid = streamingDebateChatIdRef.current;
      if (!isLoggedIn || !cid) return false;
      try {
        const token = localStorage.getItem("token");
        if (!token) return false;
        const response = await axios.get(
          `${API_URL}/ai/chats/${encodeURIComponent(cid)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          },
        );
        return syncDebateStateFromLoadedChat(response.data?.chat);
      } catch (e) {
        console.warn("[DEBATE] Could not resume from saved chat:", e);
        return false;
      }
    };

    const pollDebateUntilSyncedFromServer = async () => {
      const cid = streamingDebateChatIdRef.current;
      if (!isLoggedIn || !cid) return false;
      const deadline = Date.now() + 30 * 60 * 1000;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      while (Date.now() < deadline) {
        if (debateStoppedRef.current) return false;
        const ok = await tryRecoverServerSnapshot();
        if (ok) return true;
        await new Promise((resolve) => setTimeout(resolve, 2600));
      }
      return false;
    };

    try {
      // Get token from backend if not available
      let authToken = localStorage.getItem("token");
      if (!authToken) {
        try {
          const tokenResponse = await axios.get(`${API_URL}/auth/token`);
          authToken = tokenResponse.data.token;
          if (authToken) {
            localStorage.setItem("token", authToken);
          }
        } catch (tokenError) {
          console.warn("Could not get token from backend:", tokenError);
        }
      }

      // Generate AI title BEFORE starting debate
      let debateTitle = "Generating title..."; // Default fallback
      try {
        const titleResponse = await fetch(`${API_URL}/ai/generate-title`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
          credentials: "include",
          body: JSON.stringify({
            query: prompt.trim(),
          }),
        });

        if (titleResponse.ok) {
          const titleData = await titleResponse.json();
          if (titleData.success && titleData.title) {
            debateTitle = titleData.title;
            console.log("[Debate Title Generated]", debateTitle);
          }
        }
      } catch (error) {
        console.warn("Title generation failed, using fallback:", error.message);
        // Continue with fallback title
      }

      // Use streaming endpoint for real-time responses
      const headers = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      // Prepare attachedFiles object supporting arrays (always full list for DB / UI).
      const currentFiles = getCurrentChatFiles().debateFiles || [];

      const origKeysSnapshot = new Set(
        (originalDebateFilesSnapshot || [])
          .map((f) => f.s3Key || f.documentId)
          .filter(Boolean),
      );
      const rerunOnlyFiles =
        didSendRerun && originalDebateFilesSnapshot?.length
          ? currentFiles.filter(
            (f) =>
              !origKeysSnapshot.has(f.s3Key) &&
              !origKeysSnapshot.has(f.documentId),
          )
          : [];

      const fileIds =
        currentFiles.length > 0
          ? {
            debateFiles: currentFiles.map((f) => f.fileId).filter(Boolean),
          }
          : {};

      // We send 'debateFiles' array. If needed for backward compat, we can also set 'debate'
      // but better to just send the new structure.
      const attachedFiles =
        currentFiles.length > 0
          ? {
            debateFiles: currentFiles.map((f) => ({
              fileId: f.fileId,
              fileName: f.fileName,
              s3Key: f.s3Key,
              fileUrl: f.fileUrl, // or s3Url
              fileText: f.fileText,
              fileType: f.fileType,
            })),
          }
          : {};

      // Legacy `fileText`: on re-run with new uploads, only those files (not Run 1 snapshot).
      // Only send real extracted text as legacy `fileText`.
      // Avoid padding with empty strings so truly-empty docs don't look non-empty.
      const combinedFileText = (
        didSendRerun &&
          originalDebateFilesSnapshot?.length &&
          rerunOnlyFiles.length > 0
          ? rerunOnlyFiles
          : currentFiles
      )
        .map((f) => (typeof f?.fileText === "string" ? f.fileText.trim() : ""))
        .filter((t) => hasUsableDocumentText(t))
        .join("\n\n");
      console.log("[DEBATE] Files being sent:", {
        fileCount: currentFiles.length,
        files: currentFiles.map(f => ({ name: f.fileName, hasText: !!(f.fileText && f.fileText.trim()), textLen: (f.fileText || "").length, s3Key: f.s3Key || "none" })),
        combinedTextLen: combinedFileText.length,
        attachedFilesKeys: Object.keys(attachedFiles),
        didSendRerun,
        rerunOnlyFileCount: rerunOnlyFiles.length,
      });
      const primaryFileName =
        didSendRerun &&
          originalDebateFilesSnapshot?.length &&
          rerunOnlyFiles.length > 0
          ? rerunOnlyFiles[0].fileName
          : currentFiles.length > 0
            ? currentFiles[0].fileName
            : null;

      // Use currently selected project (from sidebar) for this debate, if any
      let projectIdForDebate = null;
      try {
        projectIdForDebate = localStorage.getItem("debateSelectedProjectId");
      } catch (e) {
        projectIdForDebate = null;
      }

      const response = await fetch(`${API_URL}/ai/debate-stream`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        signal: abortControllerRef.current?.signal,
        body: JSON.stringify({
          prompt,
          maxRounds,
          operationId: operationId, // Group all actions from this button press
          fileText: combinedFileText || null, // Legacy field
          fileName: primaryFileName, // Legacy field
          fileIds: Object.keys(fileIds).length > 0 ? fileIds : undefined, // NEW mapped IDs payload
          attachedFiles:
            Object.keys(attachedFiles).length > 0 ? attachedFiles : undefined,
          // Re-run only when same chat: use that chatId and set re-run flag. Otherwise new debate (currentChatId or null).
          chatId: didSendRerun ? originalDebateChatId : currentChatId || null,
          title: debateTitle, // AI-generated title (fetched before starting debate)
          composition,
          projectId: projectIdForDebate || null,
          originalDebateChatId: didSendRerun ? originalDebateChatId : undefined, // Re-run flag: backend saves to debateRerun and keeps same chatId
        }),
      });

      if (!response.ok) {
        if (response.status === 429 || response.status === 403) {
          try {
            const errorData = await response.json();
            if (errorData.anonLimitExceeded) {
              setDebating(false);
              // FUTURE USE: toast-only feedback for free-limit blocks.
              // Disabled to ensure only the modal dialog is shown.
              // showWarning(
              //   errorData.message ||
              //   "You've used all your free demo debates. Sign up to continue.",
              //   "Free Limit Reached",
              // );
              setShowAnonSignupModal(true);
              return;
            }
            throw new Error(errorData.message);
          } catch (e) {
            if (e.message) throw e;
          }
        }

        let errorData = {
          message: `Server error: ${response.status} ${response.statusText}`,
        };
        try {
          errorData = await response.json();
        } catch (e) { }
        if (errorData && errorData.emptyDocument) {
          showError("Attached document is empty", "Empty Document");
          setDebating(false);
          return;
        }
        throw new Error(errorData.message);
      }

      // Store debate data for potential stop
      // Store debate data for potential stop
      currentDebateDataRef.current = {
        prompt: prompt.trim(),
        maxRounds: maxRounds,
        debateFiles: currentFiles,
        title: debateTitle,
      };

      // If this is the first successfully started debate with at least one document,
      // capture the original prompt and the original document set for future comparison re-runs.
      if (
        !originalDebatePrompt &&
        Array.isArray(currentFiles) &&
        currentFiles.length > 0
      ) {
        setOriginalDebatePrompt(prompt.trim());
        setOriginalDebateFilesSnapshot(
          currentFiles.map((f) => ({
            fileName: f.fileName,
            s3Key: f.s3Key,
            documentId: f.documentId,
          })),
        );
      }

      await acquireDebateWakeLock();

      // Parse Server-Sent Events stream
      if (!response.body || typeof response.body.getReader !== "function") {
        throw new Error(
          "Connection was interrupted while the app was in the background. Please try your debate again.",
        );
      }
      const reader = response.body.getReader();
      streamReaderRef.current = reader; // Reader ref for stop functionality
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let finalData = null;
      let currentRounds = [];
      let currentConsensus = "";
      let validationErrorOccurred = false;
      let finalRoundNumber = null; // Track the final round number

      while (true) {
        // Check if debate was stopped
        if (debateStoppedRef.current) {
          break;
        }

        const { done, value } = await reader.read();
        if (done || validationErrorOccurred) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (validationErrorOccurred) break;

          if (line.startsWith("event: ")) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === "start") {
                setRound(0);

                const backendMaxRounds = data.maxRounds || data.rounds;
                if (backendMaxRounds && backendMaxRounds > 0) {
                  setMaxRounds((prev) => Math.max(prev, backendMaxRounds));
                }
                console.log("[DEBATE START] Received start event:", {
                  dataChatId: data.chatId,
                  currentUrlChatId: chatId,
                  willNavigate: !chatId || chatId !== data.chatId,
                });

                // Add new debate chat to sidebar as soon as processing starts
                if (data.chatId) {
                  streamingDebateChatIdRef.current = data.chatId;
                  // Clear comparison state on every new run (first run or re-run)
                  // so the Comparison Dashboard can be regenerated with latest data
                  setComparisonDashboard(null);
                  setComparisonSections(null);
                  setComparisonError(null);
                  setComparisonStreamingBullets(null);
                  setComparisonStreamingNetLabel(null);
                  setComparisonGeneratingDetails(false);
                  if (!hadExistingRoundsBeforeRun) {
                    setShowComparisonDashboard(false);
                  }
                  // Migrate files from "new" or current chat to this chatId so uploaded docs stay visible
                  setChatFiles((prev) => {
                    const fromNew = prev["new"] || {};
                    const fromCurrent =
                      currentChatId && currentChatId !== data.chatId
                        ? prev[currentChatId] || {}
                        : {};
                    const existingForChat = prev[data.chatId] || {};
                    const merge = (a, b, c) =>
                      a?.length ? a : b?.length ? b : c || [];
                    return {
                      ...prev,
                      [data.chatId]: {
                        mainQueryFiles: merge(
                          existingForChat.mainQueryFiles,
                          fromNew.mainQueryFiles,
                          fromCurrent.mainQueryFiles,
                        ),
                        comparisonFiles: merge(
                          existingForChat.comparisonFiles,
                          fromNew.comparisonFiles,
                          fromCurrent.comparisonFiles,
                        ),
                        synthesisFiles: merge(
                          existingForChat.synthesisFiles,
                          fromNew.synthesisFiles,
                          fromCurrent.synthesisFiles,
                        ),
                        debateFiles: merge(
                          existingForChat.debateFiles,
                          fromNew.debateFiles,
                          fromCurrent.debateFiles,
                        ),
                      },
                    };
                  });
                  // Set currentChatId so UI shows this chat (and its files)
                  setCurrentChatId(data.chatId);
                  console.log(
                    "[DEBATE START] Set currentChatId to:",
                    data.chatId,
                  );

                  const chatForSidebar = {
                    _id: data.chatId,
                    chatId: data.chatId,
                    chatId16: data.chatId,
                    title: data.title || "Generating...",
                    query: prompt?.trim?.() || "",
                    type: "debate",
                    chatType: "debate",
                    debateRounds: [],
                    projectId: projectIdForDebate || null,
                    createdAt: new Date(),
                    updatedAt: Date.now(),
                    chatStatus: "loading",
                  };
                  setNewChatForSidebar(chatForSidebar);
                  if (didSendRerun && data.chatId) {
                    setNewChatForSidebar({
                      _id: data.chatId,
                      chatId: data.chatId,
                      sidebarEvent: "rerun-start",
                      chatStatus: "loading",
                    });
                  }


                  const hasSearchParams = location.search && location.search.length > 1;
                  if (!chatId || chatId !== data.chatId || hasSearchParams) {
                    console.log(
                      "[DEBATE START] Navigating to primary chat URL to focus new run:",
                      `/debate-mode/${data.chatId}`,
                      { hasSearchParams }
                    );
                    navigate(`/debate-mode/${data.chatId}`, { replace: true });
                  } else {
                    console.log(
                      "[DEBATE START] Already on correct clean URL, skipping navigation",
                    );
                  }

                  // Comparison baseline: set the original debate (Run 1) for all future comparisons.
                  // This is set once and never updated — no rolling baseline. For every re-run,
                  // the Comparison Dashboard compares against this original run, not the prior re-run.
                  // Set whenever we don't have one yet (first run with or without files).
                  if (!originalDebateChatId && data.chatId) {
                    setOriginalDebateChatId(data.chatId);
                  }
                }
              } else if (currentEvent === "round-start") {
                const totalRounds = data.totalRounds || maxRounds;
                const isSummaryPhase =
                  data.summaryPhase ||
                  (data.round && totalRounds && data.round > totalRounds);
                const isFinalDebateRound = data.round === totalRounds;
                const hideSummaryPhaseForAnonymous =
                  !isLoggedIn && isSummaryPhase;

                if (hideSummaryPhaseForAnonymous) {
                  continue;
                }

                setRound(data.round);

                // Summary phase (separate step after all debate rounds)
                if (isSummaryPhase) {
                  finalRoundNumber = data.round;
                  setStreamingRounds((prev) => ({
                    ...prev,
                    [data.round]: {
                      ...prev[data.round],
                      consensus: {
                        model: "Claude",
                        text: "",
                        fullText: "",
                        loading: true,
                        done: false,
                        error: false,
                      },
                    },
                  }));
                } else if (isFinalDebateRound) {
                  finalRoundNumber = data.round;
                  setStreamingRounds((prev) => ({
                    ...prev,
                    [data.round]: prev[data.round] || {},
                  }));
                } else {
                  // For regular rounds, preserve existing data
                  setStreamingRounds((prev) => {
                    // CRITICAL: Don't clear existing data for this round - preserve any roles that have already started
                    // This is critical for strategist which might have delayed start or for rounds 2+
                    // where streaming events might arrive before round-start
                    const existingRoundData = prev[data.round] || {};
                    // Preserve ALL existing data - don't overwrite with empty object
                    return {
                      ...prev,
                      [data.round]: existingRoundData, // Preserve existing data instead of clearing
                    };
                  });
                }

                setExpandedDebateRounds((prev) => ({
                  ...prev,
                  [data.round - 1]: true, // Auto-expand the current round so user sees streaming
                  ...(data.round < totalRounds ? { [data.round]: true } : {}), // Auto-expand next (queued) round so "Round will begin shortly…" is visible
                }));

                // Put the "Round n/m" header at the top of the transcript as
                // the round begins. QA saw Round 1's header out of view for
                // the whole round while its cards streamed, with the later
                // rounds' headers visible below — scrolling the new round's
                // container into view (its scroll-margin clears the sticky
                // page header) guarantees the header is on screen when the
                // round starts, whatever moved the viewport before.
                const startedRound = data.round;
                window.requestAnimationFrame(() => {
                  setTimeout(() => {
                    const el = document.getElementById(`debate-round-${startedRound}`);
                    if (el && typeof el.scrollIntoView === "function") {
                      el.scrollIntoView({ block: "start", behavior: "smooth" });
                    }
                  }, 60);
                });
              } else if (currentEvent === "model-start") {
                const {
                  round: rNum,
                  role: rRole,
                  model: rModel,
                  attempt: rAttempt,
                } = data;
                setStreamingRounds((prev) => {
                  const roundData = prev[rNum] || {};
                  const roleData = roundData[rRole] || {};
                  // Determine model - prioritize incoming model, then existing, then role-based default
                  const finalModel =
                    rModel ||
                    roleData.model ||
                    (rRole === "strategist"
                      ? "Gemini"
                      : rRole === "advocate"
                        ? "ChatGPT"
                        : "Claude");
                  // If this is a retry/fallback, preserve existing text unless it's attempt 1 and we have no content
                  // Only reset if this is attempt 1 AND we don't have existing content, otherwise preserve
                  const isNewAttempt = !roleData.model || rAttempt === 1;
                  const hasExistingContent =
                    (roleData.fullText || roleData.text) &&
                    (roleData.fullText || roleData.text).trim().length > 0;
                  // Only clear text if it's a new attempt AND we don't have existing content
                  const shouldClearText = isNewAttempt && !hasExistingContent;
                  return {
                    ...prev,
                    [rNum]: {
                      ...roundData,
                      [rRole]: {
                        model: finalModel, // Always set model
                        text: shouldClearText ? "" : roleData.text || "",
                        fullText: shouldClearText
                          ? ""
                          : roleData.fullText || "",
                        attempt: rAttempt || roleData.attempt || 1,
                        loading: true,
                        error: false, // Clear error state on new attempt
                      },
                    },
                  };
                });
              } else if (currentEvent === "model-chunk") {
                const {
                  round: rNum,
                  role: rRole,
                  text: rText,
                  fullText: rFullText,
                  model: rModel,
                } = data;
                setStreamingRounds((prev) => {
                  const roundData = prev[rNum] || {};
                  const roleData = roundData[rRole] || {};
                  // Preserve existing model if not provided in chunk, or use role-based default
                  const preservedModel =
                    rModel ||
                    roleData.model ||
                    (rRole === "strategist"
                      ? "Gemini"
                      : rRole === "advocate"
                        ? "ChatGPT"
                        : "Claude");
                  // CRITICAL FIX: Always update with new chunk data, even if round-done already marked as done
                  // This ensures strategist streaming data is preserved even if it arrives late
                  const newFullText = rFullText || roleData.fullText || "";
                  const newText = rText || roleData.text || "";
                  return {
                    ...prev,
                    [rNum]: {
                      ...roundData,
                      [rRole]: {
                        ...roleData,
                        model: preservedModel, // Always preserve model
                        text: newText,
                        fullText: newFullText,
                        loading: false,
                        // CRITICAL: Don't mark as done if we're still receiving chunks
                        // Only mark as done if explicitly set and we have substantial content
                        done:
                          roleData.done && newFullText.trim().length > 10
                            ? true
                            : false,
                      },
                    },
                  };
                });
              } else if (currentEvent === "model-done") {
                const {
                  round: rNum,
                  role: rRole,
                  text: rFinalText,
                  model: rModel,
                  usage: rUsage,
                } = data;
                setStreamingRounds((prev) => {
                  const roundData = prev[rNum] || {};
                  const roleData = roundData[rRole] || {};
                  // Use the model from the done event, or preserve existing, or default based on role
                  const finalModel =
                    rModel ||
                    roleData.model ||
                    (rRole === "strategist"
                      ? "Gemini"
                      : rRole === "advocate"
                        ? "ChatGPT"
                        : "Claude");
                  // CRITICAL FIX: Prioritize existing fullText if it's more complete than final text
                  // This handles cases where streaming accumulated more text than the final event
                  const existingFullText = roleData.fullText || "";
                  const finalFullText = rFinalText || "";
                  // Use whichever has more content, or final if both are similar
                  const bestText =
                    existingFullText.length > finalFullText.length + 50
                      ? existingFullText
                      : finalFullText || existingFullText;
                  return {
                    ...prev,
                    [rNum]: {
                      ...roundData,
                      [rRole]: {
                        ...roleData,
                        model: finalModel,
                        text: bestText,
                        fullText: bestText,
                        usage: rUsage || roleData.usage,
                        loading: false,
                        done: true,
                        error: false, // Clear any previous error state
                      },
                    },
                  };
                });
              } else if (currentEvent === "model-error") {
                const {
                  round: rNum,
                  role: rRole,
                  error: rError,
                  model: rModel,
                } = data;
                setStreamingRounds((prev) => {
                  const roundData = prev[rNum] || {};
                  const roleData = roundData[rRole] || {};
                  return {
                    ...prev,
                    [rNum]: {
                      ...roundData,
                      [rRole]: {
                        ...roleData,
                        model: rModel || roleData.model,
                        text: `Error: ${rError}`,
                        fullText: `Error: ${rError}`,
                        error: true,
                        loading: false,
                        done: true,
                      },
                    },
                  };
                });
              } else if (currentEvent === "round-done") {
                // Round completed - add to rounds array
                currentRounds.push({
                  round: data.round,
                  arguments: data.arguments,
                });

                // Check if this is the final round with consensus summary
                const isFinalRound = data.round === maxRounds;
                const hasConsensus = data.arguments?.consensus;

                // In that case we must NOT add phantom advocate/critic/strategist entries
                const isSummaryPhaseRoundDone = maxRounds > 0 && data.round > maxRounds;

                // CRITICAL: Update streamingRounds with final data from round-done
                // This ensures all roles (including strategist/Gemini) are preserved for display
                // DEFENSIVE: Always preserve existing streaming data - it's more reliable than final data
                setStreamingRounds((prev) => {
                  const roundData = prev[data.round] || {};
                  const updatedRoundData = { ...roundData };

                  if (isSummaryPhaseRoundDone) {
                    if (hasConsensus) {
                      const consensusData = data.arguments.consensus;
                      const consensusText =
                        typeof consensusData === "string"
                          ? consensusData
                          : consensusData.text || consensusData.fullText || "";
                      const consensusModel =
                        typeof consensusData === "object" && consensusData !== null
                          ? consensusData.model || "Claude"
                          : "Claude";
                      updatedRoundData.consensus = {
                        model: consensusModel,
                        text: consensusText,
                        fullText: consensusText,
                        loading: false,
                        done: true,
                        error: false,
                      };
                    }
                    return {
                      ...prev,
                      [data.round]: updatedRoundData,
                    };
                  }

                  // If this is the final round with consensus, store consensus data
                  if (isFinalRound && hasConsensus) {
                    const consensusData = data.arguments.consensus;
                    const consensusText =
                      typeof consensusData === "string"
                        ? consensusData
                        : consensusData.text || consensusData.fullText || "";
                    const consensusModel =
                      typeof consensusData === "object" &&
                        consensusData !== null
                        ? consensusData.model || "Claude"
                        : "Claude";

                    updatedRoundData.consensus = {
                      model: consensusModel,
                      text: consensusText,
                      fullText: consensusText,
                      loading: false,
                      done: true,
                      error: false,
                    };

                    return {
                      ...prev,
                      [data.round]: updatedRoundData,
                    };
                  }

                  // Define all expected roles
                  const expectedRoles = ["advocate", "critic", "strategist"];

                  // Update each expected role - ensure ALL roles are always present
                  expectedRoles.forEach((role) => {
                    const argument = data.arguments?.[role];
                    let finalText = "";
                    let finalModel =
                      role === "strategist"
                        ? "Gemini"
                        : role === "advocate"
                          ? "ChatGPT"
                          : "Claude";

                    if (argument) {
                      if (typeof argument === "string") {
                        finalText = argument;
                      } else if (
                        typeof argument === "object" &&
                        argument !== null
                      ) {
                        finalText = argument.text || "";
                        finalModel = argument.model || finalModel;
                      }
                    }

                    // Get existing streaming data
                    const existingData = roundData[role];
                    const existingText =
                      existingData?.fullText || existingData?.text || "";
                    const existingModel = existingData?.model || finalModel;
                    const hasExistingContent =
                      existingText && existingText.trim().length > 0;
                    const hasFinalContent =
                      finalText && finalText.trim().length > 0;

                    // CRITICAL FIX: Prioritize existing streaming data if it has content
                    // Only use final data if it's better (has content AND existing doesn't)
                    // This prevents overwriting valid streaming data with empty/placeholder final data
                    // CRITICAL: For strategist/Gemini, streaming data might arrive late, so we must preserve existing structure
                    if (hasExistingContent) {
                      // We have existing streaming data with content - preserve it (most reliable)
                      updatedRoundData[role] = {
                        ...existingData,
                        model: existingModel, // Preserve existing model
                        text: existingText,
                        fullText: existingText,
                        loading: false,
                        done: true,
                        error: false,
                      };
                    } else if (
                      hasFinalContent &&
                      finalText.trim().length > 10
                    ) {
                      // No existing content but we have final data with substantial content - use it
                      // Only use if final text is substantial (more than 10 chars) to avoid placeholder text
                      updatedRoundData[role] = {
                        model: finalModel,
                        text: finalText,
                        fullText: finalText,
                        loading: false,
                        done: true,
                        error: false,
                      };
                    } else if (existingData) {
                      // No content in either, but we have existing data structure - preserve it
                      // This is critical for strategist which might still be streaming
                      // Don't overwrite with placeholder - let streaming continue
                      updatedRoundData[role] = {
                        ...existingData,
                        model: existingModel,
                        loading: existingData.loading !== false, // Preserve loading state
                        done: existingData.done || false,
                        error: false,
                      };
                    } else {
                      // No data at all - create placeholder to ensure role is shown
                      // But mark it as still loading if it's strategist (might be delayed)
                      updatedRoundData[role] = {
                        model: finalModel,
                        text:
                          role === "strategist"
                            ? ""
                            : `No response generated for ${role} in round ${data.round}`,
                        fullText:
                          role === "strategist"
                            ? ""
                            : `No response generated for ${role} in round ${data.round}`,
                        loading: role === "strategist", // Keep loading state for strategist if no data yet
                        done: role !== "strategist", // Don't mark strategist as done if no data
                        error: false,
                      };
                    }
                  });

                  return {
                    ...prev,
                    [data.round]: updatedRoundData,
                  };
                });
              } else if (currentEvent === "consensus-start") {
                setStreamingConsensus("");
                // Also update streamingRounds for the final round
                const targetRound = finalRoundNumber || maxRounds;
                setStreamingRounds((prev) => ({
                  ...prev,
                  [targetRound]: {
                    ...prev[targetRound],
                    consensus: {
                      model: data.model || "Claude",
                      text: "",
                      fullText: "",
                      loading: true,
                      done: false,
                      error: false,
                    },
                  },
                }));
              } else if (currentEvent === "consensus-chunk") {
                // Update consensus in real-time
                setStreamingConsensus(data.fullText);
                // Also update streamingRounds for the final round
                const targetRound = finalRoundNumber || maxRounds;
                setStreamingRounds((prev) => ({
                  ...prev,
                  [targetRound]: {
                    ...prev[targetRound],
                    consensus: {
                      model: data.model || "Claude",
                      text: data.text || "",
                      fullText: data.fullText || "",
                      loading: true,
                      done: false,
                      error: false,
                    },
                  },
                }));
              } else if (currentEvent === "consensus-done") {
                currentConsensus = data.text;
                setStreamingConsensus(data.text);
                // Also update streamingRounds for the final round
                const targetRound = finalRoundNumber || maxRounds;
                setStreamingRounds((prev) => ({
                  ...prev,
                  [targetRound]: {
                    ...prev[targetRound],
                    consensus: {
                      model: data.model || "Claude",
                      text: data.text || "",
                      fullText: data.text || "",
                      loading: false,
                      done: true,
                      error: false,
                    },
                  },
                }));
              } else if (currentEvent === "complete") {
                finalData = data;
                // Re-run on same chat just finished: show "Generate Comparison Dashboard"
                if (data?.chatId && didSendRerun) {
                  setHasRerunOnSameChat(true);
                  setDebateRerunCount((prev) => prev + 1);
                }
                // Fallback: if backend indicates this was a rerun
                if (data?.isRerun) {
                  setHasRerunOnSameChat(true);
                  setDebateRerunCount((prev) => Math.max(prev, 1));
                }
              } else if (currentEvent === "error") {
                // Check if this is a validation error
                if (data.validationError) {
                  setError(
                    data.message ||
                    "This request is outside the scope of legal analysis. Please rephrase it as a legal issue or provide a legal document for review.",
                  );
                  setDebating(false);
                  setRound(0);
                  validationErrorOccurred = true;
                  break; // Break out of inner loop
                }
                throw new Error(data.message || "Error processing debate");
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e, line);
            }
            currentEvent = null;
          } else if (line.trim() === "") {
            currentEvent = null;
          }
        }
      }

      // If validation error occurred, stop processing
      if (validationErrorOccurred) {
        streamReaderRef.current = null;
        return;
      }

      // Check if debate was stopped by user
      if (debateStoppedRef.current) {
        streamReaderRef.current = null;
        // stopDebate function handles UI update and saving
        return;
      }

      // Handle final completion
      if (finalData) {
        const normalizedFinalRounds = normalizeDebateRounds(
          finalData.rounds || currentRounds,
        );
        setDebating(false);
        setRound(finalData.maxRounds || maxRounds);

        // CRITICAL: Only populate top-level consensusSummary if the rounds array
        // does NOT already contain a summary round — otherwise both UI paths would
        // render simultaneously and duplicate the Final Consensus section.
        const hasSummaryRoundInFinalData = normalizedFinalRounds.some(
          (r) =>
            (finalData.maxRounds > 0 && r.round > finalData.maxRounds) ||
            (r.arguments?.consensus && !r.arguments?.advocate),
        );
        setDebateData({
          rounds: normalizedFinalRounds,
          consensusSummary: hasSummaryRoundInFinalData
            ? ""
            : (typeof finalData.consensusSummary === "string"
              ? finalData.consensusSummary
              : currentConsensus),
          maxRounds: finalData.maxRounds || maxRounds,
          stopped: false,
          stoppedAtRound: 0,
          debateError: null,
        });

        // Auto-expand the first round, plus the summary round (if the consensus
        // is embedded as an extra round) so users see it without an extra click.
        if (normalizedFinalRounds.length > 0) {
          const summaryRoundIndex = normalizedFinalRounds.findIndex(
            (r) =>
              (finalData.maxRounds > 0 && r.round > finalData.maxRounds) ||
              (r.arguments?.consensus && !r.arguments?.advocate),
          );
          const expanded = { 0: true };
          if (summaryRoundIndex > -1) expanded[summaryRoundIndex] = true;
          setExpandedDebateRounds(expanded);
        }
        setSummaryOpen(true);

        // Don't clear streaming state immediately - let it fade out naturally
        // This ensures formatting is preserved during the transition
        setTimeout(() => {
          setStreamingRounds({});
          setStreamingConsensus("");
        }, 500);

        // Navigate to the chat URL if chatId is available
        const savedChatId =
          finalData.chatId ||
          finalData.chat?.chatId ||
          finalData.chat?.chatId16 ||
          null;
        if (savedChatId && !chatId) {
          // Only navigate if we're not already on a chat URL
          navigate(`/debate-mode/${savedChatId}`, { replace: true });
        }
        if (savedChatId) {
          setNewChatForSidebar({
            _id: savedChatId,
            chatId: savedChatId,
            sidebarEvent: "status-update",
            chatStatus: "done",
          });
        }

        // Re-run flow: auto-generate and display Comparison Dashboard after re-run completes.
        // Use didSendRerun OR backend's isRerun flag as fallback.
        const isRerunCompleted = didSendRerun || finalData?.isRerun;
        const rerunChatId = originalDebateChatId || finalData?.chatId;
        if (isRerunCompleted && rerunChatId && originalDebateHadDocument) {
          setTimeout(() => {
            runDebateComparison(rerunChatId, rerunChatId)
              .then(() => {
                setShowComparisonDashboard(true);
              })
              .catch(() => { });
          }, 800);
        }
      } else if (!debateStoppedRef.current) {
        let recoveredFromServer = await tryRecoverServerSnapshot();
        if (!recoveredFromServer) {
          recoveredFromServer = await pollDebateUntilSyncedFromServer();
        }
        if (!recoveredFromServer) {
          const nf = classifyDebateStreamFailure(
            new Error("Debate stream ended before completion"),
          );
          const failureMessage = formatClassifiedDebateFailure(nf);
          setError(failureMessage);
          if (
            Object.keys(streamingRounds || {}).length > 0 ||
            streamingConsensus
          ) {
            setDebateData(buildPartialDebateState(failureMessage));
            setQueryLockedAfterStop(true);
          }
          setDebating(false);
          setRound(0);
        }
      }

      // Cleanup reader ref, streaming flag, and operation id
      streamReaderRef.current = null;
      isStreamingDebateRef.current = false;
      operationIdRef.current = null;
    } catch (err) {
      console.error("Debate error:", err);

      // Cleanup reader ref, streaming flag, and operation id
      streamReaderRef.current = null;
      isStreamingDebateRef.current = false;
      operationIdRef.current = null;

      const classified = classifyDebateStreamFailure(err);
      if (
        classified.category === "aborted" ||
        err.name === "AbortError" ||
        debateStoppedRef.current
      ) {
        setDebating(false);
        setRound(0);
        return;
      }

      let recoveredFromServer = false;
      if (isLoggedIn && streamingDebateChatIdRef.current) {
        recoveredFromServer = await tryRecoverServerSnapshot();
        if (!recoveredFromServer) {
          recoveredFromServer = await pollDebateUntilSyncedFromServer();
        }
      }

      if (!recoveredFromServer) {
        if (err.message?.includes("timeout")) {
          const timeoutMessage =
            "Debate request timed out. This can happen with many rounds. Try reducing the number of rounds or try again.";
          setError(timeoutMessage);
          if (
            Object.keys(streamingRounds || {}).length > 0 ||
            streamingConsensus
          ) {
            setDebateData(buildPartialDebateState(timeoutMessage));
            setQueryLockedAfterStop(true);
          }
        } else {
          const failureMessage =
            formatClassifiedDebateFailure(classified) ||
            err.message ||
            "Connection was interrupted before the debate completed. Partial results are shown below.";
          setError(failureMessage);
          if (
            Object.keys(streamingRounds || {}).length > 0 ||
            streamingConsensus
          ) {
            setDebateData(buildPartialDebateState(failureMessage));
            setQueryLockedAfterStop(true);
          }
        }
      }

      // Restore original debate data if a rerun failed, so the original is not lost
      if (previousDebateData && !recoveredFromServer) {
        setDebateData(previousDebateData);
      }

      setDebating(false);
      setRound(0);
    } finally {
      await releaseDebateWakeLock();
    }
  };

  // Animation variants for sidebar
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // Screen size detection
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [isMediumScreen, setIsMediumScreen] = useState(
    window.innerWidth >= 501 && window.innerWidth <= 900,
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsSmallScreen(width <= 1024);
      setIsMediumScreen(width >= 501 && width <= 900);
      setIsMobileScreen(width <= 500);
      setIsDebateIntroMobile(width <= 940);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Keep sidebar/overlay offset aligned with the rendered header height.
  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerEl = document.querySelector(
        ".debate-layout-container .landing-navbar",
      );
      const fallback = 69;
      const measured = headerEl?.getBoundingClientRect?.().height || 0;
      setHeaderHeightPx(measured > 0 ? Math.round(measured) : fallback);
    };

    updateHeaderHeight();
    const timeoutId = setTimeout(updateHeaderHeight, 150);
    window.addEventListener("resize", updateHeaderHeight);
    window.addEventListener("orientationchange", updateHeaderHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateHeaderHeight);
      window.removeEventListener("orientationchange", updateHeaderHeight);
    };
  }, [isMobileScreen]);

  useEffect(() => {
    const handleHeaderMenuToggle = (event) => {
      if (!event?.target?.closest?.(".header-mobile-toggle")) return;
      setSidebarOpen(false);
    };

    document.addEventListener("click", handleHeaderMenuToggle, true);
    return () => {
      document.removeEventListener("click", handleHeaderMenuToggle, true);
    };
  }, []);

  const showDebateOverlay = debating;

  const hasStreamingBullets =
    comparisonStreamingBullets &&
    typeof comparisonStreamingBullets === "object" &&
    Object.values(comparisonStreamingBullets).some(
      (v) => (v || "").toString().trim().length > 0,
    );

  return (
    <div
      className={`dashboard-container debate-layout-container ${darkMode ? "dark" : ""}`}
      style={{
        minHeight: "100vh",
        minHeight: "100dvh",
        "--header-height": `${headerHeightPx}px`,
      }}
    >
      <SEO title="Debate Mode" path="/debate-mode" description="AI-powered adversarial legal debate. Three AI models take assigned roles to stress-test your legal arguments over multiple rounds." noIndex={true} />
      {/* Standardized Header - Positioned outside the main wrapper like the Dashboard */}
      <Header />

      {subscriptionPaymentBlocked && isLoggedIn && (
        <div
          role="alertdialog"
          aria-labelledby="debate-subscription-lock-title"
          aria-describedby="debate-subscription-lock-desc"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: `${headerHeightPx}px`,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "1.5rem",
            textAlign: "center",
            background: darkMode
              ? "rgba(15, 23, 42, 0.92)"
              : "rgba(248, 250, 252, 0.96)",
            zIndex: 9998,
          }}
        >
          <Lock
            size={40}
            style={{ color: darkMode ? "#94a3b8" : "#64748b" }}
            aria-hidden
          />
          <h2
            id="debate-subscription-lock-title"
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: darkMode ? "#f1f5f9" : "#0f172a",
            }}
          >
            Payment Required
          </h2>
          <p
            id="debate-subscription-lock-desc"
            style={{
              margin: 0,
              maxWidth: "28rem",
              fontSize: "0.95rem",
              lineHeight: 1.5,
              color: darkMode ? "#cbd5e1" : "#475569",
            }}
          >
            {subscriptionPaymentMessage ||
              "Subscription suspended — update your payment method to restore access."}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              className="subscription-cta"
              onClick={() => navigate("/settings?tab=billing")}
            >
              Update billing
            </button>
            <button
              type="button"
              className="subscription-cta"
              style={{ background: "transparent", border: "1px solid currentColor" }}
              onClick={() => navigate("/pricing")}
            >
              View plans
            </button>
          </div>
        </div>
      )}

      {loadingChat && (
        <div
          aria-live="polite"
          aria-busy="true"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            background: darkMode
              ? "rgba(15, 23, 42, 0.85)"
              : "rgba(248, 250, 252, 0.9)",
            zIndex: 9999,
          }}
        >
          <Loader2
            size={32}
            style={{ flexShrink: 0, animation: "spin 1s linear infinite" }}
          />
          <span
            style={{
              fontSize: "0.9375rem",
              color: darkMode ? "#94a3b8" : "#64748b",
              fontWeight: 500,
            }}
          >
            Loading debate…
          </span>
        </div>
      )}

      <div
        className={`dashboard-main-wrapper ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        {isSidebarOverlay && sidebarOpen && (
          <button
            type="button"
            className="dashboard-mobile-backdrop"
            aria-label="Close sidebar"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {/* Drawer opener at drawer widths. The chat sidebar is off-canvas there
            with no rail on screen, and the setup-screen header is not rendered
            once a debate has results, so this sits outside both: a fixed
            control under the site header that exists in every state. */}
        {isSidebarOverlay &&
          !sidebarOpen &&
          typeof document !== "undefined" &&
          createPortal(
            // Portalled to <body>: inside the debate container the legacy
            // phone rules restyle buttons, which turned this into a centred
            // block above the header instead of a fixed control at the left.
            <button
              type="button"
              className={`dw-drawer-toggle${darkMode ? " dw-drawer-toggle--dark" : ""}`}
              onClick={() => setSidebarOpen(true)}
              aria-label="Open chats"
              title="Chats"
            >
              <PanelLeft size={18} aria-hidden="true" />
            </button>,
            document.body,
          )}
        {/* Debate Sidebar with chat history */}
        <DebateSidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          newChatFromDebate={newChatForSidebar}
          onClearNewChat={() => setNewChatForSidebar(null)}
          headerHeightPx={headerHeightPx}
        />

        <motion.div
          className="main-content debate-main-content-wrapper"
          initial={{
            opacity: 0,
            x: 50,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            opacity: { duration: 0.5, delay: 0.2 },
            x: { duration: 0.5, delay: 0.2 },
          }}
          style={{
            marginLeft: 0, // Margin handled by .dashboard-main-wrapper CSS
            marginTop: 0, // Padding-top handled by CSS
            paddingTop: "2vh", // 2vh top padding
            width: "100%", // Full width within the wrapper
            paddingRight: isMobileScreen ? "0rem" : "0rem", // Add right padding
            paddingLeft: isMobileScreen ? "0.5rem" : "1rem", // Add left padding
            boxSizing: "border-box",
            position: "relative",
            zIndex: isMobileScreen ? 1 : "auto", // Below sidebar overlay on mobile
          }}
        >
          <main className="debate-page-content">
            {showDebateOverlay && (
              <div
                className={`debate-progress-wrapper ${darkMode ? "dark" : ""}`}
              >
                <DebateProgressUI
                  maxRounds={maxRounds}
                  currentRound={round || 1}
                  darkMode={darkMode}
                  modelNames={composition?.models?.replace(/, ([^,]+)$/, ", and $1")}
                />
              </div>
            )}

            {/* Show streaming rounds as they come in - below DebateProgress */}
            {/* Show rounds if debating and we have any streaming data OR if we have a round number OR if maxRounds is set */}
            {debating &&
              (Object.keys(streamingRounds).length > 0 ||
                round > 0 ||
                maxRounds > 0) && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="debate-all-rounds"
                  style={{ marginTop: showDebateOverlay ? "0" : "2rem" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <h2 className="debate-rounds-title" style={{ margin: 0 }}>
                      Debate Rounds (Streaming)
                    </h2>
                    {(Object.keys(streamingRounds).length > 0 ||
                      streamingConsensus) && (
                        <div style={{ position: "relative" }}>
                          <motion.button
                            type="button"
                            className="debate-ui-btn-text-fix debate-streaming-download-btn"
                            onClick={() =>
                              setDownloadStreamingMenuOpen((prev) => !prev)
                            }
                            disabled={debating}
                            whileHover={{ scale: debating ? 1 : 1.05 }}
                            whileTap={{ scale: debating ? 1 : 0.95 }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.35rem",
                              padding: "0.55rem 1rem",
                              borderRadius: "0.5rem",
                              border: "none",
                              background: debating
                                ? "#94a3b8"
                                : darkMode
                                  ? "#2563eb"
                                  : "#3b82f6",
                              color: "white",
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              cursor: debating ? "not-allowed" : "pointer",
                              opacity: debating ? 0.6 : 1,
                              transition: "all 0.2s ease",
                            }}
                          >
                            <Download size={16} />
                            Download Rounds
                          </motion.button>
                          {downloadStreamingMenuOpen && (
                            <div
                              style={{
                                position: "absolute",
                                right: 0,
                                marginTop: "0.5rem",
                                minWidth: "220px",
                                background: darkMode ? "#0f172a" : "#ffffff",
                                border: "1px solid rgba(148,163,184,0.3)",
                                borderRadius: "0.5rem",
                                boxShadow: "0 10px 30px rgba(15,23,42,0.25)",
                                zIndex: 20,
                                overflow: "hidden",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setDownloadStreamingMenuOpen(false);
                                  handleDownloadDebatePDF();
                                }}
                                style={{
                                  width: "100%",
                                  padding: "0.5rem 0.75rem",
                                  textAlign: "left",
                                  fontSize: "0.85rem",
                                  border: "none",
                                  background: "transparent",
                                  color: darkMode ? "#e5e7eb" : "#111827",
                                  cursor: "pointer",
                                }}
                              >
                                All rounds (PDF)
                              </button>
                              <div
                                style={{
                                  borderTop: "1px solid rgba(148,163,184,0.3)",
                                }}
                              />
                              {Array.from(
                                new Set(
                                  Object.keys(streamingRounds)
                                    .map(Number)
                                    .filter((n) => n > 0)
                                    .sort((a, b) => a - b),
                                ),
                              ).map((roundNum) => (
                                <button
                                  key={roundNum}
                                  type="button"
                                  onClick={() => {
                                    setDownloadStreamingMenuOpen(false);
                                    handleDownloadDebatePDF([roundNum]);
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "0.5rem 0.75rem",
                                    textAlign: "left",
                                    fontSize: "0.85rem",
                                    border: "none",
                                    background: "transparent",
                                    color: darkMode ? "#e5e7eb" : "#111827",
                                    cursor: "pointer",
                                  }}
                                >
                                  Round {roundNum} only
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                  {/* Show all rounds up to current round, even if not all models have started */}
                  {/* Calculate max round from round state, streamingRounds keys, and maxRounds */}
                  {/* CRITICAL: When debating, show all rounds up to maxRounds to ensure rounds 5-8 are visible */}
                  {(() => {
                    const streamingRoundNumbers = Object.keys(streamingRounds)
                      .map(Number)
                      .filter((n) => n > 0);
                    const maxStreamingRound =
                      streamingRoundNumbers.length > 0
                        ? Math.max(...streamingRoundNumbers)
                        : 0;

                    // When debating, always show up to maxRounds to ensure all rounds are visible
                    // This is critical for rounds 5-8 where round state might lag
                    let finalMaxRound;
                    if (debating && maxRounds > 0) {
                      // While debating, show all rounds up to maxRounds
                      // But also include any rounds that have started (might be ahead of round state)
                      finalMaxRound = Math.max(
                        maxRounds,
                        maxStreamingRound,
                        round || 0,
                      );
                    } else {
                      // Not debating or no maxRounds - use current round or streaming rounds
                      finalMaxRound = Math.max(round || 0, maxStreamingRound);
                    }

                    const roundNumbers = Array.from(
                      { length: finalMaxRound || 0 },
                      (_, i) => i + 1,
                    );
                    // When debating, show rounds 1..maxRounds; when in summary phase (round > maxRounds) also show Final Summary inline so it can auto-expand
                    const toShow = debating
                      ? roundNumbers.filter((r) => {
                        if (maxRounds > 0 && r > maxRounds) {
                          if (!isLoggedIn) return false;
                          return round > maxRounds && r === maxRounds + 1;
                        }
                        return true;
                      })
                      : roundNumbers;
                    return toShow;
                  })().map((roundNumber) => {
                    const rolesForRound = streamingRounds[roundNumber] || {};
                    const roundIndex = roundNumber - 1; // Convert to 0-based index for state
                    // Summary phase is the separate step after all debate rounds (roundNumber > maxRounds)
                    const isSummaryRound =
                      maxRounds > 0 && roundNumber > maxRounds;
                    const isFinalRound = isSummaryRound;
                    const hasConsensusData =
                      rolesForRound.consensus || streamingConsensus;

                    return (
                      <div
                        key={roundNumber}
                        id={`debate-round-${roundNumber}`}
                        className="debate-round-container"
                      >
                        <button
                          onClick={() => toggleDebateRound(roundIndex)}
                          className="debate-round-header"
                        >
                          <span className="debate-round-title">
                            {isSummaryRound
                              ? "Final Consensus & Summary"
                              : `Round ${roundNumber}/${maxRounds}`}
                          </span>
                          {expandedDebateRounds[roundIndex] ? (
                            <ChevronUp size={20} />
                          ) : (
                            <ChevronDown size={20} />
                          )}
                        </button>
                        <AnimatePresence>
                          {expandedDebateRounds[roundIndex] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="debate-round-content"
                            >
                              {/* Check if this is the final round - always show consensus UI for final round */}
                              {isFinalRound ? (
                                <div
                                  className="debate-results-grid"
                                  style={{ gridTemplateColumns: "1fr" }}
                                >
                                  <div className="debate-result-card">
                                    {streamingConsensus ? (
                                      <>
                                        <div
                                          ref={(el) => {
                                            const refKey = `${roundNumber}-consensus`;
                                            if (el) {
                                              streamingResponseRefs.current[
                                                refKey
                                              ] = el;
                                            } else {
                                              delete streamingResponseRefs
                                                .current[refKey];
                                            }
                                          }}
                                          className="response-html"
                                          style={{
                                            height: "400px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            minHeight: "400px",
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: convertTextToHtml(
                                              ensureDisclaimerAtEnd(
                                                removeCitationMarkers(
                                                  removeSeparatorLines(
                                                    streamingConsensus,
                                                  ),
                                                ),
                                                false,
                                              ),
                                            ),
                                          }}
                                        />
                                        <div
                                          className="response-actions"
                                          style={{
                                            marginTop: "0.5rem",
                                            display: "flex",
                                            gap: "0.5rem",
                                          }}
                                        >
                                          <motion.button
                                            className="response-btn"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() =>
                                              handleOpenEditor(
                                                "debate-consensus",
                                                "Claude Final Consensus Summary",
                                                convertTextToHtml(
                                                  ensureDisclaimerAtEnd(
                                                    removeCitationMarkers(
                                                      removeSeparatorLines(
                                                        streamingConsensus,
                                                      ),
                                                    ),
                                                    false,
                                                  ),
                                                ),
                                                {},
                                              )
                                            }
                                            disabled={debating}
                                            style={{
                                              opacity: debating ? 0.6 : 1,
                                              cursor: debating
                                                ? "not-allowed"
                                                : "pointer",
                                              padding: "0.5rem 1rem",
                                              borderRadius: "0.5rem",
                                              border: "none",
                                              background: darkMode
                                                ? "#2563eb"
                                                : "#3b82f6",
                                              color: "white",
                                              fontSize: "0.875rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Edit / Export
                                          </motion.button>
                                        </div>
                                      </>
                                    ) : rolesForRound.consensus ? (
                                      <>
                                        <div
                                          ref={(el) => {
                                            const refKey = `${roundNumber}-consensus`;
                                            if (el) {
                                              streamingResponseRefs.current[
                                                refKey
                                              ] = el;
                                            } else {
                                              delete streamingResponseRefs
                                                .current[refKey];
                                            }
                                          }}
                                          className="response-html"
                                          style={{
                                            height: "400px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            minHeight: "400px",
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: convertTextToHtml(
                                              ensureDisclaimerAtEnd(
                                                removeCitationMarkers(
                                                  removeSeparatorLines(
                                                    rolesForRound.consensus
                                                      .text ||
                                                    rolesForRound.consensus
                                                      .fullText ||
                                                    "",
                                                  ),
                                                ),
                                                true,
                                              ),
                                            ),
                                          }}
                                        />
                                        <div
                                          className="response-actions"
                                          style={{
                                            marginTop: "0.5rem",
                                            display: "flex",
                                            gap: "0.5rem",
                                          }}
                                        >
                                          <motion.button
                                            className="response-btn"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() =>
                                              handleOpenEditor(
                                                "debate-consensus",
                                                "Claude Final Consensus Summary",
                                                convertTextToHtml(
                                                  ensureDisclaimerAtEnd(
                                                    removeCitationMarkers(
                                                      removeSeparatorLines(
                                                        rolesForRound.consensus
                                                          .text ||
                                                        rolesForRound
                                                          .consensus
                                                          .fullText ||
                                                        "",
                                                      ),
                                                    ),
                                                    true,
                                                  ),
                                                ),
                                                {},
                                              )
                                            }
                                            disabled={debating}
                                            style={{
                                              opacity: debating ? 0.6 : 1,
                                              cursor: debating
                                                ? "not-allowed"
                                                : "pointer",
                                              padding: "0.5rem 1rem",
                                              borderRadius: "0.5rem",
                                              border: "none",
                                              background: darkMode
                                                ? "#2563eb"
                                                : "#3b82f6",
                                              color: "white",
                                              fontSize: "0.875rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Edit / Export
                                          </motion.button>
                                        </div>
                                      </>
                                    ) : (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                          color: darkMode
                                            ? "#94a3b8"
                                            : "#64748b",
                                          padding: "1rem",
                                        }}
                                      >
                                        <Hourglass
                                          size={16}
                                          className="debate-hourglass-queued"
                                          aria-hidden
                                        />
                                        <span>
                                          Generating consensus summary...
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="debate-results-grid">
                                  {/* Display by role (slot) order: advocate, critic, strategist.
                                      Expected model for each role should come from the current composition
                                      (e.g. standard vs adversarial panel), not hardcoded. */}
                                  {[
                                    {
                                      role: "advocate",
                                      displayName: "Advocate",
                                      expectedModel:
                                        composition?.roles?.advocate ||
                                        "ChatGPT",
                                    },
                                    {
                                      role: "critic",
                                      displayName: "Critical Analyst",
                                      expectedModel:
                                        composition?.roles?.critic || "Claude",
                                    },
                                    {
                                      role: "strategist",
                                      displayName:
                                        "Strategic/Procedural Analyst",
                                      expectedModel:
                                        composition?.roles?.strategist ||
                                        "Gemini",
                                    },
                                  ].map((slot) => {
                                    // For streaming, data is stored by role; each role entry includes the actual model used
                                    const data = rolesForRound[slot.role];
                                    // Determine the actual model - prioritize data.model, fallback to expectedModel
                                    const actualModel =
                                      data?.model || slot.expectedModel;
                                    // Check if we have any content to display
                                    // CRITICAL: For strategist, be more lenient - check if data exists even if text is empty
                                    const hasContent =
                                      data &&
                                      (data.fullText || data.text) &&
                                      (data.fullText || data.text).trim()
                                        .length > 0;
                                    // Check if still loading (data exists but no content yet and loading is true)
                                    // CRITICAL FIX: For strategist, if we have data structure but no content and not done, it's still loading
                                    // This handles cases where round-done arrives before strategist streaming completes
                                    const isLoading =
                                      data?.loading === true ||
                                      (slot.role === "strategist" &&
                                        data &&
                                        !hasContent &&
                                        !data?.error &&
                                        !data?.done) ||
                                      (!hasContent &&
                                        !data?.error &&
                                        !data?.done &&
                                        data);

                                    return (
                                      <div
                                        key={slot.role}
                                        className="debate-result-card"
                                      >
                                        <h3
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            margin: "0 0 0.5rem 0",
                                          }}
                                        >
                                          <span>
                                            {actualModel}'s Argument (
                                            {slot.displayName})
                                          </span>
                                          {data && data.done && !data.error && (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                                color: darkMode ? "#94a3b8" : "#4b5563",
                                              }}
                                            >
                                              <CheckCircle size={16} />
                                              <span>Complete</span>
                                            </div>
                                          )}
                                          {data && (!data.done || isLoading) && !data.error && (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontSize: "14px",
                                                fontWeight: "normal",
                                                color: darkMode ? "#94a3b8" : "#2563eb",
                                              }}
                                            >
                                              <Loader2
                                                size={16}
                                                className="debate-spinner"
                                              />
                                              <span>Debating...</span>
                                            </div>
                                          )}
                                        </h3>
                                        {isLoading ? (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              color: darkMode
                                                ? "#94a3b8"
                                                : "#64748b",
                                            }}
                                          >
                                            <span>
                                              {data?.loading
                                                ? "Thinking..."
                                                : "Waiting to start..."}
                                            </span>
                                          </div>
                                        ) : data?.error ? (
                                          <div
                                            style={{
                                              color: "#ef4444",
                                              padding: "1rem",
                                              border: "1px solid #fee2e2",
                                              borderRadius: "0.5rem",
                                              background: "#fef2f2",
                                            }}
                                          >
                                            <strong>Model Error:</strong>{" "}
                                            {data.text ||
                                              "Unknown error occurred"}
                                          </div>
                                        ) : hasContent ? (
                                          <>
                                            <div
                                              ref={(el) => {
                                                const refKey = `${roundNumber}-${slot.role}`;
                                                if (el) {
                                                  streamingResponseRefs.current[
                                                    refKey
                                                  ] = el;
                                                } else {
                                                  delete streamingResponseRefs
                                                    .current[refKey];
                                                }
                                              }}
                                              className="response-html"
                                              style={{
                                                height: "400px",
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                                minHeight: "400px",
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html: convertTextToHtml(
                                                  ensureDisclaimerAtEnd(
                                                    removeCitationMarkers(
                                                      removeSeparatorLines(
                                                        data.fullText ||
                                                        data.text ||
                                                        "",
                                                      ),
                                                    ),
                                                    !isLoading &&
                                                    !data?.loading &&
                                                    data?.done,
                                                  ),
                                                ),
                                              }}
                                            />
                                            <div
                                              className="response-actions"
                                              style={{
                                                marginTop: "0.5rem",
                                                display: "flex",
                                                gap: "0.5rem",
                                              }}
                                            >
                                              <motion.button
                                                className="response-btn"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() =>
                                                  handleOpenEditor(
                                                    "debate-round",
                                                    `Round ${roundNumber} - ${actualModel} (${slot.displayName})`,
                                                    convertTextToHtml(
                                                      ensureDisclaimerAtEnd(
                                                        removeCitationMarkers(
                                                          removeSeparatorLines(
                                                            data.fullText ||
                                                            data.text ||
                                                            "",
                                                          ),
                                                        ),
                                                        !isLoading &&
                                                        !data?.loading &&
                                                        data?.done,
                                                      ),
                                                    ),
                                                    {
                                                      roundIndex: roundIndex,
                                                      role: slot.role,
                                                    },
                                                  )
                                                }
                                                disabled={debating}
                                                style={{
                                                  opacity: debating ? 0.6 : 1,
                                                  cursor: debating
                                                    ? "not-allowed"
                                                    : "pointer",
                                                  padding: "0.5rem 1rem",
                                                  borderRadius: "0.5rem",
                                                  border: "none",
                                                  background: darkMode
                                                    ? "#2563eb"
                                                    : "#3b82f6",
                                                  color: "white",
                                                  fontSize: "0.875rem",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Edit / Export
                                              </motion.button>
                                            </div>
                                          </>
                                        ) : (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              color: darkMode
                                                ? "#94a3b8"
                                                : "#64748b",
                                              padding: "1rem",
                                            }}
                                          >
                                            {roundNumber === 1 ||
                                              roundNumber === round ? (
                                              <>
                                                <Loader2
                                                  size={16}
                                                  className="debate-spinner"
                                                />
                                                <span>
                                                  Waiting for response...
                                                </span>
                                              </>
                                            ) : (
                                              <>
                                                <Hourglass
                                                  size={16}
                                                  className="debate-hourglass-queued"
                                                  aria-hidden
                                                />
                                                <span>
                                                  Round will begin shortly…
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Show stop button below the currently running round */}
                      </div>
                    );
                  })}
                </motion.section>
              )}

            {/* DebateSummary for streaming state - only when summary is not yet shown inline (round <= maxRounds); when round > maxRounds the summary is in the expandable list above */}
            {debating && round <= maxRounds && (
              <DebateSummary
                summary={streamingConsensus}
                loading={false}
                debating={debating}
                isLocked={false}
                isOpen={summaryOpen}
                onToggle={() => setSummaryOpen(!summaryOpen)}
                handleOpenEditor={handleOpenEditor}
                darkMode={darkMode}
                title="Final Consensus & Summary"
              />
            )}
            {/* Show stop button below the summary */}
            {debating && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  marginTop: "1.5rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <motion.button
                  onClick={stopDebate}
                  className="debate-status-bar debate-stop-button"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 8px 16px rgba(239, 68, 68, 0.3)",
                    backgroundColor: "#dc2626",
                  }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.75rem 1.5rem",
                    fontSize: "1rem",
                    fontWeight: 500,
                    transition: "all 0.2s ease",
                  }}
                >
                  <X
                    className="debate-status-icon"
                    size={20}
                    strokeWidth={2.5}
                  />
                  <span className="debate-status-text">
                    Stop Debate (No Summary)
                  </span>
                </motion.button>
              </motion.div>
            )}

            <div style={{ display: showDebateOverlay ? "none" : "block" }}>
              {/* Mobile sidebar toggle - hidden on mobile as sidebar is always visible as icon bar */}

              <div
                className={`debate-page${setupFormIsDead ? " debate-page--setup-done" : ""}${setupFormIsCollapsible ? ` debate-page--setup-collapsible${setupFormOpen ? " is-open" : ""}` : ""}`}
                style={{ position: "relative" }}
              >
                {setupFormIsCollapsible && (
                  <button
                    type="button"
                    className="dw-setup-toggle"
                    aria-expanded={setupFormOpen}
                    onClick={() => setSetupFormOpen((prev) => !prev)}
                  >
                    <span className="dw-setup-toggle-label">
                      Run this debate again
                    </span>
                    <span className="dw-setup-toggle-hint">
                      Upload a revised document to compare
                    </span>
                    {setupFormOpen ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>
                )}
                <>
                  {debateData ? (
                    <section className="debate-complete">
                      <div className="debate-complete-container">
                        <div className="debate-complete-header">
                          <div
                            className="debate-complete-badge"
                            style={
                              debateData.stopped
                                ? {
                                  backgroundColor: "#fee2e2",
                                  color: "#ef4444",
                                  borderColor: "#fca5a5",
                                }
                                : {}
                            }
                          >
                            {debateData.stopped
                              ? "Debate Stopped"
                              : "Debate Finished"}
                          </div>

                          <h2 className="debate-complete-title">
                            {debateData.stopped
                              ? "Debate Stopped Incomplete"
                              : "AI Debate Successfully Completed"}
                          </h2>

                          <p className="debate-complete-subtitle">
                            {debateData.stopped
                              ? "The debate was stopped before all rounds were completed."
                              : "Multiple AI models have rigorously challenged, refined, and stress-tested your arguments across structured debate rounds."}
                          </p>
                        </div>

                        <div className="debate-complete-stats">
                          <div className="debate-stat debate-stat--rounds">
                            <DebateSelectedRoundDisplay
                              roundCount={debateData.maxRounds ?? maxRounds}
                              isLoggedIn={isLoggedIn}
                              compact
                            />
                            {debateData.stopped && (
                              <span className="debate-stat-sublabel">
                                {debateData.stoppedAtRound ||
                                  debateData.rounds?.length ||
                                  0}{" "}
                                of {debateData.maxRounds ?? maxRounds} rounds
                                completed
                              </span>
                            )}
                          </div>

                          <div className="debate-stat-divider" />

                          <div className="debate-stat">
                            <span className="debate-stat-label">Models</span>
                            <span className="debate-stat-value">
                              {composition?.models
                                ? composition.models.replace(/, /g, " · ")
                                : "Claude · ChatGPT · Gemini"}
                            </span>
                          </div>

                          <div className="debate-stat-divider" />

                          <div className="debate-stat">
                            <span className="debate-stat-label">Method</span>
                            <span className="debate-stat-value">IRAC</span>
                          </div>
                        </div>

                        <div className="debate-complete-footer">
                          {debateData.stopped && (
                            <div
                              className="debate-stopped-notice"
                              style={{
                                backgroundColor: darkMode
                                  ? "#3d3524"
                                  : "#f5f5dc",
                                border: `1px solid ${darkMode ? "#5c4a2a" : "#d4c4a0"}`,
                                borderRadius: "0.5rem",
                                padding: "0.5rem 0.75rem",
                                marginBottom: "1rem",
                                display: "inline-block",
                                width: "fit-content",
                              }}
                            >
                              <span style={{ fontWeight: 700, color: darkMode ? "#f1f5f9" : "#111827" }}>
                                This debate cannot be resumed.
                              </span>{" "}
                              <span style={{ color: darkMode ? "#e2e8f0" : "#374151" }}>
                                To continue analysis,{" "}
                                <span
                                  onClick={resetDebate}
                                  style={{
                                    color: darkMode ? "#60a5fa" : "#2563eb",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  start a new debate
                                </span>
                                .
                              </span>
                            </div>
                          )}
                          <p className="debate-complete-hint">
                            Review each round below or jump to the final
                            consensus summary.
                          </p>
                        </div>
                      </div>
                    </section>
                  ) : (
                    <header className="debate-header">
                      <h1 className="debate-title">AI Debate Mode</h1>
                      <div className="dashboard-research-stat">
                        <div className="dashboard-research-stat-badge">
                          <div
                            className="dashboard-research-stat-icon"
                            aria-hidden="true"
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                          <span className="dashboard-research-stat-count">
                            {displayedDebateSessionsCompleted === null
                              ? "…"
                              : displayedDebateSessionsCompleted.toLocaleString(
                                "en-US",
                              )}
                          </span>
                          <div
                            className="dashboard-research-stat-divider"
                            aria-hidden="true"
                          />
                          <span className="dashboard-research-stat-label">
                            Debate Sessions Completed
                          </span>
                        </div>
                      </div>

                    </header>
                  )}

                  <section className="debate-main">
                    <div className="debate-main-card">
                      <div className="debate-input-section">
                        <div className="debate-input-card">
                          <div className="debate-input-wrapper">
                            {/* File input (hidden) */}
                            <input
                              type="file"
                              ref={debateFileInputRef}
                              style={{ display: "none" }}
                              accept=".pdf,.docx,.doc,.txt"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length > 0) {
                                  onFileUpload(files);
                                }
                                e.target.value = ""; // Reset input
                              }}
                            />
                            {queryLockedForComparison &&
                              !queryLockedAfterStop &&
                              !(onResultsScreen && originalDebateHadDocument) ? (
                              /* Locked query UI: "Query" label, padlock + read-only text, then info message */
                              <div style={{ width: "100%" }}>
                                <h3
                                  style={{
                                    margin: "0 0 0.5rem 0",
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    color: darkMode ? "#e2e8f0" : "#334155",
                                    borderBottom: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                                    paddingBottom: "0.5rem",
                                  }}
                                >
                                  Query
                                </h3>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "0.75rem",
                                    padding: "0.75rem 1rem",
                                    background: darkMode
                                      ? "#1e293b"
                                      : "#f1f5f9",
                                    border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                                    borderRadius: "0.5rem",
                                    minHeight: "52px",
                                  }}
                                >
                                  <Lock
                                    size={20}
                                    style={{
                                      flexShrink: 0,
                                      color: darkMode ? "#94a3b8" : "#64748b",
                                      marginTop: "2px",
                                    }}
                                  />
                                  <span
                                    style={{
                                      flex: 1,
                                      fontSize: "0.9rem",
                                      fontStyle: "italic",
                                      color: darkMode ? "#94a3b8" : "#475569",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {prompt?.trim() || "No query"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    marginTop: "0.75rem",
                                    fontSize: "0.9rem",
                                    color: darkMode ? "#94a3b8" : "#64748b",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "1.25rem",
                                      height: "1.25rem",
                                      borderRadius: "50%",
                                      background: "#3b82f6",
                                      color: "white",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Info size={14} strokeWidth={2.5} />
                                  </span>
                                  <span>
                                    Using original query to ensure accurate
                                    comparison.
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <>
                                <textarea
                                  className="debate-textarea"
                                  placeholder="What are the strongest and weakest arguments in my case? How can the weakest arguments be improved?"
                                  value={prompt}
                                  onChange={(event) =>
                                    setPrompt(event.target.value)
                                  }
                                  disabled={
                                    debating ||
                                    queryLockedAfterStop ||
                                    queryLockedForComparison ||
                                    (onResultsScreen && originalDebateHadDocument) ||
                                    (isTrial && onResultsScreen)
                                  }
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (
                                      !debating &&
                                      !queryLockedAfterStop &&
                                      !onResultsScreen &&
                                      !(isTrial && onResultsScreen)
                                    ) {
                                      e.currentTarget.style.borderColor =
                                        "#193CB9";
                                      e.currentTarget.style.backgroundColor =
                                        darkMode
                                          ? "rgba(25, 60, 185, 0.1)"
                                          : "rgba(25, 60, 185, 0.05)";
                                    }
                                  }}
                                  onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.style.borderColor = "";
                                    e.currentTarget.style.backgroundColor = "";
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.style.borderColor = "";
                                    e.currentTarget.style.backgroundColor = "";
                                    if (
                                      debating ||
                                      queryLockedAfterStop ||
                                      (onResultsScreen &&
                                        !originalDebateHadDocument) ||
                                      (isTrial && onResultsScreen)
                                    ) {
                                      return;
                                    }
                                    const files = Array.from(
                                      e.dataTransfer.files || [],
                                    );
                                    if (files.length > 0) {
                                      onFileUpload(files);
                                    }
                                  }}
                                />

                                <button
                                  className="debate-attach-btn"
                                  type="button"
                                  onClick={() =>
                                    debateFileInputRef.current?.click()
                                  }
                                  aria-label="Attach file"
                                  title={fileUploadTooltip ? undefined : "Attach files (PDF, DOCX, TXT)"}
                                  disabled={
                                    debating ||
                                    queryLockedAfterStop ||
                                    (onResultsScreen &&
                                      !originalDebateHadDocument) ||
                                    fileUploading["debateFiles"] ||
                                    isClipperLockedAfterRerun ||
                                    isFileUploaderDisabled ||
                                    (isTrial && onResultsScreen)
                                  }
                                  data-tooltip={fileUploadTooltip}
                                >
                                  {fileUploading["debateFiles"] ? (
                                    <Loader2
                                      size={20}
                                      style={{
                                        animation: "spin 1s linear infinite",
                                      }}
                                    />
                                  ) : (
                                    <Paperclip
                                      size={20}
                                      style={{ transform: "rotate(-45deg)" }}
                                    />
                                  )}
                                </button>
                              </>
                            )}
                          </div>

                          {/* Uploaded files display using FileUploader component */}
                          <FileUploader
                            files={getCurrentChatFiles().debateFiles || []}
                            isUploading={fileUploading["debateFiles"]}
                            onRemove={onFileRemove}
                            protectedFileKeys={protectedFileKeys}
                            disabled={
                              debating ||
                              queryLockedAfterStop ||
                              (onResultsScreen && !originalDebateHadDocument) ||
                              (isTrial && onResultsScreen)
                            }
                          />

                          {queryLockedAfterStop && (
                            <p
                              className="debate-stopped-helper"
                              style={{
                                margin: "0.75rem 0 0",
                                fontSize: "0.9rem",
                                color: darkMode ? "#94a3b8" : "#64748b",
                              }}
                            >
                              This debate was ended.
                            </p>
                          )}

                          {onResultsScreen && !originalDebateHadDocument && (
                            <p
                              className="debate-stopped-helper"
                              style={{
                                margin: "8px 0 12px 0",
                                fontSize: "0.85rem",
                                color: darkMode ? "#94a3b8" : "#64748b",
                                textAlign: "center",
                              }}
                            >
                              To continue analysis,{" "}
                              <span
                                onClick={resetDebate}
                                style={{
                                  color: "#2563eb",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                }}
                              >
                                start a new debate
                              </span>{" "}
                              using New Debate in the left sidebar.
                            </p>
                          )}

                          {onResultsScreen &&
                            originalDebateHadDocument &&
                            !queryLockedAfterStop &&
                            !isViewingRerunSnapshot &&
                            (originalDebateFilesSnapshot?.length || 0) <= 1 && (
                              <p
                                className="debate-stopped-helper"
                                style={{
                                  margin: "8px 0 12px 0",
                                  fontSize: "0.85rem",
                                  color: darkMode ? "#94a3b8" : "#64748b",
                                  textAlign: "center",
                                }}
                              >
                                Upload a revised document to re-run this debate
                                using the same settings and generate a
                                Comparison Dashboard.
                              </p>
                            )}

                          <div className="debate-controls-top">
                            {onResultsScreen ? (
                              <DebateSelectedRoundDisplay
                                roundCount={debateData?.maxRounds ?? maxRounds}
                                isLoggedIn={isLoggedIn}
                              />
                            ) : (
                              <div className="debate-controls-top-inner">
                                <label htmlFor="roundSelect">
                                  Max Debate Rounds
                                </label>
                                <div className="debate-rounds-selector">
                                  {getDebateRoundOptions(isLoggedIn).map((opt) => {
                                    const isSelected = maxRounds === opt.value;
                                    const isControlDisabled = debating || queryLockedForComparison || (onResultsScreen && originalDebateHadDocument);
                                    const isAnonymousLocked = !isLoggedIn && opt.value !== 1;
                                    const isLocked =
                                      isAnonymousLocked ||
                                      (opt.lockedFor === "anonymous" && !isLoggedIn) ||
                                      (opt.lockedFor === "free" && !isLoggedIn) ||
                                      (opt.lockedFor === "trial" && isTrial);
                                    const btnDisabled = isControlDisabled;
                                    const idleLabelColor = darkMode ? "#e2e8f0" : "#374151";

                                    return (
                                      <div key={opt.value} className="debate-round-option-wrapper" style={{ position: "relative" }}>
                                        <button
                                          type="button"
                                          onClick={() => !isLocked && !isControlDisabled && setMaxRounds(opt.value)}
                                          disabled={btnDisabled}
                                          className={`debate-round-option ${isSelected ? "debate-round-option-active" : ""} ${isLocked ? "debate-round-option-locked" : ""}`}
                                          style={{
                                            padding: "0.5rem 1rem",
                                            borderRadius: "0.5rem",
                                            ...(isSelected
                                              ? { color: "#ffffff" }
                                              : {
                                                border: isLocked
                                                  ? `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`
                                                  : `1px solid ${darkMode ? "#475569" : "#d1d5db"}`,
                                                background: isLocked
                                                  ? darkMode ? "#1e293b" : "#f8fafc"
                                                  : darkMode ? "#0f172a" : "#ffffff",
                                                color: isLocked
                                                  ? darkMode ? "#94a3b8" : "#6b7280"
                                                  : idleLabelColor,
                                              }),
                                            fontSize: "0.85rem",
                                            fontWeight: isSelected ? 600 : 500,
                                            cursor: btnDisabled ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.4rem",
                                            justifyContent:
                                              !isLoggedIn && isDebateIntroMobile && opt.value === 1
                                                ? "space-between"
                                                : "flex-start",
                                            transition: "all 0.15s ease",
                                          }}
                                        >
                                          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                                            {isLocked && (
                                              <Lock size={12} style={{ flexShrink: 0 }} />
                                            )}
                                            {opt.label}
                                          </span>
                                          {!isLoggedIn && isDebateIntroMobile && opt.value === 1 && (
                                            <span className="debate-mode--no-login-message-box">
                                              <b>Free</b> No Login
                                            </span>
                                          )}
                                        </button>
                                        {isLocked && (
                                          <div className="debate-round-locked-tooltip">
                                            <Lock size={12} style={{ display: "inline", marginRight: "4px", verticalAlign: "text-bottom" }} />
                                            {isAnonymousLocked ? (
                                              <>Create a free account to unlock {opt.value}-round debates</>
                                            ) : opt.lockedFor === "anonymous" ? (
                                              <>Create a free account to unlock 3-round debates</>
                                            ) : opt.lockedFor === "free" ? (
                                              <>Start a <a href="/pricing" style={{ color: "#fff", textDecoration: "underline", fontWeight: 600 }}>Free Trial</a> to unlock 5-round debates</>
                                            ) : (
                                              <><a href="/pricing" style={{ color: "#fff", textDecoration: "underline", fontWeight: 600 }}>Upgrade to a paid plan</a> to unlock 8-round debates</>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {!isLoggedIn && !isDebateIntroMobile && (
                                  <div className="debate-mode--no-login-message-box">
                                    <b>Free</b> No Login
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <DebatModePanelComposition
                            composition={composition}
                            setComposition={setComposition}
                            disabled={
                              debating ||
                              queryLockedForComparison ||
                              !!debateData?.rounds?.length ||
                              (onResultsScreen && originalDebateHadDocument)
                            }
                          />

                          {!isLoggedIn && (
                            <div className="debate-free-note" style={{ display: "flex", justifyContent: "flex-start" }}>
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: "6px",
                                background: "#e6f9f0", border: "1.5px solid #2dc76d",
                                borderRadius: "999px", padding: "5px 14px",
                                fontSize: "14px", fontWeight: 500, color: "#1a8f4e",
                              }}>
                                <span style={{ fontSize: "13px" }}>&#10003;</span> Free &middot; 1 round &middot; No login required
                              </span>
                            </div>
                          )}

                          {queryLockedAfterStop ? (
                            <div
                              className="debate-start-btn-wrapper"
                              style={{ position: "relative", width: "100%", marginTop: "7px" }}
                              onMouseEnter={() => setShowTooltip(true)}
                              onMouseLeave={() => setShowTooltip(false)}
                            >
                              <button
                                className="debate-start-btn debate-new-debate-btn"
                                type="button"
                                onClick={resetDebate}
                                disabled={true}
                                style={{ pointerEvents: "none" }}
                              >
                                Start AI Debate
                                <ArrowRight
                                  size={18}
                                  style={{ marginLeft: "0.5rem" }}
                                />
                              </button>
                              <div className="debate-start-disabled-overlay" />
                              {showTooltip && (
                                <div className="debate-tooltip">
                                  <AlertTriangle
                                    size={20}
                                    className="debate-tooltip-icon"
                                    aria-hidden
                                  />
                                  <span className="debate-tooltip-text">
                                    Start a new debate by clicking "New Debate" in the sidebar.
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Re-run button for revised document:
                                  - Visible on results screen when original debate had a document
                                  - Disabled until the current file set differs from the original snapshot
                                    (i.e., a revised document is attached). */}
                              {onResultsScreen &&
                                originalDebateHadDocument &&
                                !isViewingRerunSnapshot &&
                                (originalDebateFilesSnapshot?.length || 0) <= 1 && (
                                  <button
                                    className="debate-start-btn"
                                    type="button"
                                    onClick={handleStartDebateClick}
                                    disabled={debating || !isComparisonReRun || !isLoggedIn || isTrial}
                                    data-tooltip={
                                      !isLoggedIn
                                        ? "Start your Free Trial to unlock Re-run with Revised Document."
                                        : isTrial
                                          ? "Re-runs with revised documents are available on paid plans"
                                          : !isComparisonReRun
                                            ? "Attach a revised document to enable this re-run"
                                            : undefined
                                    }
                                    title={
                                      !isLoggedIn || !isComparisonReRun
                                        ? ""
                                        : undefined
                                    }
                                    style={{ marginBottom: 0 }}
                                  >
                                    {(!isLoggedIn || isTrial) && <Lock size={14} style={{ marginRight: "0.25rem" }} />}
                                    Re-run with Revised Document
                                    {isLoggedIn && !isTrial && (
                                      <ArrowRight
                                        size={18}
                                        style={{ marginLeft: "0.5rem" }}
                                      />
                                    )}
                                  </button>
                                )}
                              {
                                // Show Start button only before any results (initial Run 1).
                                // For re-runs, starting is driven via document attachment (clipping) instead.
                                !onResultsScreen && (
                                  <div
                                    className="debate-start-btn-wrapper"
                                    style={{ position: "relative", width: "100%" }}
                                    onMouseEnter={() => {
                                      if (!canStartDebate && !debating) showStartTooltip();
                                    }}
                                    onMouseLeave={hideStartTooltip}
                                  >
                                    <button
                                      className="debate-start-btn"
                                      type="button"
                                      onClick={() => {
                                        if (!canStartDebate && !debating) {
                                          showStartTooltip();
                                          return;
                                        }

                                        handleStartDebateClick();
                                      }}
                                      disabled={debating}
                                      aria-disabled={!canStartDebate || debating}
                                      style={{
                                        cursor:
                                          !canStartDebate || debating
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                    >
                                      {debating
                                        ? "Running Debate…please allow up to one minute"
                                        : "Start AI Debate"}
                                      {!debating && (
                                        <ArrowRight
                                          size={18}
                                          style={{ marginLeft: "0.5rem" }}
                                        />
                                      )}
                                    </button>
                                    {showTooltip && (
                                      <div
                                        className={`debate-tooltip${isMobileScreen ? " debate-tooltip-mobile" : ""}`}
                                      >
                                        <AlertTriangle
                                          size={20}
                                          className="debate-tooltip-icon"
                                          aria-hidden
                                        />
                                        <span className="debate-tooltip-text">
                                          {startTooltipMessage}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )
                              }
                              {originalDebateHadDocument &&
                                (comparisonLoading || (
                                  !comparisonDashboard && !comparisonSections &&
                                  !comparisonStreamingBullets?.whatImproved
                                )) &&
                                (hasRerunOnSameChat || debateRerunCount > 0) &&
                                (isTrial && isLoggedIn ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      padding: "0.75rem 1rem",
                                      marginTop: "0.5rem",
                                      borderRadius: "0.5rem",
                                      border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                                      background: darkMode
                                        ? "#1e293b"
                                        : "#f8fafc",
                                      fontSize: "0.875rem",
                                      color: darkMode ? "#94a3b8" : "#64748b",
                                      cursor: "not-allowed",
                                      opacity: 0.8,
                                    }}
                                    title="The Comparison Dashboard is available on paid plans."
                                  >
                                    🔒{" "}
                                    <strong style={{ fontWeight: 600 }}>
                                      Comparison Dashboard
                                    </strong>
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        fontStyle: "italic",
                                        marginLeft: "0.25rem",
                                      }}
                                    >
                                      — paid plan only
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      width: "100%",
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    <button
                                      className="debate-start-btn"
                                      type="button"
                                      onClick={() => {
                                        if (
                                          manualCompareMode &&
                                          selectedChatForComparison
                                        ) {
                                          runDebateComparison(
                                            selectedChatForComparison,
                                            currentChatId,
                                          );
                                        } else if (
                                          hasRerunOnSameChat &&
                                          currentChatId === originalDebateChatId
                                        ) {
                                          // Same chatId: Run 1 + re-run stored in one document
                                          runDebateComparison(
                                            originalDebateChatId,
                                            originalDebateChatId,
                                          );
                                        } else {
                                          // Legacy: two separate chats
                                          const rerunId =
                                            currentChatId !== originalDebateChatId
                                              ? currentChatId
                                              : latestRerunChatId;
                                          runDebateComparison(
                                            originalDebateChatId,
                                            rerunId,
                                          );
                                        }
                                      }}
                                      disabled={
                                        debating ||
                                        comparisonLoading ||
                                        !originalDebateChatId ||
                                        (!hasRerunOnSameChat && debateRerunCount === 0) ||
                                        !debateData ||
                                        !debateData.rounds?.length ||
                                        !!comparisonDashboard ||
                                        !!comparisonSections
                                      }
                                      data-tooltip={
                                        (comparisonDashboard || comparisonSections)
                                          ? "Comparison Dashboard has already been generated"
                                          : undefined
                                      }
                                      title={
                                        currentChatId !== originalDebateChatId
                                          ? "Generate comparison: Original (Run 1) vs this re-run"
                                          : "Generate comparison: Original (Run 1) vs latest re-run"
                                      }
                                      style={{
                                        marginTop: 0,
                                        marginBottom: comparisonLoading ? "0.75rem" : 0,
                                      }}
                                    >
                                      {comparisonLoading
                                        ? "Generating Comparison Dashboard"
                                        : "Generate Comparison Dashboard"}
                                    </button>
                                    {comparisonLoading && (
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          paddingBottom: "0.5rem",
                                        }}
                                      >
                                        <Hourglass
                                          size={24}
                                          style={{
                                            animation: "spin 2s linear infinite",
                                            color: darkMode ? "#60a5fa" : "#2563eb",
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              {/* Manual cross-debate comparison has been removed per product decision */}
                            </>
                          )}
                        </div>
                      </div>

                    </div>

                    {/* Feature cards — pre-debate only; on results screen they render above the footer.
                        Not while a debate streams either: debate-redesign.css keys its
                        "intro" layout on these cards being present, and that layout lets
                        every block shrink to zero — which collapsed the streaming
                        transcript under the summary. */}
                    {!onResultsScreen && !debating && (
                      <>
                        <DebateInfoPanels />
                        {/* Demo video under the cards. A thumbnail row until
                            clicked, then it opens and plays. */}
                        <div className="dw-demo-video">
                          <VideoWidget
                            mode="collapsible"
                            accentColor="red"
                            badge="DEMO"
                            title="See how your argument gets pressure-tested — try one round free"
                            subtitle="Watch it debate a real argument — no login required"
                            videoSrc="/videos/debate-mode-demo.mp4"
                          />
                        </div>
                      </>
                    )}
                  </section>
                </>

                {(debateData?.debateError?.message || error) && (
                  <div
                    className={
                      debateData?.debateError?.type === "user_stop"
                        ? "debate-stop-notice"
                        : "debate-error"
                    }
                    role="status"
                  >
                    {debateData?.debateError?.type === "user_stop" ? (
                      <p>{debateData.debateError.message}</p>
                    ) : (
                      <>
                        <p className="debate-error-title">Streaming interrupted</p>
                        <p>{debateData?.debateError?.message || error}</p>
                      </>
                    )}
                  </div>
                )}

                {/* Show loading only if no streaming data is available yet */}
                {debating &&
                  Object.keys(streamingRounds).length === 0 &&
                  !streamingConsensus && (
                    <div className="debate-status">
                      <Loader2 className="debate-spinner" />
                      <div className="debate-status-copy">
                        <span>
                          {round > 0
                            ? `Running Round ${round}/${maxRounds}…please allow up to one minute`
                            : "Starting debate…please allow up to one minute"}
                        </span>
                        {isMobileScreen && (
                          <span className="debate-mobile-wake-hint">
                            On phones, avoid locking the screen or leaving this tab
                            for long stretches—otherwise the connection may pause.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                {/* Show consensus after debate is finished */}
                {!debating && streamingConsensus && (
                  <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className="debate-summary"
                  >
                    <div
                      ref={streamingConsensusRef}
                      className="response-html"
                      style={{
                        maxHeight: "600px",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: convertTextToHtml(
                          ensureDisclaimerAtEnd(
                            removeCitationMarkers(
                              removeSeparatorLines(streamingConsensus),
                            ),
                            !debating,
                          ),
                        ),
                      }}
                    />
                    <div
                      className="response-actions"
                      style={{
                        marginTop: "0.5rem",
                        display: "flex",
                        gap: "0.5rem",
                      }}
                    >
                      <motion.button
                        className="response-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          handleOpenEditor(
                            "debate-consensus",
                            "Claude Final Consensus Summary",
                            convertTextToHtml(
                              ensureDisclaimerAtEnd(
                                removeCitationMarkers(
                                  removeSeparatorLines(streamingConsensus),
                                ),
                                !debating,
                              ),
                            ),
                            {},
                          )
                        }
                        style={{
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          border: "none",
                          background: darkMode ? "#2563eb" : "#3b82f6",
                          color: "white",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Edit / Export
                      </motion.button>
                    </div>
                  </motion.section>
                )}

                {!debating && debateData && (
                  <>
                    {/* Comparison Dashboard: only show when logged in; when not logged in hide this card in debate mode.
                        Also only show when chat has a re-run or comparison is in progress (hide stale content on first run).
                        IMPORTANT: also show when we already have persisted comparisonSections from the backend,
                        otherwise cards won't render even though the backend has returned data. */}
                    {isLoggedIn &&
                      runSelectionKey !== "initial" &&
                      (comparisonDashboard ||
                        comparisonSections ||
                        comparisonError ||
                        comparisonLoading ||
                        comparisonStreamingBullets) &&
                      (hasRerunOnSameChat ||
                        debateRerunCount > 0 ||
                        comparisonLoading ||
                        !!comparisonStreamingBullets ||
                        comparisonGeneratingDetails) && (
                        <section className="debate-comparison-dashboard-section">
                          {!showComparisonDashboard && (
                            <div
                              className={`debate-comparison-card debate-comparison-collapsed-bar ${darkMode ? "debate-comparison-card-dark" : ""
                                }`}
                            >
                              <div className="debate-comparison-header debate-comparison-collapsed-header">
                                <div className="debate-comparison-title-row">
                                  <span className="debate-comparison-icon">
                                    <span className="debate-comparison-icon-inner" />
                                  </span>
                                  <div className="debate-comparison-title-text">
                                    <h3>Comparison Dashboard</h3>
                                    <p>Original Brief vs. Revised Brief</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="debate-comparison-collapse-btn"
                                  onClick={handleToggleComparisonDashboard}
                                  disabled={comparisonCollapsedSyncing}
                                  aria-label="Expand comparison dashboard"
                                >
                                  <ChevronDown size={20} />
                                </button>
                              </div>
                            </div>
                          )}
                          {showComparisonDashboard && (
                            <div
                              className={`debate-comparison-card ${darkMode ? "debate-comparison-card-dark" : ""
                                }`}
                            >
                              <header className="debate-comparison-header">
                                <div className="debate-comparison-title-row">
                                  <span className="debate-comparison-icon">
                                    <span className="debate-comparison-icon-inner" />
                                  </span>
                                  <div className="debate-comparison-title-text">
                                    <h3>Comparison Dashboard</h3>
                                    <p>Original Brief vs. Revised Brief</p>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {(comparisonDashboard ||
                                    comparisonSections ||
                                    hasStreamingBullets) && (
                                      <button
                                        type="button"
                                        className="debate-comparison-collapse-btn debate-ui-btn-text-fix"
                                        onClick={
                                          comparisonDashboard
                                            ? handleDownloadComparisonDashboard
                                            : handleGenerateDetailedComparisonDashboard
                                        }
                                        disabled={
                                          comparisonCardsGenerating ||
                                          comparisonGeneratingDetails ||
                                          (!comparisonDashboard && !comparisonDetailChatId)
                                        }
                                        aria-label={
                                          comparisonCardsGenerating
                                            ? "Generating comparison cards"
                                            : comparisonGeneratingDetails
                                              ? "Generating full details"
                                              : comparisonDashboard
                                                ? "Download detailed comparison dashboard"
                                                : "Generate detailed comparison dashboard"
                                        }
                                        title={
                                          comparisonCardsGenerating
                                            ? "Generating comparison cards"
                                            : comparisonGeneratingDetails
                                              ? "Generating full details"
                                              : comparisonDashboard
                                                ? "Download detailed comparison dashboard"
                                                : "Generate detailed comparison dashboard"
                                        }
                                      >
                                        {comparisonGeneratingDetails ? (
                                          <>
                                            <Loader2
                                              size={18}
                                              className="debate-spinner"
                                            />
                                            Generating Detailed Document Comparison Dashboard...
                                          </>
                                        ) : (
                                          <>
                                            {comparisonDashboard ? (
                                              <>
                                                <Download size={18} />
                                                Download Detailed Comparison Dashboard
                                              </>
                                            ) : (
                                              <>Generate Detailed Document Comparison Dashboard</>
                                            )}
                                          </>
                                        )}
                                      </button>
                                    )}
                                  <button
                                    type="button"
                                    className="debate-comparison-collapse-btn"
                                    onClick={handleToggleComparisonDashboard}
                                    disabled={comparisonCollapsedSyncing}
                                    aria-label="Collapse comparison dashboard"
                                  >
                                    <ChevronUp size={20} />
                                  </button>
                                </div>
                              </header>

                              <div className="debate-comparison-body">
                                {comparisonLoading &&
                                  !comparisonStreamingBullets && (
                                    <p className="debate-comparison-status">
                                      Generating comparison with Claude…
                                    </p>
                                  )}
                                {/* Full document generation happens in background — download button shows spinner */}
                                {comparisonError && !comparisonLoading && (
                                  <p className="debate-comparison-error">
                                    {comparisonError}
                                  </p>
                                )}
                                {!comparisonError &&
                                  (comparisonSections ||
                                    comparisonStreamingBullets) &&
                                  (() => {
                                    // Phase 1: show streaming 3-bullet content (all cards open from start)
                                    if (
                                      comparisonStreamingBullets &&
                                      !comparisonSections
                                    ) {
                                      const streamText = (text) =>
                                        (text || "").trim();
                                      const netLabel =
                                        comparisonStreamingNetLabel
                                          ? mapNetAssessmentLabel(
                                            comparisonStreamingNetLabel,
                                          )
                                          : { cssSuffix: "", display: "" };
                                      const isCardStreaming = (cardId) =>
                                        comparisonStreamingCard === cardId;
                                      return (
                                        <div className="debate-comparison-grid">
                                          <div
                                            className={`debate-comparison-section-card${isCardStreaming("whatImproved") ? " debate-comparison-card-streaming" : ""}`}
                                          >
                                            <h4 className="debate-comparison-card-heading">
                                              I. What Improved
                                            </h4>
                                            {isCardStreaming(
                                              "whatImproved",
                                            ) && (
                                                <span
                                                  className="debate-comparison-card-streaming-indicator"
                                                  aria-hidden="true"
                                                >
                                                  <Loader2
                                                    size={16}
                                                    className="debate-spinner"
                                                  />
                                                </span>
                                              )}
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: convertTextToHtml(streamText(comparisonStreamingBullets.whatImproved)) }} />
                                          </div>
                                          <div
                                            className={`debate-comparison-section-card${isCardStreaming("whatRemainsWeak") ? " debate-comparison-card-streaming" : ""}`}
                                          >
                                            <h4 className="debate-comparison-card-heading">
                                              II. What Remains Weak
                                            </h4>
                                            {isCardStreaming(
                                              "whatRemainsWeak",
                                            ) && (
                                                <span
                                                  className="debate-comparison-card-streaming-indicator"
                                                  aria-hidden="true"
                                                >
                                                  <Loader2
                                                    size={16}
                                                    className="debate-spinner"
                                                  />
                                                </span>
                                              )}
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: convertTextToHtml(streamText(comparisonStreamingBullets.whatRemainsWeak)) }} />
                                          </div>
                                          <div
                                            className={`debate-comparison-section-card debate-comparison-section-card-net${isCardStreaming("netAssessment") || isCardStreaming("netAssessmentLabel") ? " debate-comparison-card-streaming" : ""}`}
                                          >
                                            <h4 className="debate-comparison-card-heading">
                                              III. Net Assessment
                                            </h4>
                                            {(isCardStreaming(
                                              "netAssessment",
                                            ) ||
                                              isCardStreaming(
                                                "netAssessmentLabel",
                                              )) && (
                                                <span
                                                  className="debate-comparison-card-streaming-indicator"
                                                  aria-hidden="true"
                                                >
                                                  <Loader2
                                                    size={16}
                                                    className="debate-spinner"
                                                  />
                                                </span>
                                              )}
                                            {netLabel.display && (
                                              <div
                                                className={`debate-comparison-net-capsule debate-comparison-net-capsule-${netLabel.cssSuffix || ""}`}
                                                role="status"
                                                aria-label={`Net assessment: ${netLabel.display}`}
                                              >
                                                {netLabel.display}
                                              </div>
                                            )}
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: convertTextToHtml(streamText(comparisonStreamingBullets.netAssessment)) }} />
                                          </div>
                                          <div
                                            className={`debate-comparison-section-card${isCardStreaming("aiConsensusOnImprovement") ? " debate-comparison-card-streaming" : ""}`}
                                          >
                                            <h4 className="debate-comparison-card-heading">
                                              IV. Areas of AI Consensus on
                                              Improvement
                                            </h4>
                                            {isCardStreaming(
                                              "aiConsensusOnImprovement",
                                            ) && (
                                                <span
                                                  className="debate-comparison-card-streaming-indicator"
                                                  aria-hidden="true"
                                                >
                                                  <Loader2
                                                    size={16}
                                                    className="debate-spinner"
                                                  />
                                                </span>
                                              )}
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: convertTextToHtml(streamText(comparisonStreamingBullets.aiConsensusOnImprovement)) }} />
                                          </div>
                                          <div
                                            className={`debate-comparison-section-card${isCardStreaming("remainingLitigationRisks") ? " debate-comparison-card-streaming" : ""}`}
                                          >
                                            <h4 className="debate-comparison-card-heading">
                                              V. Remaining Litigation Risks
                                            </h4>
                                            {isCardStreaming(
                                              "remainingLitigationRisks",
                                            ) && (
                                                <span
                                                  className="debate-comparison-card-streaming-indicator"
                                                  aria-hidden="true"
                                                >
                                                  <Loader2
                                                    size={16}
                                                    className="debate-spinner"
                                                  />
                                                </span>
                                              )}
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: convertTextToHtml(streamText(comparisonStreamingBullets.remainingLitigationRisks)) }} />
                                          </div>
                                          <div className="debate-comparison-section-card debate-comparison-disclaimer">
                                            <p>
                                              <strong>Disclaimer:</strong> This
                                              comparison is generated by AI
                                              based on the two debate runs. It
                                              is not legal advice, does not
                                              replace independent legal
                                              judgment, and should be reviewed
                                              by a licensed attorney before any
                                              reliance.
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    }

                                    const toCardText = (val) => {
                                      if (val == null) return "";
                                      return Array.isArray(val)
                                        ? val.join("\n")
                                        : String(val);
                                    };
                                    const toCardHtml = (val) =>
                                      convertTextToHtml(toCardText(val));
                                    const hasContent = (val) =>
                                      toCardText(val).trim().length > 0;

                                    const netBody =
                                      comparisonSections.netAssessmentSection ??
                                      comparisonSections.netAssessmentReason ??
                                      "";
                                    return (
                                      <div className="debate-comparison-grid">
                                        {/* I. What Improved */}
                                        <div className="debate-comparison-section-card">
                                          <h4 className="debate-comparison-card-heading">
                                            I. What Improved
                                          </h4>
                                          {hasContent(
                                            comparisonSections.whatImproved,
                                          ) ? (
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: toCardHtml(comparisonSections.whatImproved) }} />
                                          ) : (
                                            <p>
                                              No specific improvements
                                              identified.
                                            </p>
                                          )}
                                        </div>

                                        {/* II. What Remains Weak */}
                                        <div className="debate-comparison-section-card">
                                          <h4 className="debate-comparison-card-heading">
                                            II. What Remains Weak
                                          </h4>
                                          {hasContent(
                                            comparisonSections.whatRemainsWeak,
                                          ) ? (
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: toCardHtml(comparisonSections.whatRemainsWeak) }} />
                                          ) : (
                                            <p>
                                              No material weaknesses
                                              highlighted.
                                            </p>
                                          )}
                                        </div>

                                        {/* III. Net Assessment — classification only in colored pill, bullets below without duplicating it */}
                                        <div className="debate-comparison-section-card debate-comparison-section-card-net">
                                          <h4 className="debate-comparison-card-heading">
                                            III. Net Assessment
                                          </h4>
                                          {comparisonSections.netAssessmentLabel &&
                                            (() => {
                                              const { cssSuffix, display } =
                                                mapNetAssessmentLabel(
                                                  comparisonSections.netAssessmentLabel,
                                                );
                                              if (!cssSuffix || !display)
                                                return null;
                                              return (
                                                <div
                                                  className={`debate-comparison-net-capsule debate-comparison-net-capsule-${cssSuffix}`}
                                                  role="status"
                                                  aria-label={`Net assessment: ${display}`}
                                                >
                                                  {display}
                                                </div>
                                              );
                                            })()}
                                          {hasContent(netBody) && (
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: toCardHtml(netBody) }} />
                                          )}
                                        </div>

                                        {/* IV. Areas of AI Consensus on Improvement */}
                                        <div className="debate-comparison-section-card">
                                          <h4 className="debate-comparison-card-heading">
                                            IV. Areas of AI Consensus on
                                            Improvement
                                          </h4>
                                          {hasContent(
                                            comparisonSections.aiConsensusOnImprovement,
                                          ) ? (
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: toCardHtml(comparisonSections.aiConsensusOnImprovement) }} />
                                          ) : (
                                            <p>
                                              No specific areas of consensus
                                              identified.
                                            </p>
                                          )}
                                        </div>

                                        {/* V. Remaining Litigation Risks */}
                                        <div className="debate-comparison-section-card">
                                          <h4 className="debate-comparison-card-heading">
                                            V. Remaining Litigation Risks
                                          </h4>
                                          {hasContent(
                                            comparisonSections.remainingLitigationRisks,
                                          ) ? (
                                            <div className="debate-comparison-content response-html" dangerouslySetInnerHTML={{ __html: toCardHtml(comparisonSections.remainingLitigationRisks) }} />
                                          ) : (
                                            <p>
                                              No remaining litigation risks
                                              highlighted.
                                            </p>
                                          )}
                                        </div>

                                        {/* Disclaimer */}
                                        <div className="debate-comparison-section-card debate-comparison-disclaimer">
                                          <p>
                                            <strong>Disclaimer:</strong> This
                                            comparison is generated by AI based
                                            on the two debate runs. It is not
                                            legal advice, does not replace
                                            independent legal judgment, and
                                            should be reviewed by a licensed
                                            attorney before any reliance.
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                {/* Fallback: show raw comparison text if parsing failed */}
                                {!comparisonLoading &&
                                  !comparisonError &&
                                  !comparisonSections &&
                                  comparisonDashboard && (
                                    <pre className="debate-comparison-content">
                                      {comparisonDashboard}
                                    </pre>
                                  )}
                              </div>
                            </div>
                          )}
                        </section>
                      )}

                    {/* Show all debate rounds */}
                    {debateData.rounds && debateData.rounds.length > 0 && (
                      <motion.section
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="debate-all-rounds"
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "1rem",
                            flexWrap: "wrap",
                            gap: "1rem",
                          }}
                        >
                          <h2
                            className="debate-rounds-title"
                            style={{ margin: 0 }}
                          >
                            All Debate Rounds (
                            {debateData.maxRounds ?? debateData.rounds.length}{" "}
                            total)
                            {debateData.stopped && (
                              <span
                                style={{
                                  marginLeft: "1rem",
                                  padding: "0.25rem 0.75rem",
                                  backgroundColor: "#fbbf24",
                                  color: "#78350f",
                                  borderRadius: "0.5rem",
                                  fontSize: "0.875rem",
                                  fontWeight: "600",
                                  display: "inline-block",
                                }}
                              >
                                Stopped at Round{" "}
                                {debateData.stoppedAtRound ||
                                  debateData.rounds.length}
                              </span>
                            )}
                          </h2>
                          {/* Actions: Executive Summary sits beside the Download
                              menu, not inside it — QA could not find it there. */}
                          <div className="dw-actions">
                          <div className="dw-dl">
                            <button
                              type="button"
                              className="dw-dl-trigger"
                              onClick={() => setDownloadsOpen((v) => !v)}
                              aria-expanded={downloadsOpen}
                              aria-haspopup="menu"
                            >
                              <Download size={16} />
                              <span>Download</span>
                              <ChevronDown
                                size={14}
                                className={`dw-dl-chev${downloadsOpen ? " is-open" : ""}`}
                              />
                            </button>
                            {downloadsOpen && (
                              <>
                                <div
                                  className="dw-dl-backdrop"
                                  onClick={() => setDownloadsOpen(false)}
                                />
                                <div className="dw-dl-menu" role="menu">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              flexWrap: "wrap",
                            }}
                          >
                            {/* Download Final Consensus & Summary — Primary blue */}
                            {(() => {
                              const isDownloadSummaryLocked = !isLoggedIn;
                              return (
                                <motion.button
                                  type="button"
                                  className="download-consensus-btn"
                                  data-tooltip={
                                    isDownloadSummaryLocked
                                      ? "Start your Free Trial to unlock Final Consensus & Summary download."
                                      : undefined
                                  }
                                  onClick={
                                    isDownloadSummaryLocked
                                      ? undefined
                                      : () => handleDownloadDebatePDF(null, true)
                                  }
                                  whileHover={
                                    isDownloadSummaryLocked ? {} : { scale: 1.05 }
                                  }
                                  whileTap={
                                    isDownloadSummaryLocked ? {} : { scale: 0.95 }
                                  }
                                  disabled={isDownloadSummaryLocked}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.75rem 1.5rem",
                                    borderRadius: "0.5rem",
                                    border: "none",
                                    background: darkMode ? "#2563eb" : "#3b82f6",
                                    color: "white",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    cursor: isDownloadSummaryLocked
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: 1,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  {isDownloadSummaryLocked && (
                                    <Lock size={14} style={{ marginRight: "0.1rem" }} />
                                  )}
                                  <Download size={18} />
                                  <span>Download Final Consensus & Summary</span>
                                </motion.button>
                              );
                            })()}

                            {/* Download Full Debate — Secondary gray/outline */}
                            <motion.button
                              onClick={() => handleDownloadDebatePDF()}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                padding: "0.75rem 1.5rem",
                                borderRadius: "0.5rem",
                                border: darkMode
                                  ? "1px solid rgba(148,163,184,0.4)"
                                  : "1px solid rgba(15,23,42,0.2)",
                                background: darkMode ? "#1e293b" : "#f8fafc",
                                color: darkMode ? "#e5e7eb" : "#1f2937",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <Download size={18} />
                              <span>Download Full Debate</span>
                            </motion.button>

                            {/* Extra downloads dropdown (Executive Summary, Comparison Dashboard) */}
                            {/* {(executiveSummaryText?.trim() || comparisonDownloadAvailable) && (
                              <div
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                }}
                              >
                                <motion.button
                                  onClick={() =>
                                    setDownloadSummaryMenuOpen((prev) => !prev)
                                  }
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.75rem 1rem",
                                    borderRadius: "0.5rem",
                                    border: darkMode
                                      ? "1px solid rgba(148,163,184,0.4)"
                                      : "1px solid rgba(15,23,42,0.2)",
                                    background: darkMode ? "#1e293b" : "#f8fafc",
                                    color: darkMode ? "#e5e7eb" : "#1f2937",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Download size={18} />
                                  <span>More</span>
                                  <ChevronDown size={16} />
                                </motion.button>
                                {downloadSummaryMenuOpen && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      right: 0,
                                      marginTop: "0.5rem",
                                      minWidth: "260px",
                                      background: darkMode
                                        ? "#0f172a"
                                        : "#ffffff",
                                      border: "1px solid rgba(148,163,184,0.3)",
                                      borderRadius: "0.5rem",
                                      boxShadow:
                                        "0 10px 30px rgba(15,23,42,0.25)",
                                      zIndex: 20,
                                      overflow: "hidden",
                                    }}
                                  >
                                    {executiveSummaryText?.trim() && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setDownloadSummaryMenuOpen(false);
                                          handleDownloadExecutiveSummaryPDF();
                                        }}
                                        style={{
                                          width: "100%",
                                          padding: "0.5rem 0.75rem",
                                          textAlign: "left",
                                          fontSize: "0.85rem",
                                          border: "none",
                                          background: "transparent",
                                          color: darkMode
                                            ? "#e5e7eb"
                                            : "#111827",
                                          cursor: "pointer",
                                        }}
                                      >
                                        Download Executive Summary
                                      </button>
                                    )}
                                    {comparisonDownloadAvailable && (
                                      <>
                                        {executiveSummaryText?.trim() && (
                                          <div
                                            style={{
                                              borderTop:
                                                "1px solid rgba(148,163,184,0.3)",
                                            }}
                                          />
                                        )}
                                        <button
                                          type="button"
                                          disabled={comparisonCardsGenerating}
                                          onClick={() => {
                                            if (comparisonCardsGenerating) return;
                                            setDownloadSummaryMenuOpen(false);
                                            handleDownloadComparisonDashboard();
                                          }}
                                          title={
                                            comparisonCardsGenerating
                                              ? "Generating comparison cards"
                                              : "Download comparison dashboard"
                                          }
                                          style={{
                                            width: "100%",
                                            padding: "0.5rem 0.75rem",
                                            textAlign: "left",
                                            fontSize: "0.85rem",
                                            border: "none",
                                            background: "transparent",
                                            color: darkMode
                                              ? "#e5e7eb"
                                              : "#111827",
                                            cursor: comparisonCardsGenerating
                                              ? "not-allowed"
                                              : "pointer",
                                            opacity: comparisonCardsGenerating
                                              ? 0.6
                                              : 1,
                                          }}
                                        >
                                          Download Comparison Dashboard
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )} */}
                          </div>
                                </div>
                              </>
                            )}
                          </div>
                            {!isViewingRerunSnapshot && !executiveSummaryText?.trim() && (() => {
                              const hasPaidAccess = isLoggedIn && hasPremiumFeatureAccess(user);
                              const isLocked = !hasPaidAccess;
                              const isDisabled = isLocked || executiveSummaryLoading || debateData?.stopped;
                              return (
                                <motion.button
                                  type="button"
                                  className="executive-summary-create-btn"
                                  data-tooltip={
                                    isLocked
                                      ? "Start your Free Trial to unlock Executive Summary."
                                      : debateData?.stopped
                                        ? "Executive Summary is disabled for stopped debates"
                                        : undefined
                                  }
                                  onClick={isLocked ? undefined : handleCreateExecutiveSummary}
                                  whileHover={
                                    isDisabled
                                      ? {}
                                      : { scale: 1.05 }
                                  }
                                  whileTap={
                                    isDisabled
                                      ? {}
                                      : { scale: 0.95 }
                                  }
                                  disabled={isDisabled}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.75rem 1.25rem",
                                    borderRadius: "0.5rem",
                                    border: darkMode
                                      ? "1px solid rgba(148,163,184,0.35)"
                                      : "1px solid rgba(59,130,246,0.35)",
                                    background: darkMode ? "#0b1220" : "#ffffff",
                                    color: darkMode ? "#e5e7eb" : "#1f2937",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    cursor:
                                      isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      executiveSummaryLoading ? 0.5 : 1,
                                    transition: "all 0.2s ease",
                                  }}
                                  title={
                                    isLocked
                                      ? undefined
                                      : debateData?.stopped
                                        ? undefined
                                        : "Generate a 1-page executive summary from the Final Consensus Summary"
                                  }
                                >
                                  {isLocked && <Lock size={14} style={{ marginRight: "0.25rem" }} />}
                                  {executiveSummaryLoading ? (
                                    <>
                                      <Loader2
                                        size={18}
                                        className="debate-spinner"
                                      />
                                      <span>Creating...</span>
                                    </>
                                  ) : (
                                    <span>Create Executive Summary</span>
                                  )}
                                </motion.button>
                              );
                            })()}
                            {isLoggedIn && !isViewingRerunSnapshot && executiveSummaryText?.trim() && !executiveSummaryLoading && (
                              <motion.button
                                type="button"
                                onClick={handleDownloadExecutiveSummaryPDF}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "0.75rem 1.25rem",
                                  borderRadius: "0.5rem",
                                  border: darkMode
                                    ? "1px solid rgba(148,163,184,0.4)"
                                    : "1px solid rgba(15,23,42,0.2)",
                                  background: darkMode ? "#1e293b" : "#f8fafc",
                                  color: darkMode ? "#e5e7eb" : "#1f2937",
                                  fontSize: "0.875rem",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Download size={18} />
                                <span>Download Executive Summary</span>
                              </motion.button>
                            )}
                          </div>
                        </div>
                        {(showExecutiveSummary || executiveSummaryText?.trim()) &&
                          !isViewingRerunSnapshot && (
                            <div className="debate-round-container" style={{ marginBottom: "1.5rem" }}>
                              <button
                                onClick={() => !executiveSummaryLoading && executiveSummaryText?.trim() && setExecutiveSummaryCollapsed((prev) => !prev)}
                                className="debate-round-header"
                                style={executiveSummaryText?.trim() ? {} : { cursor: "default" }}
                              >
                                <span className="debate-round-title">
                                  Executive Summary
                                </span>
                                {executiveSummaryText?.trim() && (
                                  executiveSummaryCollapsed ? (
                                    <ChevronDown size={20} />
                                  ) : (
                                    <ChevronUp size={20} />
                                  )
                                )}
                              </button>
                              <AnimatePresence initial={false}>
                                {!executiveSummaryCollapsed && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="debate-round-content"
                                    style={{ overflow: "hidden" }}
                                  >
                                    <div style={{ padding: "1rem" }}>
                                      {executiveSummaryLoading && (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            color: darkMode ? "#cbd5e1" : "#334155",
                                            fontSize: "0.9rem",
                                          }}
                                        >
                                          <Loader2 size={18} className="debate-spinner" />
                                          Generating executive summary...
                                        </div>
                                      )}

                                      {executiveSummaryText?.trim() && (
                                        <>
                                          <div
                                            style={{
                                              maxHeight: "420px",
                                              overflowY: "auto",
                                              overflowX: "hidden",
                                            }}
                                            className="response-html"
                                            dangerouslySetInnerHTML={{
                                              __html: convertTextToHtml(executiveSummaryText),
                                            }}
                                          />
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-start",
                                              marginTop: "0.75rem",
                                              paddingTop: "0.75rem",
                                              borderTop: darkMode
                                                ? "1px solid rgba(148,163,184,0.15)"
                                                : "1px solid rgba(15,23,42,0.08)",
                                            }}
                                          >
                                            <motion.button
                                              className="response-btn"
                                              disabled={executiveSummaryLoading}
                                              whileHover={executiveSummaryLoading ? {} : { scale: 1.05 }}
                                              whileTap={executiveSummaryLoading ? {} : { scale: 0.95 }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!executiveSummaryLoading) {
                                                  handleOpenEditor(
                                                    "debate-executive-summary",
                                                    "Executive Summary",
                                                    convertTextToHtml(executiveSummaryText),
                                                    {},
                                                  );
                                                }
                                              }}
                                              style={{
                                                padding: "0.5rem 1rem",
                                                borderRadius: "0.5rem",
                                                border: "none",
                                                background: darkMode ? "#2563eb" : "#3b82f6",
                                                color: "white",
                                                fontSize: "0.875rem",
                                                fontWeight: 500,
                                                cursor: executiveSummaryLoading ? "not-allowed" : "pointer",
                                                opacity: executiveSummaryLoading ? 0.6 : 1,
                                              }}
                                            >
                                              Edit / Export
                                            </motion.button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        {debateData.rounds.map((round, index) => {

                          const isSummaryRound = !!(
                            (debateData.maxRounds > 0 && round.round > debateData.maxRounds) ||
                            (round.arguments?.consensus && !round.arguments?.advocate)
                          );

                          return (
                            <div key={index} className="debate-round-container">
                              <button
                                onClick={() => toggleDebateRound(index)}
                                className="debate-round-header"
                              >
                                <span className="debate-round-title">
                                  {isSummaryRound
                                    ? "Final Consensus & Summary"
                                    : `Round ${round.round}/${debateData.maxRounds}`}
                                  {debateData.stopped &&
                                    round.round === debateData.rounds.length &&
                                    " (Stopped)"}
                                </span>
                                {expandedDebateRounds[index] ? (
                                  <ChevronUp size={20} />
                                ) : (
                                  <ChevronDown size={20} />
                                )}
                              </button>
                              <AnimatePresence>
                                {expandedDebateRounds[index] && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="debate-round-content"
                                  >
                                    <div
                                      className="debate-results-grid"
                                      style={
                                        isSummaryRound
                                          ? { gridTemplateColumns: "1fr" }
                                          : undefined
                                      }
                                    >
                                      {isSummaryRound &&
                                        round.arguments?.consensus ? (
                                        <div className="debate-result-card">
                                          {!isLoggedIn ? (
                                            <div
                                              style={{
                                                borderRadius: "0.75rem",
                                                overflow: "hidden",
                                                border: darkMode ? "1px solid #334155" : "1px solid #e2e8f0",
                                                background: darkMode ? "#0b1220" : "#f8fafc",
                                              }}
                                            >
                                              <div
                                                className="response-html"
                                                style={{
                                                  maxHeight: "440px",
                                                  overflow: "hidden",
                                                  filter: "blur(5px)",
                                                  opacity: 0.78,
                                                  padding: "1rem",
                                                }}
                                              >
                                                <p><strong>I. Issue Presented</strong></p>
                                                <p>This summary is available after creating an account and upgrading access.</p>
                                                <p><strong>II. Arguments</strong></p>
                                                <p>Key points from each model are synthesized into a final legal position.</p>
                                                <p><strong>III. Recommendation</strong></p>
                                                <p>Actionable next steps and risk considerations are included here.</p>
                                              </div>
                                              <div
                                                style={{
                                                  width: "100%",
                                                  maxWidth: "520px",
                                                  textAlign: "center",
                                                  padding: "1rem 1rem 1.25rem",
                                                  margin: "0 auto",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: "34px",
                                                    height: "34px",
                                                    borderRadius: "999px",
                                                    border: darkMode
                                                      ? "1px solid #475569"
                                                      : "1px solid #cbd5e1",
                                                    background: darkMode ? "rgba(15,23,42,0.8)" : "#f8fafc",
                                                    color: darkMode ? "#cbd5e1" : "#64748b",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    margin: "0 auto 0.85rem",
                                                  }}
                                                >
                                                  <Lock size={15} />
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "2rem",
                                                    fontWeight: 700,
                                                    color: darkMode ? "#f8fafc" : "#111827",
                                                    lineHeight: 1.2,
                                                    marginBottom: "0.45rem",
                                                  }}
                                                >
                                                  This summary determines where your argument is most vulnerable
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "1.65rem",
                                                    color: darkMode ? "#d1d5db" : "#374151",
                                                    lineHeight: 1.35,
                                                    marginBottom: "1rem",
                                                  }}
                                                >
                                                  See exactly where opposing counsel would attack - and how to fix it before you file.
                                                </div>
                                                <a
                                                  href="/pricing"
                                                  style={{
                                                    display: "inline-block",
                                                    minWidth: "210px",
                                                    padding: "0.78rem 1.3rem",
                                                    borderRadius: "0.65rem",
                                                    textDecoration: "none",
                                                    background: "#3b5bfd",
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    fontSize: "1.05rem",
                                                  }}
                                                >
                                                  Start Free Trial
                                                </a>
                                                <div
                                                  style={{
                                                    marginTop: "0.55rem",
                                                    fontSize: "0.95rem",
                                                    color: darkMode ? "#9ca3af" : "#6b7280",
                                                  }}
                                                >
                                                  7-day free trial. Cancel anytime.
                                                </div>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <div
                                                className="response-html"
                                                style={{
                                                  height: "400px",
                                                  overflowY: "auto",
                                                  overflowX: "hidden",
                                                  minHeight: "400px",
                                                }}
                                                dangerouslySetInnerHTML={{
                                                  __html: convertTextToHtml(
                                                    ensureDisclaimerAtEnd(
                                                      removeCitationMarkers(
                                                        removeSeparatorLines(
                                                          typeof round.arguments
                                                            .consensus.text ===
                                                            "string"
                                                            ? round.arguments
                                                              .consensus.text
                                                            : "",
                                                        ),
                                                      ),
                                                      true,
                                                    ),
                                                  ),
                                                }}
                                              />
                                              <div
                                                style={{
                                                  marginTop: "1rem",
                                                  display: "flex",
                                                  gap: "0.5rem",
                                                }}
                                              >
                                                <motion.button
                                                  type="button"
                                                  whileHover={{ scale: 1.05 }}
                                                  whileTap={{ scale: 0.95 }}
                                                  onClick={() => {
                                                    const rawText =
                                                      typeof round.arguments
                                                        ?.consensus?.text ===
                                                        "string"
                                                        ? round.arguments.consensus
                                                          .text
                                                        : "";
                                                    const cleaned =
                                                      ensureDisclaimerAtEnd(
                                                        removeCitationMarkers(
                                                          removeSeparatorLines(
                                                            rawText,
                                                          ),
                                                        ),
                                                        true,
                                                      );
                                                    handleOpenEditor(
                                                      "debate-consensus",
                                                      "Claude Final Consensus Summary",
                                                      convertTextToHtml(cleaned),
                                                      {},
                                                    );
                                                  }}
                                                  style={{
                                                    padding: "0.5rem 1rem",
                                                    borderRadius: "0.5rem",
                                                    border: "none",
                                                    background: darkMode
                                                      ? "#2563eb"
                                                      : "#3b82f6",
                                                    color: "white",
                                                    fontSize: "0.875rem",
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  Edit / Export
                                                </motion.button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      ) : (
                                        <>
                                          {/* Display by role (slot) order: advocate, critic, strategist */}
                                          {[
                                            {
                                              role: "advocate",
                                              displayName: "Advocate",
                                              expectedModel: "ChatGPT",
                                            },
                                            {
                                              role: "critic",
                                              displayName: "Critical Analyst",
                                              expectedModel: "Claude",
                                            },
                                            {
                                              role: "strategist",
                                              displayName:
                                                "Strategic/Procedural Analyst",
                                              expectedModel: "Gemini",
                                            },
                                          ].map((slot) => {
                                            // Get argument by role
                                            const argument =
                                              round.arguments?.[slot.role];
                                            let text = "";
                                            let actualModel =
                                              slot.expectedModel;

                                            if (argument) {
                                              if (
                                                typeof argument === "string"
                                              ) {
                                                text = argument;
                                              } else if (
                                                typeof argument === "object" &&
                                                argument !== null
                                              ) {
                                                text = argument.text || "";
                                                actualModel =
                                                  argument.model ||
                                                  slot.expectedModel;
                                              }
                                            }

                                            // Ensure we always have a model name, even if argument is missing
                                            if (
                                              !actualModel ||
                                              actualModel === "Unknown"
                                            ) {
                                              actualModel = slot.expectedModel;
                                            }

                                            return (
                                              <div
                                                key={slot.role}
                                                className="debate-result-card"
                                              >
                                                <h3>
                                                  {actualModel}'s Argument (
                                                  {slot.displayName})
                                                </h3>
                                                {text &&
                                                  text.trim().length > 0 ? (
                                                  <>
                                                    <div
                                                      className="response-html"
                                                      style={{
                                                        height: "400px",
                                                        overflowY: "auto",
                                                        overflowX: "hidden",
                                                        minHeight: "400px",
                                                      }}
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          convertTextToHtml(
                                                            ensureDisclaimerAtEnd(
                                                              removeCitationMarkers(
                                                                removeSeparatorLines(
                                                                  text,
                                                                ),
                                                              ),
                                                              true,
                                                            ),
                                                          ),
                                                      }}
                                                    />
                                                    <div
                                                      className="response-actions"
                                                      style={{
                                                        marginTop: "0.5rem",
                                                        display: "flex",
                                                        gap: "0.5rem",
                                                      }}
                                                    >
                                                      <motion.button
                                                        className="response-btn"
                                                        whileHover={{
                                                          scale: 1.05,
                                                        }}
                                                        whileTap={{
                                                          scale: 0.95,
                                                        }}
                                                        onClick={() =>
                                                          handleOpenEditor(
                                                            "debate-round",
                                                            `Round ${round.round} - ${actualModel} (${slot.displayName})`,
                                                            convertTextToHtml(
                                                              ensureDisclaimerAtEnd(
                                                                removeCitationMarkers(
                                                                  removeSeparatorLines(
                                                                    text,
                                                                  ),
                                                                ),
                                                                true,
                                                              ),
                                                            ),
                                                            {
                                                              roundIndex: index,
                                                              role: slot.role,
                                                            },
                                                          )
                                                        }
                                                        style={{
                                                          padding:
                                                            "0.5rem 1rem",
                                                          borderRadius:
                                                            "0.5rem",
                                                          border: "none",
                                                          background: darkMode
                                                            ? "#2563eb"
                                                            : "#3b82f6",
                                                          color: "white",
                                                          fontSize: "0.875rem",
                                                          fontWeight: 500,
                                                          cursor: "pointer",
                                                        }}
                                                      >
                                                        Edit / Export
                                                      </motion.button>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <p
                                                    style={{
                                                      color: "#94a3b8",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    No response available
                                                  </p>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}

                        {/* Show message for incomplete rounds if debate was stopped */}
                        {debateData.stopped &&
                          debateData.rounds.length < debateData.maxRounds && (
                            <div
                              style={{
                                padding: "1.5rem",
                                marginTop: "1rem",
                                backgroundColor: darkMode
                                  ? "#1f2937"
                                  : "#f3f4f6",
                                borderRadius: "0.75rem",
                                border: `2px dashed ${darkMode ? "#fbbf24" : "#f59e0b"}`,
                              }}
                            >
                              <p
                                style={{
                                  color: darkMode ? "#fbbf24" : "#f59e0b",
                                  fontWeight: "600",
                                  fontSize: "1rem",
                                  marginBottom: "0.5rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                }}
                              >
                                <span style={{ fontSize: "1.5rem" }}>⚠️</span>
                                Debate Stopped by User
                              </p>
                              <p
                                style={{
                                  color: darkMode ? "#9ca3af" : "#6b7280",
                                  fontSize: "0.95rem",
                                }}
                              >
                                This debate was stopped at round{" "}
                                {debateData.stoppedAtRound ||
                                  debateData.rounds.length}{" "}
                                of {debateData.maxRounds}.
                                {debateData.maxRounds -
                                  debateData.rounds.length >
                                  0 &&
                                  ` ${debateData.maxRounds - debateData.rounds.length} round${debateData.maxRounds - debateData.rounds.length > 1 ? "s" : ""} ${debateData.maxRounds - debateData.rounds.length > 1 ? "were" : "was"} not completed.`}
                              </p>
                            </div>
                          )}
                      </motion.section>
                    )}

                    {debateData &&
                      (debateData.consensusSummary ||
                        debateData.finalSummary) &&
                      !debateData.rounds?.some(
                        (r) =>
                          (debateData.maxRounds > 0 && r.round > debateData.maxRounds) ||
                          (r.arguments?.consensus && !r.arguments?.advocate),
                      ) && (
                        <DebateSummary
                          summary={
                            debateData.consensusSummary ||
                            debateData.finalSummary
                          }
                          loading={false}
                          debating={false}
                          isLocked={!isLoggedIn}
                          isOpen={summaryOpen}
                          onToggle={() => setSummaryOpen(!summaryOpen)}
                          handleOpenEditor={handleOpenEditor}
                          darkMode={darkMode}
                          title="Final Consensus & Summary"
                        />
                      )}
                  </>
                )}
              </div>
            </div>
          </main>
          <Footer variant="compact" disableLazy />
        </motion.div>

        {/* ── Context rail: panel roster + round progress ── */}
        <DebateContextRail
          composition={composition}
          round={
            /* `round` is the live counter; a restored debate reports its rounds
               through debateData. That array also carries the consensus round,
               so clamp it to maxRounds (otherwise a 3-round debate reads 4/3). */
            Math.min(debateData?.rounds?.length || round, maxRounds || Infinity)
          }
          maxRounds={maxRounds}
          hasResults={onResultsScreen}
          isRunning={!!(Object.keys(streamingRounds || {}).length || streamingConsensus)}
          sessionsCompleted={displayedDebateSessionsCompleted}
          roundsLabel={getDebateRoundLabel(maxRounds, isLoggedIn)}
        />

      </div>

      {/* Editor Modal */}
      {editorOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`editor-modal ${darkMode ? "dark" : ""}`}
            role="dialog"
            aria-modal="true"
          >
            <div className="editor-panel">
              <div className="editor-header">
                <div>
                  <p className="editor-label">Rich Text Editor</p>
                  <h3>{editorTitle || "Document Editor"}</h3>
                </div>
                <button
                  className="editor-close-btn"
                  onClick={handleCloseEditor}
                  aria-label="Close editor"
                >
                  <X size={18} />
                </button>
              </div>
              <ReactQuill
                theme="snow"
                value={editorContent}
                onChange={setEditorContent}
                modules={quillModules}
                formats={quillFormats}
                className="editor-quill"
              />
              <div className="editor-actions">
                <button
                  className="editor-action-btn editor-action-primary"
                  onClick={handleEditorSave}
                >
                  Save Changes
                </button>
                <button
                  className="editor-action-btn"
                  onClick={handleDownloadPDF}
                >
                  Download PDF
                </button>
                <button
                  className="editor-action-btn"
                  onClick={handleDownloadDoc}
                >
                  Download DOCX
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Pre-Run Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPreRunConfirm}
        title={!isLoggedIn ? "Start Free AI Debate?" : "Start AI Debate Mode?"}
        message={
          !isLoggedIn ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  backgroundColor: darkMode
                    ? "rgba(37, 99, 235, 0.15)"
                    : "#DBEAFF",
                  border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                  borderRadius: "0.5rem",
                  padding: "0.4rem 1rem 0.4rem 0.75rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Info
                  size={20}
                  color="#2563eb"
                  style={{ flexShrink: 0 }}
                />
                <p
                  style={{
                    margin: 0,
                    color: darkMode ? "#E2E8F0" : "#1E3A8A",
                    fontSize: "0.95rem",
                    lineHeight: "1.5",
                  }}
                >
                  <strong>Free Demo:</strong> This will run a <strong>1-round</strong> debate
                  using 3 AI models <em>(Standard Panel)</em>
                </p>
              </div>

              <div className="debate-free-demo-split">
                <div className="debate-free-demo-col">
                  <h4
                    style={{
                      margin: "0 0 0.5rem 0",
                      fontSize: "0.95rem",
                      color: darkMode ? "#E2E8F0" : "#0F172A",
                    }}
                  >
                    This demo includes:
                  </h4>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {[
                      "One document upload (optional)",
                      "1 debate round only",
                      "Standard Panel (ChatGPT, Claude, Gemini)",
                    ].map((text, i) => (
                      <li
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.5rem",
                          fontSize: "0.9rem",
                          color: darkMode ? "#94A3B8" : "#475569",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            backgroundColor: "#10B981",
                            color: "white",
                            flexShrink: 0,
                            marginTop: "0.15rem",
                          }}
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="debate-free-demo-divider"></div>
                <div className="debate-free-demo-col-right">
                  <h4
                    style={{
                      margin: "0 0 0.5rem 0",
                      fontSize: "0.95rem",
                      color: darkMode ? "#E2E8F0" : "#0F172A",
                    }}
                  >
                    Not included in free demo:
                  </h4>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {[
                      "3+ round debates",
                      "Adversarial Panel",
                      "Re-runs with revised documents",
                      "Comparison Dashboard",
                    ].map((text, i) => (
                      <li
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.5rem",
                          fontSize: "0.9rem",
                          color: darkMode ? "#94A3B8" : "#475569",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            backgroundColor: "#EF4444",
                            color: "white",
                            flexShrink: 0,
                            marginTop: "0.15rem",
                          }}
                        >
                          <X size={12} strokeWidth={3} />
                        </span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div
                style={{
                  borderTop: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`,
                  margin: "0.5rem 0",
                }}
              ></div>

              <p
                style={{
                  margin: 0,
                  fontSize: "0.9rem",
                  color: darkMode ? "#94A3B8" : "#475569",
                  lineHeight: "1.5",
                }}
              >
                To unlock extended rounds, re-runs, the Adversarial Panel, and
                the Comparison Dashboard, start a free trial or upgrade.
              </p>
              <div
                style={{
                  borderTop: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`,
                  margin: "0.5rem 0 0.25rem 0",
                }}
              ></div>

              <div style={{ marginTop: "0.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.95em",
                    fontWeight: 600,
                    color: darkMode ? "#e2e8f0" : "#1E3A8A",
                  }}
                >
                  Your query:
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + "px";
                    }
                  }}
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    padding: "0.4rem 1rem 0.4rem 0.75rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                    background: darkMode ? "rgba(37, 99, 235, 0.15)" : "#DBEAFF",
                    color: darkMode ? "#e2e8f0" : "#1e293b",
                    fontFamily: "inherit",
                    fontSize: "0.95rem",
                    resize: "none",
                    outline: "none",
                    overflow: "hidden",
                    transition: "border-color 0.2s",
                    display: "block",
                    fieldSizing: "content",
                    lineHeight: "1.5",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) =>
                  (e.target.style.borderColor = darkMode
                    ? "rgba(59, 130, 246, 0.3)"
                    : "#BFDBFE")
                  }
                  placeholder="Enter your legal query for debate..."
                />
              </div>

              {/* ── Free Directory CTA ──
                  Opens the full directory form on /directory in a new tab so
                  this screen stays uncluttered. ── */}
              <div style={{ marginTop: "0.75rem" }}>
                <a
                  href="/directory"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.7rem 1rem",
                    background: darkMode ? "rgba(250,251,254,0.04)" : "#FAFBFE",
                    border: `1.5px dashed ${darkMode ? "rgba(172,208,255,0.35)" : "#acd0ff"}`,
                    borderRadius: "10px",
                    textDecoration: "none",
                  }}
                >
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      background: darkMode ? "rgba(219,232,255,0.15)" : "#dbe8ff",
                      color: darkMode ? "#93c5fd" : "#2347b2",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ExternalLink size={12} />
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: darkMode ? "#93c5fd" : "#24458f" }}>
                    Want more visibility for your firm or practice? Join our free directory{" "}
                    <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#7a889d" }}>(optional)</span>
                  </span>
                </a>
              </div>

              <p style={{
                margin: "0.25rem 0 0 0",
                fontSize: "1.35rem",
                fontWeight: 800,
                color: "var(--accent-tertiary)",
                textAlign: "left",
                letterSpacing: "-0.01em",
              }}>
                Are you ready to proceed?
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  backgroundColor: darkMode
                    ? "rgba(37, 99, 235, 0.15)"
                    : "#DBEAFF",
                  border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                  borderRadius: "0.5rem",
                  padding: "0.4rem 1rem 0.4rem 0.75rem",
                  marginBottom: "0.25rem",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <p
                  style={{ margin: 0, color: darkMode ? "#e2e8f0" : "#000000", lineHeight: "1.5" }}
                >
                  <strong>⚠️ Important:</strong> Debate Mode uses AI Actions as
                  rounds run.
                </p>
              </div>

              <p
                style={{
                  margin: 0,
                  fontSize: "0.95em",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  lineHeight: "1.5",
                }}
              >
                This process will orchestrate <strong>3 AI models across{" "}
                  {maxRounds} rounds of debate.</strong> Once started, the
                debate will run automatically until completion or until you
                manually stop it.
              </p>

              <p
                style={{
                  margin: 0,
                  fontSize: "0.95em",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  lineHeight: "1.5",
                }}
              >
                <strong>If you stop early, you will need to start a new query to
                  continue.</strong>
              </p>

              <div style={{ marginTop: "0.25rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.95em",
                    fontWeight: 600,
                    color: darkMode ? "#e2e8f0" : "#1E3A8A",
                  }}
                >
                  Your query:
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + "px";
                    }
                  }}
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    padding: "0.4rem 1rem 0.4rem 0.75rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                    background: darkMode ? "rgba(37, 99, 235, 0.15)" : "#DBEAFF",
                    color: darkMode ? "#e2e8f0" : "#1e293b",
                    fontFamily: "inherit",
                    fontSize: "0.95rem",
                    resize: "none",
                    outline: "none",
                    overflow: "hidden",
                    transition: "border-color 0.2s",
                    display: "block",
                    fieldSizing: "content",
                    lineHeight: "1.5",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) =>
                  (e.target.style.borderColor = darkMode
                    ? "rgba(59, 130, 246, 0.3)"
                    : "#BFDBFE")
                  }
                  placeholder="Enter your legal query for debate..."
                />
              </div>

              <p style={{
                margin: "0.25rem 0 0 0",
                fontSize: "1.35rem",
                fontWeight: 800,
                color: "var(--accent-tertiary)",
                textAlign: "left",
                letterSpacing: "-0.01em",
              }}>
                Are you ready to proceed?
              </p>
            </div>
          )
        }
        onConfirm={handleConfirmStartDebate}
        onCancel={handleCancelStartDebate}
        confirmText={!isLoggedIn ? "Start Free Debate" : "Start Debate"}
        cancelText="Cancel"
        type="debate"
        requireConsent={!isLoggedIn}
      />

      {/* Anonymous Limit Explainer Modal */}
      <AnimatePresence>
        {showAnonSignupModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.7)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: "1rem",
            }}
            onClick={() => setShowAnonSignupModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="modal-content"
              style={{
                background: darkMode ? "#1e293b" : "#ffffff",
                padding: "2rem",
                borderRadius: "1rem",
                maxWidth: "400px",
                width: "100%",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: darkMode ? "rgba(59, 130, 246, 0.1)" : "#dbeafe",
                  color: darkMode ? "#60a5fa" : "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.25rem",
                }}
              >
                <Lock size={24} />
              </div>
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color: darkMode ? "#f8fafc" : "#0f172a",
                  marginBottom: "0.75rem",
                }}
              >
                You've Hit Your Free Limit
              </h2>
              <p
                style={{
                  color: darkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.95rem",
                  lineHeight: "1.5",
                  marginBottom: "1.5rem",
                }}
              >
                You've used your free demo for this session. Start your free trial
                to continue analyzing your case, running debates, and refining your
                arguments.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <Link
                  to="/pricing"
                  style={{
                    display: "block",
                    padding: "0.75rem",
                    background: "#3b82f6",
                    color: "white",
                    fontWeight: "600",
                    borderRadius: "0.5rem",
                    textDecoration: "none",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#2563eb")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "#3b82f6")
                  }
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/login"
                  style={{
                    display: "block",
                    padding: "0.75rem",
                    background: darkMode ? "#334155" : "#f1f5f9",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                    fontWeight: "600",
                    borderRadius: "0.5rem",
                    textDecoration: "none",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                  (e.currentTarget.style.background = darkMode
                    ? "#475569"
                    : "#e2e8f0")
                  }
                  onMouseOut={(e) =>
                  (e.currentTarget.style.background = darkMode
                    ? "#334155"
                    : "#f1f5f9")
                  }
                >
                  Log In (Already have an account?)
                </Link>
              </div>
              <button
                onClick={() => setShowAnonSignupModal(false)}
                style={{
                  marginTop: "1.25rem",
                  background: "transparent",
                  border: "none",
                  color: darkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Maybe later
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DebateMode;
